/*
 * Foodiepaws v3 theme runtime.
 * Vanilla port of the design's DCLogic class (Foodiepaws Homepage v3.dc.html),
 * kept 1:1 with the approved React reference (foodiepaws-web/src/vals.ts):
 * state shape, pricing math, sound policy and persistence keys are identical.
 *
 * Markup hooks (emitted by the Liquid sections):
 *   data-fp-t    "YOUR PACK (%cartCount%)"      text template
 *   data-fp-t0   "%chew.titleA%"                template for first text node only
 *   data-fp-a    "href:%item.href%"             attribute templates
 *   data-fp-s    "opacity:heroOp1;border:tpl(2px solid %item.border%)"  style bindings
 *   data-fp-on   "click:pickHero0;input:searchType"                     handlers
 *   <template data-fp-if="jwCard">              conditional mount (absent when false)
 *   <template data-fp-each="cartItems">         state-driven list
 *   <script type="application/json" data-fp-data="core|chews|best|pack|jw">
 *
 * Cart runs in two modes:
 *   demo — no Shopify products linked: localStorage "foodiepaws-cart-v1" (design behavior)
 *   real — every Best Sellers card has a product: Shopify AJAX cart (/cart/*.js),
 *          subscribe adds the product's first selling plan.
 */
(function () {
  'use strict';

  // No-settings fallbacks. These are NOT free parameters: they are what the store
  // actually sells on, so a blank theme setting must not print a different offer
  // from the one the checkout honours. Free shipping is $35 (Shipping policy /
  // the Domestic zone's conditional rate) and the welcome discount is 50%
  // (WELCOME50). They carried 49 and 15 — the pre-$35 threshold and the retired
  // 15% code — long after both had moved.
  var S = window.FP_SETTINGS || { freeShipThreshold: 35, subscribeDiscount: 50, subscribeSavePercent: 20, openCartOnAdd: true, currencySymbol: '$' };

  // The GA4 currency, resolved — never the literal 'USD'. The store runs two Shopify
  // Markets and the Canada market is served in CAD, while every `value` in these events
  // is read from the theme's blobs and /cart.js, which are PRESENTMENT-denominated. A
  // Canadian view_item was reporting 19.00 tagged USD (~41% over) and mixing two
  // currencies inside one metric. Shopify publishes the active code in
  // content_for_header on every storefront page.
  var FP_CCY = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

  // ---------- data blobs ----------
  function readData(name, fallback) {
    var el = document.querySelector('script[data-fp-data="' + name + '"]');
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }
  var CORE = readData('core', { products: {}, chews: [], packCaptions: [], searchTags: {}, unitNotes: {}, heroRotateSeconds: 5 });
  var CHEWS = readData('chews', CORE.chews || []);
  var BEST = readData('best', []);
  BEST.forEach(function (b) { b.price = parseFloat(b.price) || 0; });
  var PACK = readData('pack', { captions: CORE.packCaptions || [], clipStages: [0, 1] });
  // No code baked in: a hardcoded fallback outlives the campaign it was written
  // for, and WELCOME15 had already expired while this line still handed it out.
  // The real code arrives in the jw blob, which exists wherever the widget does.
  var JW = readData('jw', { delaySeconds: 2.2, code: '' });

  // registry: core products, overridden by best cards (by demo key)
  var PRODUCTS = {};
  Object.keys(CORE.products || {}).forEach(function (k) { PRODUCTS[k] = CORE.products[k]; });
  BEST.forEach(function (b) {
    // `url` was deliberately absent, and that made the two blobs disagree in the
    // worst possible way: the Best Sellers card overwrote name/price on the
    // 'variety' key while the URL stayed on the cart-drawer registry's product.
    // The search card then advertised "Variety Pack (15 Count)" at $32.99 and
    // linked to the $27.99 10-Count. The override's URL wins; the registry's is
    // the fallback for keys the cards do not carry.
    PRODUCTS[b.key] = Object.assign({}, PRODUCTS[b.key] || {}, {
      name: b.name, meta: b.meta, price: b.price, img: b.img, alt: b.alt,
      url: b.url || (PRODUCTS[b.key] || {}).url
    });
  });
  // Availability is NOT part of this switch. A sold-out variant is already handled
  // per-add by the 422 path in realAdd(); folding it in here demoted the whole page
  // to the demo localStorage cart the moment one card went out of stock, which is
  // where the drawer's CHECKOUT became a silent no-op.
  var REAL = BEST.length > 0 && BEST.every(function (b) { return !!b.variantId; });
  // FP47 fix round 1: can THIS page be in demo mode at all? Only a page whose own data
  // decides the mode can: the Best Sellers row (home), the product blob (a product page)
  // and the cart-page blob (/cart), each of which carries variant ids or does not. Every
  // other page (collection, search, pages, blog, article, 404, policies) carries none of
  // them, so it reads the real cart only and never the localStorage demo cart. Those pages
  // never showed the demo cart before FP47 (their header painted the server count and they
  // had no drawer); now that the drawer renders there, reading it listed a line the
  // Shopify cart does not hold under a "(0)" title. Computed here, before the page modules
  // below push their real variants into BEST.
  var DEMO_ABLE = BEST.length > 0 || !!document.querySelector('script[data-fp-data="pdp"], script[data-fp-data="cartpage"]');

  var money = function (n) { return S.currencySymbol + Number(n).toFixed(2); };
  // Subscribe & Save, NOT the welcome discount. These were one setting until the
  // two values diverged: the welcome code went to 50% while the selling plans
  // still discount 20%, and the PDP advertised a price the cart would not honour.
  var subOf = function (price) { return Math.round(price * (1 - S.subscribeSavePercent / 100) * 100) / 100; };

  // ---------- email typo guard (shared with the first-order modal) ----------
  // Measured on live signups 2026-08-24: 2 of the 7 addresses captured in the first
  // 11 hours after the Klaviyo cutover were unreachable — yahoo.comm and yahoo.comt.
  // 29%. Both shoppers asked for the 50% code and can never receive it, and neither
  // failed loudly: they sit on the list looking like subscribers. type="email"
  // validates the SYNTAX; it never checks that the domain exists.
  //
  // The list is ranked from this store's OWN audience (100-profile sample, 24 Aug):
  // gmail 47, yahoo 17, hotmail 7, verizon 4, aol 3, comcast 2 — those six ARE 80%
  // of it. The rest are the US consumer providers that appear in ones and twos, plus
  // suddenlink.net, which showed up in the Bot Protection incident.
  //
  // Two rules stop it from second-guessing a real address:
  //   1. A domain that IS on the list is never questioned.
  //   2. Distance 2 only for candidates of 8+ characters. rcn.com is a real provider
  //      on this list and sits at distance 2 from msn.com; at 7 characters it is
  //      exempt, so an rcn.com shopper is never asked whether they meant msn.com.
  // Business domains (naturegnaws.com, petwants.com, wholesalehelper.io — all real
  // here) are far past either threshold and never match.
  //
  // It only ever SUGGESTS. Nothing in here can block a submission — the caller keeps
  // that decision, and both callers let a second press through unchanged.
  var FP_MAIL_DOMAINS = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com',
    'comcast.net', 'verizon.net', 'msn.com', 'live.com', 'me.com', 'mac.com', 'att.net',
    'sbcglobal.net', 'bellsouth.net', 'charter.net', 'cox.net', 'ymail.com',
    'rocketmail.com', 'earthlink.net', 'optonline.net', 'suddenlink.net',
    'roadrunner.com', 'windstream.net', 'frontier.com', 'protonmail.com',
    'googlemail.com', 'mail.com'
  ];
  // Levenshtein, abandoned the moment an entire row exceeds cap: the result is only
  // ever compared against a threshold, so the exact distance beyond it is noise.
  function fpEditDistance(a, b, cap) {
    var prev = [], cur = [], i, j, cost, best;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur[0] = i; best = i;
      for (j = 1; j <= b.length; j++) {
        cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (cur[j] < best) best = cur[j];
      }
      if (best > cap) return cap + 1;
      for (j = 0; j <= b.length; j++) prev[j] = cur[j];
    }
    return prev[b.length];
  }
  // Returns the corrected address, or '' when the domain is fine, unknown, or too far
  // from anything on the list to guess at. Hung off window because
  // snippets/fp-first-order-modal.liquid is self-contained by design: it reads this
  // OPTIONALLY and simply skips the hint when the bundle has not parsed, so a missing
  // bundle costs the hint and never the signup.
  window.fpEmailTypo = function (email) {
    var at = String(email || '').lastIndexOf('@');
    if (at < 1) return '';
    var local = email.slice(0, at);
    var domain = email.slice(at + 1).toLowerCase();
    if (!domain || FP_MAIL_DOMAINS.indexOf(domain) !== -1) return '';
    for (var i = 0; i < FP_MAIL_DOMAINS.length; i++) {
      var cand = FP_MAIL_DOMAINS[i];
      var allow = cand.length >= 8 ? 2 : 1;
      if (fpEditDistance(domain, cand, allow) <= allow) return local + '@' + cand;
    }
    return '';
  };

  // ---------- state (design INITIAL) ----------
  var st = {
    cart: {}, open: false, subCongrats: false,
    jw: 'off', jwStatus: '',
    // FP40: where the join widget may show (fpJwPlacement). Flags, not choices: they
    // never overwrite st.jw, so the shopper's own bubble/min state returns with them.
    jwHeroOn: false, jwAtcOn: false, jwFootOn: false, jwBuyOn: false, jwFocusOn: false,
    faqIdx: 0, heroIndex: 0, chewIdx: 0,
    pkSnd: false, packStage: 0,
    // WCAG 2.2.2 needs a pause mechanism for EVERYONE, not only for the shopper
    // who set the OS reduced-motion preference. These two carry the shopper's own
    // choice; the reduced-motion checks stay where they are and both must hold.
    heroPaused: false, pkPaused: false,
    sel: {}, freq: {},
    mnav: false, searchOpen: false, searchQ: '', searchLive: null
  };
  // The card's opening cadence is the MERCHANT's published default (Theme settings >
  // Store rules > Default subscription frequency), the same source the product page
  // reads at fpDefaultWeeks. It was the literal 4, which agreed with the setting only
  // by coincidence: lowering the setting would have moved the product page and left
  // this row on 4, so the two surfaces would recommend different schedules for one
  // product. Clamped to the cadences THIS card actually publishes, so the stepper can
  // never open on a frequency no selling plan sells.
  //
  // NOT the product page's pack-fit rule (one chew a day, rounded up to whole weeks).
  // Porting that here would move every 5-pack card subscriber from the setting to
  // WEEKLY — four times the shipments — which is a commercial decision, not a repair.
  var bestSeedWeeks = {};
  BEST.forEach(function (_b, i) {
    var seed = (S.subscribeDefaultWeeks > 0) ? S.subscribeDefaultWeeks : 4;
    var rng = bestWeekRange(i);
    if (rng) seed = Math.min(rng.max, Math.max(rng.min, seed));
    st.sel[i] = 'one';
    st.freq[i] = seed;
    bestSeedWeeks[i] = seed;
  });

  var shopifyCart = null; // real-mode cart snapshot (/cart.js)
  // Has /cart.js actually answered? The cart page is client-rendered, so until
  // this is true the page knows nothing — and "nothing" must not be painted as
  // "your cart is empty" one click before checkout.
  var cartLoaded = false;

  // ---------- registries ----------
  var boundNodes = [];   // { el, item, t, t0, a:[[attr,tpl]], s:[[prop,expr|tpl(...)]] }
  var ifTpls = [];       // { tpl, expr, item, mounted, specs, children }
  var eachTpls = [];     // { tpl, expr, sig, rows }

  // ---------- per-template page modules (collection/pdp/cart/reviews) ----------
  var PAGE = {};

  function parseHooks(el, item) {
    var spec = { el: el, item: item || null, t: null, t0: null, a: [], s: [] };
    var has = false;
    if (el.hasAttribute('data-fp-t')) { spec.t = el.getAttribute('data-fp-t'); has = true; }
    if (el.hasAttribute('data-fp-t0')) { spec.t0 = el.getAttribute('data-fp-t0'); has = true; }
    if (el.hasAttribute('data-fp-a')) {
      el.getAttribute('data-fp-a').split(';').forEach(function (pair) {
        var i = pair.indexOf(':');
        if (i > 0) spec.a.push([pair.slice(0, i), pair.slice(i + 1)]);
      });
      has = true;
    }
    if (el.hasAttribute('data-fp-s')) {
      el.getAttribute('data-fp-s').split(';').forEach(function (pair) {
        var i = pair.indexOf(':');
        if (i > 0) spec.s.push([pair.slice(0, i), pair.slice(i + 1)]);
      });
      has = true;
    }
    return has ? spec : null;
  }

  function scan(root) {
    var all = Array.prototype.slice.call(root.querySelectorAll('*'));
    all.forEach(function (el) {
      if (el.tagName === 'TEMPLATE') return;
      // skip anything living inside an unmounted <template>
      var content = el.getRootNode ? el.getRootNode() : document;
      if (content !== document) return;
      var spec = parseHooks(el, null);
      if (spec) boundNodes.push(spec);
    });
    Array.prototype.slice.call(root.querySelectorAll('template[data-fp-if], template[data-fp-each]')).forEach(function (tpl) {
      if (tpl.getRootNode && tpl.getRootNode() !== document) return;
      if (tpl.hasAttribute('data-fp-if')) ifTpls.push({ tpl: tpl, expr: tpl.getAttribute('data-fp-if'), item: null, mounted: null, specs: [], children: [] });
      else eachTpls.push({ tpl: tpl, expr: tpl.getAttribute('data-fp-each'), sig: '', rows: [] });
    });
  }

  // ---------- derived values ----------
  function cartEntries() {
    if (REAL) {
      if (!shopifyCart) return [];
      return shopifyCart.items.map(function (it) {
        var card = BEST.filter(function (b) { return String(b.variantId) === String(it.variant_id) || String(b.variantId) === String(it.id); })[0];
        var id = card ? card.key : '';
        var freq = (it.properties && it.properties._freq) || 4;
        var sub = !!it.selling_plan_allocation;
        // The plan's OWN name — the exact string checkout, the confirmation
        // email and the subscription portal show. Liquid cannot expose a plan's
        // interval, so the theme used to parse a number out of the name and
        // append "weeks"; the published plans are named "Every 1".."Every 6"
        // with no unit, so that unit was invented here. Print what the merchant
        // named the plan and never add a unit it does not carry.
        var planName = sub && it.selling_plan_allocation.selling_plan
          ? (it.selling_plan_allocation.selling_plan.name || '') : '';
        // ONE source of truth for the cadence. `_freq` is a theme-written private
        // property; the plan is what Appstle bills on. When the plan's own name
        // carries a number, that number wins — otherwise /cart printed the
        // property while the drawer printed the plan, and the weekly plan came out
        // as "EVERY 1 WKS" beside a drawer reading "Every 1 week".
        var planWeeksM = /(\d+)/.exec(planName || '');
        var planWeeks = planWeeksM ? parseInt(planWeeksM[1], 10) : 0;
        var vt = it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : '';
        return {
          _key: it.key, id: id, qty: it.quantity, sub: sub, freq: (planWeeks || freq), planName: planName,
          // Needed to change the cadence: the delivery schedule lives on the
          // selling plan, and the plan list has to be looked up per product.
          _handle: it.handle || '',
          _planId: sub && it.selling_plan_allocation.selling_plan ? it.selling_plan_allocation.selling_plan.id : null,
          // The numeric Shopify variant id, for GA4's item_id. `id` above is the
          // theme's demo slug ("bully") and is EMPTY for anything outside the Best
          // Sellers row, so begin_checkout shipped items with no item_id at all and
          // its rows could not be joined to view_item / add_to_cart, which both send
          // the numeric id. That join is the whole point of the funnel.
          _vid: it.variant_id || it.id || '',
          name: it.product_title || (card && card.name) || '',
          meta: (vt || (card && card.meta) || '') + (sub ? ' • Subscribe • ' + (planName || 'every ' + (+freq === 1 ? '1 week' : freq + ' weeks')) : ''),
          img: it.image ? it.image + (it.image.indexOf('?') !== -1 ? '&' : '?') + 'width=192' : '',
          url: it.url || '',
          line: it.final_line_price / 100
        };
      });
    }
    var keys = Object.keys(st.cart).filter(function (k) { return PRODUCTS[k.split(':')[0]]; });
    return keys.map(function (key) {
      var parts = key.split(':');
      var id = parts[0], plan = parts[1], freqW = parts[2];
      var p = PRODUCTS[id];
      var qty = st.cart[key];
      var unit = plan === 'sub' ? subOf(p.price) : p.price;
      return {
        // Present but empty: the demo cart has no Shopify variant to name, and the
        // two branches must return the same shape or the consumer has to test which
        // one it got.
        _key: key, id: id, _vid: '', qty: qty, sub: plan === 'sub', freq: freqW || 4, planName: '',
        name: p.name,
        meta: p.meta + (plan === 'sub' ? ' • Subscribe • every ' + (+(freqW || 4) === 1 ? '1 week' : (freqW || 4) + ' weeks') : ''),
        line: unit * qty
      };
    });
  }
  function cartTotals() {
    var entries = cartEntries();
    var count = 0, subtotal = 0, payable = 0;
    if (REAL && shopifyCart) {
      count = shopifyCart.item_count;
      // Subtotal is DERIVED as payable + discount so the three rows the shopper
      // reads — Subtotal, minus Discount applied, equals Total — cannot disagree,
      // whatever kind of discount is on the cart.
      //
      // Measured on the live cart 2026-08-16, 4x$13.49 with WELCOME50 applied via
      // /discount/WELCOME50: original_total_price 5396, items_subtotal_price 5396,
      // total_price 2698, total_discount 2698. So WELCOME50 allocates at the CART
      // level and items_subtotal_price is NOT reduced — today the old binding and
      // this one produce the same $53.96 and no shopper ever saw a wrong number.
      // The reason to derive it anyway is the case that measurement does not cover:
      // a LINE-level discount does reduce items_subtotal_price while total_discount
      // still prints in full, and the ledger would then subtract the same money
      // twice. payable + discount is right under both allocations.
      //
      // Also measured the same day: an Appstle selling plan does NOT populate
      // total_discount (the 20% is baked into the line price — a subscribed line
      // reported price 1079 against original_price 1079, cart total_discount 0),
      // so the discount row stays absent on a subscription-only cart.
      subtotal = (shopifyCart.total_price + (shopifyCart.total_discount || 0)) / 100;
      // What the shopper will actually be charged for goods, after cart
      // discounts. The free-shipping threshold is measured on the discounted
      // order total (Shipping policy), and the welcome code takes 50% off — so
      // comparing the PRE-discount subtotal promised free shipping that
      // checkout then revoked, to exactly the shoppers the campaign targets.
      payable = (shopifyCart.total_price != null ? shopifyCart.total_price : shopifyCart.items_subtotal_price) / 100;
    } else {
      entries.forEach(function (e) { count += e.qty; subtotal += e.line; });
      payable = subtotal;
    }
    return { entries: entries, count: count, subtotal: subtotal, payable: payable };
  }
  // Rendered as the drawer's subscription chip (%item.freqLabel%), and in parts on /cart
  // (%item.freqEvery% in the cadence stepper, %item.offTail% on the SUBSCRIPTION chip).
  // The plan's own name is the truth — a plan the merchant named "Monthly" prints
  // Monthly instead of being relabelled by this theme. Falls back to the numeric
  // property, pluralised, for demo mode where there is no plan at all — never the raw
  // "%s WKS" that made the weekly plan read "EVERY 1 WKS".
  //
  // Module scope, NOT the cart page's: the drawer resolves cartItems through the
  // global mapper below, and two copies of this rule are exactly how the two
  // surfaces printed different cadences for the same line.
  //
  // FP47 (2026-10-20, CART-10): the live plans are now named "Deliver every 2 weeks, 20% off",
  // and stripping a LEADING "Every " left the whole name standing, so the drawer chip read
  // "SUBSCRIPTION · EVERY DELIVER EVERY 2 WEEKS, 20% OFF" over two lines and the /cart pill
  // wrapped it five times. The name is now read for its two facts, wherever they sit in it:
  //   cadence  — "every N <unit>" or "every <unit>"   -> "Every 2 weeks" / "Every week"
  //   discount — "N% off"                              -> "20% off"
  // and fpFreqLabel returns them as one short chip, "Every 2 weeks · 20% off". The older names
  // still parse: "Every 4 weeks" -> "Every 4 weeks", "Every 1 week" -> "Every 1 week". A unit
  // the name does not carry is never invented ("Every 3" stays "Every 3"), and a name with no
  // "every" at all ("Monthly") is printed as the merchant wrote it, minus a trailing ", N% off"
  // that the chip then states itself. Demo mode (no plan) falls back to the week count.
  // Mixed case on purpose: the drawer chip and the /cart stepper uppercase it in CSS, and the
  // title attributes read it as a sentence.
  function fpPlanParts(e) {
    var raw = String((e && e.planName) || '').replace(/\s+/g, ' ').trim();
    var every = '', off = '', m;
    if ((m = /\bevery\s+(\d+)(?:\s*(day|week|month|year)(s)?)?/i.exec(raw))) {
      var n = parseInt(m[1], 10);
      every = 'Every ' + n + (m[2] ? ' ' + m[2].toLowerCase() + (n === 1 ? '' : 's') : '');
    } else if ((m = /\bevery\s+(other\s+)?(day|week|month|year)\b/i.exec(raw))) {
      every = 'Every ' + (m[1] ? 'other ' : '') + m[2].toLowerCase();
    } else if (raw) {
      every = raw.replace(/\s*[,·•|–—-]\s*\d+(?:\.\d+)?\s*%\s*off\b.*$/i, '').trim();
    }
    if ((m = /(\d+(?:\.\d+)?)\s*%\s*off\b/i.exec(raw))) off = m[1] + '% off';
    if (!every) {
      var w = parseInt(e && e.freq, 10) || 4;
      every = 'Every ' + w + (w === 1 ? ' week' : ' weeks');
    }
    return { every: every, off: off };
  }
  function fpFreqLabel(e) {
    var p = fpPlanParts(e);
    return p.off ? p.every + ' · ' + p.off : p.every;
  }
  // "Beef Bully Stick • Subscribe • Every 4 weeks" already carries the cadence,
  // and the subscription chip prints it again. Keep the badge, drop the tail.
  function fpMetaNoFreq(meta) {
    return String(meta || '').replace(/ • Subscribe • .*$/, ' • Subscribe');
  }

  // built-in catalog fallback (used until/unless live Shopify results arrive)
  function mapLocal() {
    var sq = st.searchQ.trim().toLowerCase();
    return ['bully', 'collagen', 'tripe', 'variety'].filter(function (id) {
      if (!PRODUCTS[id]) return false;
      if (!sq) return true;
      return ((PRODUCTS[id].name + ' ' + (CORE.searchTags[id] || '')).toLowerCase().indexOf(sq) !== -1);
    }).map(function (id) {
      var card = BEST.filter(function (b) { return b.key === id; })[0];
      return {
        name: PRODUCTS[id].name, meta: PRODUCTS[id].meta, image: PRODUCTS[id].img || '',
        priceStr: money(REAL && card && card.realPrice != null ? card.realPrice : PRODUCTS[id].price),
        href: PRODUCTS[id].url || (card && card.url) || '/products/' + id
      };
    });
  }

  // live Shopify predictive search (debounced); falls back to mapLocal on any failure
  var _searchTimer = null;
  function fetchPredictive(q) {
    q = (q || '').trim();
    if (_searchTimer) clearTimeout(_searchTimer);
    if (!q) { patch({ searchLive: null }); return; }
    _searchTimer = setTimeout(function () {
      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (st.searchQ.trim() !== q) return; // a newer query superseded this one
          var products = data && data.resources && data.resources.results && data.resources.results.products;
          if (!Array.isArray(products)) return; // unexpected shape → keep the local fallback
          patch({ searchLive: products.map(function (p) {
            var img = (p.featured_image && p.featured_image.url) || (typeof p.image === 'string' ? p.image : (p.image && p.image.url)) || '';
            var price = '';
            if (p.price != null) {
              if (typeof p.price === 'number') price = money(p.price / 100);
              else if (/[^\d.\s]/.test(String(p.price))) price = String(p.price);
              else { var pn = parseFloat(String(p.price)); price = isNaN(pn) ? '' : money(pn); }
            }
            // /search/suggest.json's `price` IS price_min, so a $13.49-$38.49 product
            // printed the same bare "$13.49" as the fixed-price 25-count sitting next to
            // it in the same list, and nothing on the row told them apart. Every sibling
            // surface guards this (main-search.liquid:47, main-collection.liquid:265) and
            // presses Enter on the same query to say "from $13.49". The payload's
            // `variants` array is empty here, so price_max > price_min is the test.
            var pmin = parseFloat(String(p.price_min)), pmax = parseFloat(String(p.price_max));
            var ranged = !isNaN(pmin) && !isNaN(pmax) && pmax > pmin;
            if (ranged && price) price = 'from ' + price;
            // `product_type` is not a key this endpoint returns — the field is `type` —
            // so this line always fell through to vendor and printed "Foodiepaws" on
            // every row. Say the multiplicity the row actually needs instead; `type`
            // ("Dog Chews") is the fallback for a single-price product.
            var meta = ranged
              ? ((p.variants && p.variants.length ? p.variants.length + ' pack sizes' : 'multiple pack sizes'))
              : (p.type || '');
            return { name: p.title || '', meta: meta, image: img, priceStr: price, href: p.url || '#' };
          }) });
        })
        // A hard failure must clear the previous query's results, or the panel
        // shows stale matches for a word the shopper has since edited. Only for
        // the query still on screen — a superseded one owns nothing.
        .catch(function () { if (st.searchQ.trim() === q) patch({ searchLive: null }); });
    }, 220);
  }

  function searchResults() {
    // live results when a fetch has populated them; otherwise the built-in catalog fallback
    if (Array.isArray(st.searchLive)) return st.searchLive;
    return mapLocal();
  }

  // NOT gated on REAL. REAL is all-or-nothing across the whole Best Sellers row,
  // so one card without a variant id (unpublished, deleted) threw away the live
  // Shopify price of every other card and fell the whole row back to the blob's
  // literal. A card's own realPrice is true whenever it is present.
  function bestPrice(i) {
    var b = BEST[i] || {};
    return b.realPrice != null ? b.realPrice : (b.price || 0);
  }

  function chewOf(idx) {
    return CHEWS[idx] || CHEWS[0] || { title: '', chipTop: '', chipSub: '', shop: '', shopHref: '#shop', benefits: [] };
  }

  // resolve a binding path against state (+ optional row item)
  function resolve(path, item) {
    var m, totals;
    if (PAGE.resolve) {
      var pv = PAGE.resolve(path, item);
      if (pv !== undefined) return pv;
    }
    if (path.indexOf('item.') === 0) {
      var v = item;
      path.slice(5).split('.').forEach(function (k) { v = v == null ? v : v[k]; });
      return v;
    }
    switch (path) {
      // Same rule as `noItems` below, applied to the number: until /cart.js has
      // answered, cartTotals() counts an empty client-side cart and returns 0 — so the
      // first render overwrote the server's true "Shopping cart (3)" with "(0)". The
      // heading is the ONLY thing the page asserts during that window (the body is
      // held back), and if /cart.js exhausts its retries the invented 0 is permanent.
      // S.cartCount is the server's count, published by theme.liquid; the guard falls
      // through to the client total whenever it is absent, so nothing changes without it.
      // FP47: also on a page that cannot be in demo mode (!DEMO_ABLE — search, pages, blog,
      // article, collection). REAL is decided there by boot()'s own /cart.js read, so
      // before it lands the client count is an empty cart. Their header link still paints
      // the server count from Liquid, now bound to cartCount / cartFootDisplay so the drawer
      // keeps it current, and the drawer footer is gated on this same flag. (A demo product
      // page has BEST empty too, but its count and list are the demo cart's: DEMO_ABLE.)
      case 'cartCount': return (!cartLoaded && S.cartCount != null && (REAL || !DEMO_ABLE)) ? S.cartCount : cartTotals().count;
      case 'showBadge': return (!cartLoaded && S.cartCount != null && (REAL || !DEMO_ABLE)) ? S.cartCount > 0 : cartTotals().count > 0;
      // The drawer's pinned footer (total, CHECKOUT, express wallets, legal line) is real
      // markup so Shopify's express element is never re-created; it is display:none while
      // the cart is empty. Goes through resolve() so a page module's showBadge (the
      // collection page keeps its own count) is honoured.
      case 'cartFootDisplay': return resolve('showBadge', null) ? 'flex' : 'none';
      case 'noDiscount': return !(shopifyCart && shopifyCart.total_discount > 0);
      // Never claim an empty cart before /cart.js has answered: the panel is
      // mounted client-side, so an unresolved request used to render as "your
      // cart is empty" with a shop-more CTA.
      case 'noItems': return (REAL && !cartLoaded) ? false : cartTotals().entries.length === 0;
      case 'subtotalStr': return money(cartTotals().subtotal);
      // The drawer's ledger used to stop at Subtotal and Discount applied and then
      // go straight to CHECKOUT, so a shopper with a discount was left to subtract
      // the two themselves to learn what they were about to pay. Goods only — the
      // drawer states shipping separately in its own free-shipping strip, and tax
      // is not known until checkout.
      case 'payableStr': return money(cartTotals().payable);
      case 'subCongrats': return st.subCongrats;
      // Does THIS cart carry a subscription line? Derived exactly like the per-row
      // isSub in cartItems below, so a surface can state the welcome code's term
      // ("one-time orders only") on screen for the shoppers it applies to, instead of
      // in a `title` no touch device renders. NOT subCongrats: that is a 6-second
      // toast fired when a plan is chosen, and it clears itself while the
      // subscription line is still sitting in the cart.
      case 'hasSub': return cartTotals().entries.some(function (e) { return !!e.sub; });
      case 'drawerX': return st.open ? 'translateX(0%)' : 'translateX(108%)';
      case 'overlayOpacity': return st.open ? 1 : 0;
      case 'overlayPE': return st.open ? 'auto' : 'none';
      case 'mnavOpen': return st.mnav;
      case 'searchOpen': return st.searchOpen;
      case 'searchQText': return st.searchQ;
      // "No chews match" may only be asserted once a predictive response has LANDED
      // and come back empty. The local fallback being empty is not evidence: it holds
      // four flagship products, so every query outside them printed the dead end
      // during the 220 ms debounce — and kept printing it whenever suggest.json is
      // slow, rate-limited or blocked, because .catch puts searchLive back to null.
      case 'searchEmpty': return !!st.searchQ.trim() && Array.isArray(st.searchLive) && st.searchLive.length === 0;
      // Bindable so the pause controls can state their own state
      // (aria-pressed:%heroPaused%, and the label/icon swap off heroPlaying).
      case 'heroPaused': return st.heroPaused;
      case 'heroPlaying': return !st.heroPaused;
      case 'pkPaused': return st.pkPaused;
      case 'pkPlaying': return !st.pkPaused;
      case 'pkSndOn': return st.pkSnd;
      case 'pkSndOff': return !st.pkSnd;
      case 'pkSndBtnOn': return PACK.clipStages.indexOf(st.packStage) !== -1;
      case 'pkCaption': return PACK.captions[st.packStage] || '';
      case 'jwBubble': return st.jw === 'bubble' && !st.jwStatus && !st.jwHeroOn && !jwTucked() && jwClearOfBuy() && !st.jwFocusOn;
      case 'jwMin': return (st.jw === 'min' || (st.jw === 'bubble' && jwTucked())) && !st.jwStatus && !st.jwHeroOn && jwClearOfBuy() && !st.jwFocusOn;
      case 'jwStatus': return !!st.jwStatus;
      case 'jwLoading': return st.jwStatus === 'loading';
      case 'jwError': return st.jwStatus === 'error';
      // Shopify's rejection of the newsletter POST, hoisted out of the card template
      // by sections/join-widget.liquid. A rejected submit reloads the page and the
      // machine boots 'off' → 'bubble', never 'card', so anything Liquid wrote inside
      // the card was simply not in the document — the shopper resubmitted the same
      // bad address with nothing on screen saying why.
      // All four read `payable` (post-discount), the base the shipping rate is
      // actually charged on — see cartTotals().
      // The free rate exists in the Domestic (US) zone ONLY — the Canada zone
      // carries two carrier-calculated rates and no free threshold at all — so the
      // whole strip is gated on the shopper's country. Defaults to 'US' so a theme
      // served before FP_SETTINGS.country lands does not blank the strip for
      // everyone.
      case 'shipUS': return (S.country || 'US') === 'US';
      case 'shipUnlocked': totals = cartTotals(); return (S.country || 'US') === 'US' && totals.payable > 0 && (S.freeShipThreshold - totals.payable) <= 0;
      case 'shipLocked': totals = cartTotals(); return (S.country || 'US') === 'US' && (totals.payable === 0 || (S.freeShipThreshold - totals.payable) > 0);
      case 'shipRemainingStr': totals = cartTotals(); return money(Math.max(0, S.freeShipThreshold - totals.payable));
      case 'shipPct': totals = cartTotals(); return Math.min(100, Math.round((totals.payable / S.freeShipThreshold) * 100)) + '%';
      case 'cartItems':
        return cartTotals().entries.map(function (e) {
          return {
            name: e.name, qty: e.qty, meta: fpMetaNoFreq(e.meta), lineStr: money(e.line),
            // The drawer's subscription chip mounts on item.isSub and prints
            // item.freqLabel. Both were published by the cart page's resolver
            // only, so the same line read as a plain one-time purchase in the
            // drawer and as a subscription on /cart.
            isSub: !!e.sub, freqLabel: fpFreqLabel(e),
            img: e.img || '', url: e.url || '', hasImg: !!e.img,
            isBully: !e.img && e.id.indexOf('bully') === 0, isCollagen: !e.img && e.id === 'collagen', isTripe: !e.img && e.id === 'tripe', isVariety: !e.img && e.id === 'variety',
            _key: e._key
          };
        });
      // A cart discount (arrived through /discount/WELCOME50, or an automatic
      // one) moved the totals with nothing on either surface saying so. Both the
      // drawer and /cart mount their discount row on these two keys, so they stay
      // absent — not wrong — on a cart that carries no discount and in demo mode,
      // where there is no Shopify cart to read.
      case 'hasDiscount': return !!(shopifyCart && shopifyCart.total_discount > 0);
      case 'discountStr': return '-' + money(((shopifyCart && shopifyCart.total_discount) || 0) / 100);
      case 'searchResults': return searchResults();
      // The welcome (first-order) percent. Its single binding in the theme is the
      // marketing opt-in on the auth page - "my {N}% off first-bag code" - which is
      // the Join the Pack offer, not the recurring subscription discount. Pointing
      // this at subscribeSavePercent made Liquid paint 50 and this overwrite it
      // with 20 on the same line.
      case 'promoPct': return S.subscribeDiscount + '%';
    }
    if ((m = path.match(/^heroOp([0-2])$/))) return st.heroIndex % 3 === +m[1] ? 1 : 0;
    if ((m = path.match(/^heroDot([0-2])$/))) return st.heroIndex % 3 === +m[1] ? 'rgba(255,255,255,.95)' : 'rgba(255,255,255,.45)';
    // State for assistive tech, beside the opacity crossfade that drives the eye.
    // The slides are stacked and all three stay in the accessibility tree, so a
    // screen reader read every hero slide and every stage caption at once as though
    // they were all on screen. STRINGS, because data-fp-a writes the attribute
    // verbatim: aria-hidden="false" is meaningful, aria-hidden="" is not.
    if ((m = path.match(/^heroHid([0-2])$/))) return st.heroIndex % 3 === +m[1] ? 'false' : 'true';
    if ((m = path.match(/^heroCur([0-2])$/))) return st.heroIndex % 3 === +m[1] ? 'true' : 'false';
    if ((m = path.match(/^pkHid(\d+)$/))) return st.packStage === +m[1] ? 'false' : 'true';
    if ((m = path.match(/^pkCur(\d+)$/))) return st.packStage === +m[1] ? 'true' : 'false';
    if ((m = path.match(/^pkOp(\d+)$/))) return st.packStage === +m[1] ? 1 : 0;
    if ((m = path.match(/^pkTb(\d+)$/))) return st.packStage === +m[1] ? '3px solid #FFD34D' : '3px solid rgba(255,255,255,.45)';
    if ((m = path.match(/^pkTo(\d+)$/))) return st.packStage === +m[1] ? 1 : 0.72;
    if ((m = path.match(/^q(\d+)\.(open|sym)$/))) {
      var open = st.faqIdx === (+m[1] - 1);
      return m[2] === 'open' ? open : (open ? '−' : '+');
    }
    if (path.indexOf('chew.') === 0) {
      var c = chewOf(st.chewIdx);
      var key = path.slice(5);
      var dotIdx = c.title.indexOf('. ');
      var tm;
      if ((tm = key.match(/^op(\d+)$/))) return st.chewIdx === +tm[1] ? 1 : 0;
      if ((tm = key.match(/^tb(\d+)$/))) return st.chewIdx === +tm[1] ? '#FFB92E' : 'rgba(255,233,201,.4)';
      if ((tm = key.match(/^tbg(\d+)$/))) return st.chewIdx === +tm[1] ? '#FFB92E' : 'transparent';
      if ((tm = key.match(/^tc(\d+)$/))) return st.chewIdx === +tm[1] ? '#3D1409' : '#FFE9C9';
      if ((tm = key.match(/^b([0-3])\.(t|s)$/))) {
        var ben = (c.benefits || [])[+tm[1]] || { t: '', s: '' };
        return ben[tm[2]];
      }
      switch (key) {
        case 'title': return c.title;
        case 'titleA': return dotIdx !== -1 ? c.title.slice(0, dotIdx + 1) : c.title;
        case 'titleB': return dotIdx !== -1 ? c.title.slice(dotIdx + 2) : '';
        case 'chipTop': return c.chipTop;
        case 'chipSub': return c.chipSub;
        case 'shop': return c.shop;
        case 'shopHref': return c.shopHref || '#shop';
      }
    }
    if ((m = path.match(/^best(\d+)\.(.+)$/))) {
      var i = +m[1];
      var b = BEST[i] || {};
      var oneSel = st.sel[i] !== 'sub';
      var f = st.freq[i] || 4;
      var price = bestPrice(i);
      switch (m[2]) {
        case 'onePriceStr': return money(price);
        case 'subPriceStr': return money(subOf(price));
        case 'ctaPriceStr': return money(oneSel ? price : subOf(price));
        case 'isSub': return !oneSel;
        case 'fLabel': return f + (f === 1 ? ' WEEK' : ' WEEKS');
        // The chip marks the cadence this card OPENED on, whatever the merchant set
        // it to. Pinned to the literal 4 it was not derived from anything: it always
        // landed on the old hardcoded seed, and would have gone on lying about which
        // row was the default the moment the setting moved.
        case 'fPop': return f === (bestSeedWeeks[i] != null ? bestSeedWeeks[i] : 4);
        case 'oneDotO': return oneSel ? '#F58220' : 'transparent';
        case 'subDotO': return oneSel ? 'transparent' : '#F58220';
        case 'oneColorO': return oneSel ? '#3D1409' : '#8A5A2E';
        case 'subColorO': return oneSel ? '#8A5A2E' : '#3D1409';
        // "with Subscribe" pricing may only be advertised when the product
        // carries a real selling plan (checkout can honor the discount).
        //
        // NO "As low as"/"From": both price and unitCount come from the FIRST
        // variant, and this catalog gets CHEAPER per chew as the pack grows —
        // the #1 card quoted $2.16/stick while its 25-pack works out at $1.23,
        // so the superlative understated the store's own best value by 76%.
        // This is the rate for the price shown on the card, and says only that.
        case 'unitNote': return b.sellingPlanId
          ? money(subOf(price) / (b.unitCount || 1)) + '/' + (b.unitNoun || 'chew') + ' with Subscribe'
          : money(price / (b.unitCount || 1)) + '/' + (b.unitNoun || 'chew');
      }
    }
    return '';
  }

  function fill(tpl, item) {
    return tpl.replace(/%([^%]+)%/g, function (_s, p) {
      var v = resolve(p, item);
      return v == null ? '' : String(v);
    });
  }

  // ---------- accessibility toolkit ----------
  // One persistent polite live region, created ONCE at boot. A node inserted in
  // the same tick as its text is routinely swallowed by screen readers, which is
  // why the add-to-cart confirmation could not simply reuse the error slot; and
  // aria-live must never go on the cart-count span, which render() rewrites on
  // every state change including opening the drawer.
  var fpLiveEl = null;
  function fpLiveRegion() {
    if (fpLiveEl && fpLiveEl.parentNode) return fpLiveEl;
    fpLiveEl = document.getElementById('fpLive');
    if (!fpLiveEl) {
      fpLiveEl = document.createElement('div');
      fpLiveEl.id = 'fpLive';
      fpLiveEl.setAttribute('role', 'status');
      fpLiveEl.setAttribute('aria-live', 'polite');
      fpLiveEl.setAttribute('aria-atomic', 'true');
      fpLiveEl.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;'
        + 'overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0';
      document.body.appendChild(fpLiveEl);
    }
    return fpLiveEl;
  }
  function fpAnnounce(text) {
    if (!text) return;
    var el = fpLiveRegion();
    // Writing the same string twice in a row is silent in most screen readers;
    // clearing first makes a second identical add audible.
    el.textContent = '';
    window.setTimeout(function () { el.textContent = text; }, 60);
  }

  // The drawer markup (sections/cart-drawer.liquid:43) now carries id="fpCartDrawer";
  // the data-screen-label lookup stays as the fallback so this file keeps working
  // against an older section, and so it never depends on a label attribute alone.
  // role/aria-modal/tabindex/inert are set from openDrawer()/closeDrawer()/
  // fpDrawerReachable() in the one safe order (unhide BEFORE focus, blur BEFORE
  // re-hide) — the section's static copies are the CLOSED state, not a duplicate
  // of that sequence.
  function fpDrawerEl() {
    return document.getElementById('fpCartDrawer')
      || document.querySelector('aside[data-screen-label="Cart Drawer"]');
  }
  function fpFocusables(root) {
    return Array.prototype.slice.call(root.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null || el.getClientRects().length; });
  }
  var drawerReturn = null, drawerTrap = null;
  function fpTrapFor(root) {
    return function (ev) {
      if (ev.key !== 'Tab') return;
      var f = fpFocusables(root);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
      else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
    };
  }
  // A drawer that is only translated off-screen still holds its seven controls in
  // the tab order on every page it renders on. Make the closed state genuinely
  // unreachable, and never leave aria-hidden on an element that contains focus.
  function fpDrawerReachable(on) {
    var d = fpDrawerEl();
    if (!d) return null;
    if (on) {
      d.removeAttribute('aria-hidden');
      if ('inert' in d) d.inert = false; else d.removeAttribute('inert');
    } else {
      d.setAttribute('aria-hidden', 'true');
      if ('inert' in d) d.inert = true; else d.setAttribute('inert', '');
    }
    return d;
  }
  // Fix round 5. The failure toast (#fpToast, fpToastRegion) is fixed at bottom:24px and
  // centred on the PAGE. Since FP47 the drawer's pinned footer puts CHECKOUT at the bottom of
  // the screen, so a toast raised inside the open drawer (a refused '+': a 422 stock or line
  // limit, a 429, a 5xx) sat on CHECKOUT's label and amount for its full 6 s on phones and
  // tablets (verify-ui r4: 712 of 712 frames at 390, 720 of 720 at 768; LIVE FP48 0).
  // While the drawer is open, html.fp-cd-open moves the toast over the drawer, 12px above
  // that footer (the rule is in sections/cart-drawer.liquid). The footer's height is the
  // cart's: a discount adds a ledger and an empty cart hides it, so it is measured, and kept
  // current while the drawer is open, never assumed.
  var cdGeoObs = null, cdGeoFor = null, cdGeoFoot = null;
  function fpDrawerGeo(d, on) {
    var root = document.documentElement;
    root.classList.toggle('fp-cd-open', !!on);
    if (!on || !d) return;
    var foot = d.querySelector('[data-fp-cd-foot]');
    var write = function () {
      root.style.setProperty('--fp-cd-w', d.offsetWidth + 'px');
      root.style.setProperty('--fp-cd-foot-h', (foot ? foot.offsetHeight : 0) + 'px');
    };
    write();
    if (typeof ResizeObserver !== 'function' || (cdGeoObs && cdGeoFor === d && cdGeoFoot === foot)) return;
    if (cdGeoObs) cdGeoObs.disconnect();
    cdGeoFor = d; cdGeoFoot = foot;
    cdGeoObs = new ResizeObserver(write);
    cdGeoObs.observe(d);
    if (foot) cdGeoObs.observe(foot);
  }
  function openDrawer() {
    if (st.open) { patch({ open: true }); return; }
    drawerReturn = document.activeElement;
    patch({ open: true });
    var d = fpDrawerReachable(true);
    if (!d) return;
    fpDrawerGeo(d, true);
    d.setAttribute('role', 'dialog');
    d.setAttribute('aria-modal', 'true');
    if (!d.hasAttribute('tabindex')) d.setAttribute('tabindex', '-1');
    document.body.style.overflow = 'hidden';
    // FP47: the drawer has one scroll area. Shopify lists the newest line first, so an
    // open after an add must start at the top, not where the shopper last left it.
    var sc = d.querySelector('[data-fp-cd-scroll]');
    if (sc) sc.scrollTop = 0;
    drawerTrap = fpTrapFor(d);
    d.addEventListener('keydown', drawerTrap);
    var f = fpFocusables(d);
    (f[0] || d).focus({ preventScroll: true });
    // Fix round 3 (rule 5 of the express block): a drawer opened while its wallets are still
    // missing asks Shopify again, bounded by fpExpressRearm itself.
    fpExpressRearm();
  }
  function closeDrawer() {
    var d = fpDrawerEl();
    if (d && drawerTrap) { d.removeEventListener('keydown', drawerTrap); }
    drawerTrap = null;
    // Three surfaces now hold this lock (drawer, mobile menu, search overlay), so
    // closing one may not release it while another still owns the screen.
    if (!st.mnav && !st.searchOpen) document.body.style.overflow = '';
    // Blur BEFORE aria-hidden lands, so focus is never inside a hidden subtree.
    if (d && d.contains && d.contains(document.activeElement) && document.activeElement.blur) document.activeElement.blur();
    patch({ open: false });
    fpDrawerReachable(false);
    fpDrawerGeo(d, false);
    // The opener first; the header's cart button (sections/header.liquid, id
    // fpCartBtn) when the opener is gone or was never a control. Measured
    // 2026-09-06: after Escape on the desktop PDP drawer document.activeElement
    // was <body> — the stored opener was the document itself (the drawer opened
    // from an add-to-cart whose button had been re-rendered) — while the mobile
    // menu, which falls back to its button by id, returned focus correctly. Same
    // shape as closeSearchOverlay/setMnav now.
    var back = (drawerReturn && drawerReturn !== document.body && drawerReturn.focus && document.contains(drawerReturn))
      ? drawerReturn
      : (document.getElementById('fpCartBtn') || document.querySelector('[data-fp-on="click:openCart"]'));
    drawerReturn = null;
    if (back && back.focus) back.focus({ preventScroll: true });
  }
  // ---------- header overlays: search + mobile menu ----------
  // Both are <template data-fp-if> in sections/header.liquid, so the panel is NOT
  // in the DOM synchronously after patch() — every focus move has to wait a tick,
  // the same way the search input already did. And a keydown listener bound to the
  // panel would be thrown away with it on the next render, so the trap and Escape
  // live on window and resolve the panel at event time.
  var searchReturn = null, mnavReturn = null;
  function openSearchOverlay() {
    searchReturn = document.activeElement;
    patch({ searchOpen: true, searchQ: '', searchLive: null });
    // The third full-screen dialog, and the only one that let the storefront scroll
    // behind it: a swipe starting outside the white panel moved the page under the
    // blurred backdrop, so closing the overlay returned the shopper to a scroll
    // position they never chose. The drawer and the mobile menu both lock here.
    document.body.style.overflow = 'hidden';
    window.setTimeout(function () {
      var i = document.getElementById('fpSearchInput');
      if (i) i.focus();
    }, 0);
  }
  function closeSearchOverlay() {
    var ov = document.getElementById('fpSearchOverlay');
    // Blur before the overlay is unmounted, so focus never falls to <body>.
    if (ov && ov.contains(document.activeElement) && document.activeElement.blur) document.activeElement.blur();
    patch({ searchOpen: false, searchQ: '', searchLive: null });
    // The drawer and the mobile menu hold the same lock — releasing it here while
    // either is open would let the page behind THEM scroll. Same guard setMnav uses.
    if (!st.open && !st.mnav) document.body.style.overflow = '';
    // The magnifier is where the shopper was, and it is the control that reopens
    // this — the stored element is only the fallback (the #search deep link opens
    // the overlay with nothing meaningful to go back to).
    var back = document.getElementById('fpSearchBtn')
      || (searchReturn && document.contains(searchReturn) ? searchReturn : null);
    searchReturn = null;
    if (back && back.focus) back.focus({ preventScroll: true });
  }
  function setMnav(on) {
    if (on) {
      mnavReturn = document.activeElement;
      patch({ mnav: true });
      document.body.style.overflow = 'hidden';
      window.setTimeout(function () {
        var p = document.getElementById('fpMnav');
        if (!p) return;
        if (!p.hasAttribute('tabindex')) p.setAttribute('tabindex', '-1');
        var f = fpFocusables(p);
        (f[0] || p).focus({ preventScroll: true });
      }, 0);
      return;
    }
    var panel = document.getElementById('fpMnav');
    if (panel && panel.contains(document.activeElement) && document.activeElement.blur) document.activeElement.blur();
    patch({ mnav: false });
    // The drawer and the search overlay hold the same lock. Releasing it here while
    // either is open would let the page behind IT scroll.
    if (!st.open && !st.searchOpen) document.body.style.overflow = '';
    var back = document.getElementById('fpMnavBtn')
      || (mnavReturn && document.contains(mnavReturn) ? mnavReturn : null);
    mnavReturn = null;
    if (back && back.focus) back.focus({ preventScroll: true });
  }

  // Focus restore across a list rebuild. Skipped when the list SHRANK (remove, or
  // "−" at quantity 1): the same index is a different product then, so focus goes
  // to the drawer's close button instead of onto someone else's stepper.
  function fpRestoreRowFocus(rec, wantRow, wantOn, hadRows) {
    if (rec.rows.length < hadRows || wantRow >= rec.rows.length) {
      // The whole recovery used to sit inside `if (st.open)` — the DRAWER's flag,
      // which is always false on /cart. So a shopper on the cart page who pressed ✕,
      // or "−" at quantity 1, lost focus to <body> and had to tab from the top of the
      // document. Fall back through the page's own controls, not only the drawer's.
      var back = null;
      if (st.open) {
        var d = fpDrawerEl();
        back = d && d.querySelector('[data-fp-on="click:closeCart"]');
      }
      if (!back && rec.rows.length) {
        var near = rec.rows[Math.min(wantRow, rec.rows.length - 1)];
        back = near && near.querySelector ? near.querySelector('[data-fp-ctl="remove"]') : null;
      }
      // goCheckout is /cart's button; the drawer's is click:checkout, so this cannot
      // pull focus into a closed drawer.
      if (!back) back = document.querySelector('[data-fp-on="click:goCheckout"]') || document.getElementById('top');
      if (back && back.focus) back.focus({ preventScroll: true });
      return;
    }
    var root = rec.rows[wantRow];
    if (!root) return;
    var back = (root.getAttribute && root.getAttribute('data-fp-on') === wantOn)
      ? root
      : root.querySelector('[data-fp-on="' + wantOn.replace(/"/g, '\\"') + '"]');
    if (back && back.focus) back.focus({ preventScroll: true });
  }

  // ---------- in-flight guard ----------
  // Every add-to-cart path was unguarded: a double tap posted twice and added two
  // units, or two subscription lines. fpLock disables the control and hands back a
  // release function that MUST be called on both the success and the failure path,
  // so a dropped request can never leave the button dead.
  function fpBusy(el) { return !!(el && el.getAttribute && el.getAttribute('data-fp-busy') === '1'); }
  function fpLock(el) {
    if (!el || !el.tagName) return function () {};
    var isBtn = el.tagName === 'BUTTON' || el.tagName === 'INPUT';
    // Restore what was there, never a hardcoded "enabled": releasing must not
    // switch on a control that was already disabled for its own reason.
    var prevOpacity = el.style.opacity;
    var prevPE = el.style.pointerEvents;
    var wasDisabled = !!el.disabled;
    el.setAttribute('data-fp-busy', '1');
    el.setAttribute('aria-busy', 'true');
    if (isBtn) el.disabled = true; else el.style.pointerEvents = 'none';
    el.style.opacity = '.6';
    var released = false;
    return function () {
      if (released) return;
      released = true;
      el.removeAttribute('data-fp-busy');
      el.removeAttribute('aria-busy');
      if (isBtn) el.disabled = wasDisabled; else el.style.pointerEvents = prevPE;
      el.style.opacity = prevOpacity;
    };
  }

  // ---------- render ----------
  function applySpec(spec) {
    var el = spec.el, item = spec.item;
    // Write only on a real change. render() runs on every state change — cart fetch,
    // add-to-cart, drawer open/close — and an unconditional assignment replaces the
    // text node each time. Inside a live region (the collection header count carries
    // role="status") that is a mutation, so an unchanged sentence was re-announced;
    // everywhere else it is pure DOM churn.
    var _t;
    if (spec.t != null) { _t = fill(spec.t, item); if (el.textContent !== _t) el.textContent = _t; }
    if (spec.t0 != null && el.firstChild && el.firstChild.nodeType === 3) { _t = fill(spec.t0, item); if (el.firstChild.nodeValue !== _t) el.firstChild.nodeValue = _t; }
    spec.a.forEach(function (pair) {
      var val = fill(pair[1], item);
      if (pair[0] === 'value') { if (el.value !== val) el.value = val; }
      else el.setAttribute(pair[0], val);
    });
    spec.s.forEach(function (pair) {
      var raw = pair[1];
      var val = raw.indexOf('tpl(') === 0 ? fill(raw.slice(4, -1), item) : resolve(raw, item);
      el.style.setProperty(pair[0], val == null ? '' : String(val));
    });
  }

  function buildRows(tplEl, item) {
    var frag = tplEl.content.cloneNode(true);
    Array.prototype.slice.call(frag.querySelectorAll('template[data-fp-if]')).forEach(function (t) {
      var ok = !!resolve(t.getAttribute('data-fp-if'), item);
      if (ok) t.parentNode.replaceChild(t.content.cloneNode(true), t);
      else t.parentNode.removeChild(t);
    });
    var nodes = Array.prototype.slice.call(frag.childNodes).filter(function (n) { return n.nodeType === 1; });
    nodes.forEach(function (n) {
      n.__fpItem = item;
      var all = [n].concat(Array.prototype.slice.call(n.querySelectorAll('*')));
      all.forEach(function (e) {
        var spec = parseHooks(e, item);
        if (spec) applySpec(spec);
      });
    });
    return nodes;
  }

  function mountIf(rec) {
    var frag = rec.tpl.content.cloneNode(true);
    var nodes = Array.prototype.slice.call(frag.childNodes).filter(function (n) { return n.nodeType === 1; });
    rec.tpl.parentNode.insertBefore(frag, rec.tpl);
    rec.mounted = nodes;
    rec.specs = [];
    rec.children = [];
    nodes.forEach(function (n) {
      var all = [n].concat(Array.prototype.slice.call(n.querySelectorAll('*')));
      all.forEach(function (e) {
        if (e.tagName === 'TEMPLATE') return;
        if (e.closest && e.closest('template') && e.closest('template') !== null && e.getRootNode() !== document) return;
        // only register hooks that are NOT inside a nested template
        var p = e.parentElement, inTpl = false;
        while (p && p !== n.parentElement) {
          if (p.tagName === 'TEMPLATE') { inTpl = true; break; }
          p = p.parentElement;
        }
        if (inTpl) return;
        var spec = parseHooks(e, rec.item);
        if (spec) rec.specs.push(spec);
      });
      Array.prototype.slice.call(n.querySelectorAll('template[data-fp-if]')).forEach(function (t) {
        var child = { tpl: t, expr: t.getAttribute('data-fp-if'), item: rec.item, mounted: null, specs: [], children: [] };
        ifTpls.push(child);
        rec.children.push(child);
      });
      Array.prototype.slice.call(n.querySelectorAll('template[data-fp-each]')).forEach(function (t) {
        var childE = { tpl: t, expr: t.getAttribute('data-fp-each'), sig: '', rows: [] };
        eachTpls.push(childE);
        rec.childrenEach = rec.childrenEach || [];
        rec.childrenEach.push(childE);
      });
    });
  }

  function unmountIf(rec) {
    (rec.children || []).forEach(function (child) {
      unmountIf(child);
      var ix = ifTpls.indexOf(child);
      if (ix !== -1) ifTpls.splice(ix, 1);
    });
    (rec.childrenEach || []).forEach(function (childE) {
      (childE.rows || []).forEach(function (r) { if (r.parentNode) r.parentNode.removeChild(r); });
      var ix2 = eachTpls.indexOf(childE);
      if (ix2 !== -1) eachTpls.splice(ix2, 1);
    });
    rec.childrenEach = [];
    if (rec.mounted) rec.mounted.forEach(function (n) { if (n.parentNode) n.parentNode.removeChild(n); });
    rec.mounted = null;
    rec.specs = [];
    rec.children = [];
  }

  function render() {
    // conditional mounts (iterate over a copy — mounts can append children)
    // fpRestoreRowFocus below cannot cover the LAST row: emptying the cart also
    // fails `hasItems`, so this loop unmounts the whole panel and drops the each-
    // record before the row-level recovery can run. Note the orphaning here and
    // repair it after the render, but only if focus really did fall to <body>.
    var focusOrphaned = false;
    // Mounting a template REGISTERS the conditional templates inside it (mountIf ->
    // ifTpls.push), and this walks a snapshot, so those children were not evaluated
    // until some LATER render happened to run. Measured on the live drawer: adding
    // the first item mounted `showBadge` and painted the free-shipping progress bar
    // filled to 39% with NO caption beside it — "You're $21.51 away from FREE US
    // shipping" is a child of that badge — and the words only appeared once the
    // shopper reopened the drawer or reloaded. Sampled every 500ms for 8s after the
    // add: empty the whole time. So sweep again while a pass mounted something new,
    // and a parent settles together with its children in ONE render. The bound is a
    // guard against a pair of conditions that flip each other, not a real depth.
    var sweeps = 0;
    var mountedThisPass = true;
    while (mountedThisPass && sweeps < 8) {
      mountedThisPass = false;
      sweeps++;
      ifTpls.slice().forEach(function (rec) {
        if (!rec.tpl.parentNode) return;
        var want = !!resolve(rec.expr, rec.item);
        if (want && !rec.mounted) { mountIf(rec); mountedThisPass = true; }
        else if (!want && rec.mounted) {
          var act = document.activeElement;
          if (act && act !== document.body && rec.mounted.some(function (n) {
            return n === act || (n.contains && n.contains(act));
          })) focusOrphaned = true;
          unmountIf(rec);
        }
      });
    }
    // lists
    eachTpls.forEach(function (rec) {
      var list = resolve(rec.expr, null) || [];
      var sig = JSON.stringify(list);
      if (sig !== rec.sig) {
        // Every cart row is torn down and rebuilt on any quantity change, so a
        // keyboard user pressing "+" lost focus to <body> on the first press and
        // could not press it again without tabbing back through the whole drawer.
        // Remember WHICH row and WHICH control had focus, and put it back.
        var act = document.activeElement;
        var wantRow = -1, wantOn = null, hadRows = rec.rows.length;
        if (act && act !== document.body) {
          for (var ri = 0; ri < rec.rows.length; ri++) {
            var row = rec.rows[ri];
            if (row === act || (row.contains && row.contains(act))) {
              wantRow = ri;
              wantOn = act.getAttribute ? act.getAttribute('data-fp-on') : null;
              break;
            }
          }
        }
        rec.rows.forEach(function (n) { if (n.parentNode) n.parentNode.removeChild(n); });
        rec.rows = [];
        list.forEach(function (item) {
          buildRows(rec.tpl, item).forEach(function (n) {
            rec.tpl.parentNode.insertBefore(n, rec.tpl);
            rec.rows.push(n);
          });
        });
        rec.sig = sig;
        if (wantRow !== -1 && wantOn) fpRestoreRowFocus(rec, wantRow, wantOn, hadRows);
      }
    });
    // bound nodes (static + currently mounted)
    boundNodes.forEach(applySpec);
    ifTpls.forEach(function (rec) { (rec.specs || []).forEach(applySpec); });
    // FP47: /cart's grid and sticky bar are real markup hidden with display:none (they used
    // to be an unmounted template, which the focusOrphaned path below covers). Removing the
    // last line hands focus to the summary's CHECKOUT a moment before the grid hides, and a
    // focused element inside display:none falls to <body>. Land it where the template path
    // lands it.
    var aeHidden = document.activeElement;
    if (aeHidden && aeHidden !== document.body && aeHidden.closest && aeHidden.closest('#fpCartGrid, #fpCartBar')
        && !aeHidden.getClientRects().length) {
      var topEl = document.getElementById('top');
      if (topEl && topEl.focus) topEl.focus({ preventScroll: true });
    }
    // Land the orphaned focus somewhere real. Only when nothing else has already
    // claimed it — closeDrawer, closeSearchOverlay and setMnav all move focus
    // themselves AFTER their patch(), so this never fights them for the final say.
    // #top is the skip-link target and carries tabindex="-1", so it takes focus
    // without drawing a ring on something the shopper did not press.
    if (focusOrphaned && document.activeElement === document.body) {
      var land = document.querySelector('[data-fp-on="click:goCheckout"]') || document.getElementById('top');
      if (land && land.focus) land.focus({ preventScroll: true });
    }
  }

  function patch(p) {
    Object.keys(p).forEach(function (k) { st[k] = p[k]; });
    render();
  }

  // ---------- cart ----------
  function saveDemoCart() {
    try { localStorage.setItem('foodiepaws-cart-v1', JSON.stringify(st.cart)); } catch (e) { /* private mode */ }
  }
  function loadDemoCart() {
    try {
      var saved = JSON.parse(localStorage.getItem('foodiepaws-cart-v1') || 'null');
      if (saved && typeof saved === 'object' && !Array.isArray(saved)) st.cart = saved;
    } catch (e) { /* keep empty */ }
  }
  // ---------- express checkout buttons (FP47, CART-02 / TIKTOK-06) ----------
  // Shopify's <shopify-accelerated-checkout-cart> is server-rendered with the cart it was
  // rendered for (its `cart-id` attribute = the cart token without its ?key). This theme
  // changes the cart client-side through /cart/add.js and /cart/change.js, and Shopify
  // documents one way for these buttons to follow a cart change: re-render the section that
  // holds them (Section Rendering API). Measured on FP47 2026-10-20: a visitor with no cart
  // yet gets a throwaway cart-id on every render; the first add creates the real cart (a new
  // token), and the element already on the page kept the throwaway id until a reload.
  //
  // Shopify's render cache (fix rounds 1 and 2, measured on FP47 2026-10-20). Renders that hold
  // this element are cached by what is IN the cart (lines, and cart attributes too), not by
  // cart, and the element prints the cart-id: a visitor whose cart matched another cart was
  // handed THAT cart's element, by the page itself, by GET ?sections= and by POST alike
  // (/cart/update.js and /cart/change.js with `sections`), at any path, for at least minutes
  // (carts emptied 10-25 min earlier were still named). A shared single-line cart was named
  // in 5 renders of 6; the same cart with a throwaway cart attribute added, 0 of 6 (and 6 of 6
  // again once it was removed). Nothing the theme sends without changing the cart alters that:
  // a sections_url query nonce, the section reading cart.token, and a unique render path were
  // each tested and still leaked. So nothing is trusted. Renders are asked for the drawer
  // section at `/pages/fp-wallets-<random>` (a 404 page: the drawer is in the footer group of
  // every template, and its box renders there, attribute-for-attribute the element '/' and
  // '/cart' render), and every box on the page, the /cart one included, takes its element
  // from that one render. Five rules, enforced in fpExpressMark / fpExpressApply /
  // fpExpressStep / fpExpressRearm:
  //   1. A fresh element is adopted only when its cart-id equals this cart's token (the token
  //      the same reply describes, or for a GET render the last token the cart API reported).
  //      Never another visitor's cart, whatever the cache does.
  //   2. A box shows only this cart's current element, and an element that names another cart
  //      never joins the page: Shopify's component sets up its wallets against the cart-id it
  //      carries whether or not its box is visible (fix round 3: a hidden foreign element held 2
  //      wallet buttons). Every element arrives inert, inside <template data-fp-express-src>: the
  //      page's own copy (the inline script right after each box, cart-drawer.liquid and
  //      main-cart.liquid, moves it into the box during parsing only when its cart-id equals the
  //      `cart` cookie's token, else drops it) and every render (fpExpressSrc reads it out of the
  //      template of a DOMParser document, which never upgrades custom elements, and rule 1
  //      decides). fpExpressMark removes an element already in a box whose cart-id stops being
  //      this cart's (the token changed). Nothing of a refused element is kept: rule 3 only ever
  //      compares with this cart's own element. An element of this cart that is known to be out
  //      of date stays, hidden, until a current one replaces it. The boxes are server-rendered
  //      `hidden` and shown only once checked. CHECKOUT, which reads the cart cookie, is
  //      unaffected.
  //   3. An element of this cart that says the same about the cart (requires-shipping,
  //      has-selling-plan, the wallet list) is left alone, so a quantity tap does not flash the
  //      buttons back to their loading skeleton. The wallets' own parameters are left out of
  //      that comparison on purpose: Google Pay's carries per-render JWTs and expiry times, which
  //      differ on every render of the same cart (measured). The wallets inside a kept element
  //      re-query the cart themselves (Shopify's component, POST /api/unstable/graphql.json).
  //      A render that names another cart was rendered for the same lines, so it still tells
  //      whether ours is current: if it says the same, ours is kept; if not, ours is out of date.
  //   4. A render that is not this cart's (or a failed request) is not the end: a chain asks
  //      again (EXP_MAIN, 8 attempts over ~38 s), each time at a fresh unique path, mostly by GET
  //      ?sections= (~0.2 s, not a cart-API call) and at most EXP_POST_MAX times per cart state
  //      by POST /cart/update.js with `sections` only (no line, note or attribute is sent, so the
  //      cart is not changed). Measured over the round-2 verification runs: GET answered with this
  //      cart's render 25 times in 38, that POST 4 times in 13, so the GET is the default and the
  //      POST only varies the way of asking. A chain stops at the first render of this cart, when
  //      a newer cart change starts its own (expSeq), or when its attempts run out.
  //   5. The chain re-arms. Opening the drawer and the page becoming visible again
  //      (visibilitychange) start a short one (EXP_REARM: 4 attempts over ~15 s) when no chain is
  //      running, no quantity change is in flight, a box still lacks this cart's element, and the
  //      last re-arm is at least EXP_REARM_GAP old. Bounded: nothing runs between those events,
  //      and every chain of one cart state shares the same POST budget.
  //   6. The subscription consent (FP50, measured on live FP49 2026-10-20). Shopify renders it
  //      NEXT TO the element, not inside it: <small id="shopify-buyer-consent"
  //      data-consent-type="subscription"> in the same .dynamic-checkout__content, whenever the
  //      cart holds a selling-plan line. The element itself only flips its own has-selling-plan
  //      attribute when the cart changes (it re-queries the cart), so rule 3 saw "the same" and
  //      kept a box that had never had the paragraph: after a one-time line, a Subscribe add
  //      showed the wallets with no consent until a reload. The element wires the paragraph's
  //      "cancellation policy" button (#shopify-subscription-policy-button) when it CONNECTS
  //      (measured: a paragraph inserted on its own had no click listener, the same markup set
  //      with its element had one), so the paragraph is never patched in: the box is re-rendered.
  //      What a box says therefore includes its consent (fpExpressConsent) in rule 3's comparison,
  //      and every fpExpressMark checks each box by what it holds against the cart (fpCartSp,
  //      fpExpressSpCheck): the paragraph is removed at once when the cart no longer needs it
  //      (removing adopts nothing), and a box without the paragraph the cart needs is out of date
  //      (stale, hidden; fpExpressApply reports it as not done) until a render of this cart
  //      re-creates it with its paragraph. Wallets are never shown without the consent the cart
  //      needs, and never with one it does not.
  // While other carts with the same lines keep that cache filled, a visitor can still end up
  // with no wallets for a while (the accepted platform limit: the one cure found, a throwaway
  // cart attribute, writes to the shopper's cart and is an owner decision), never another
  // visitor's; CHECKOUT works throughout, and a later render of this cart shows them at once.
  var EXP_MAIN = [[250, 'G'], [600, 'G'], [1200, 'P'], [2000, 'G'], [3500, 'G'], [6000, 'P'], [10000, 'G'], [15000, 'G']];
  var EXP_REARM = [[0, 'G'], [1500, 'G'], [4000, 'P'], [9000, 'G']];
  var EXP_POST_MAX = 3, EXP_REARM_GAP = 10000;
  var EXP_EL = 'shopify-accelerated-checkout-cart', EXP_SP = 'data-fp-express-sp';
  var expTimer = null, expSeq = 0, expChain = 0, expLive = false, expPostSeq = -1, expPosts = 0, expRearmAt = 0;
  // What an element says about the cart, without the cart-id itself (rule 3).
  function fpExpressFacts(el) {
    if (!el || !el.attributes) return '';
    return Array.prototype.map.call(el.attributes, function (a) {
      if (a.name === 'cart-id') return '';
      if (a.name !== 'wallet-configs') return a.name + '=' + a.value;
      var w = null;
      try { w = JSON.parse(a.value); } catch (e) { w = null; }
      if (!Array.isArray(w)) return a.name + '=' + a.value;
      return a.name + '=' + w.map(function (x) { return x ? [x.name, x.supports_subs, x.supports_def_opts].join(':') : ''; }).join(',');
    }).sort().join('\n');
  }
  function fpCartTok(t) { return String(t || '').split('?')[0]; }
  // Rule 6: the consent a box or a rendered box holds ('' for none).
  function fpExpressConsent(root) {
    var c = root && root.querySelector ? root.querySelector('#shopify-buyer-consent') : null;
    return c ? (c.getAttribute('data-consent-type') || 'consent') : '';
  }
  // Rule 6: does this cart hold a selling-plan line ('1' / '0')? Null when the cart is not known,
  // is another cart than `tok`, or is empty.
  function fpCartSp(tok) {
    var c = shopifyCart;
    if (!c || !Array.isArray(c.items) || !(c.item_count > 0) || fpCartTok(c.token) !== tok) return null;
    for (var i = 0; i < c.items.length; i++) if (c.items[i] && c.items[i].selling_plan_allocation) return '1';
    return '0';
  }
  function fpSectionId(el) {
    var sec = el && el.closest ? el.closest('.shopify-section') : null;
    return sec && sec.id && sec.id.indexOf('shopify-section-') === 0 ? sec.id.slice(16) : '';
  }
  function fpExpressBoxes() { return document.querySelectorAll('[data-fp-express]'); }
  // What to render: the drawer section at a unique path (see above). A page without the drawer
  // section (switched off in the editor) falls back to the boxes' own sections at this page's
  // path, which the rules above still guard.
  function fpExpressReq() {
    if (!document.querySelector('[data-fp-express]')) return null;
    var src = fpSectionId(document.getElementById('fpCartDrawer'));
    if (src) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return { src: src, sections: src, url: root + 'pages/fp-wallets-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36) };
    }
    var ids = [];
    Array.prototype.forEach.call(fpExpressBoxes(), function (b) {
      var id = fpSectionId(b);
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids.length ? { src: '', sections: ids.join(','), url: location.pathname } : null;
  }
  function fpExpressOwn(box, tok) {
    var el = box.querySelector(EXP_EL);
    return !!(el && tok && el.getAttribute('cart-id') === tok && !box.hasAttribute('data-fp-express-stale'));
  }
  // Does any box on the page still lack this cart's current element?
  function fpExpressNeed(tok) {
    return Array.prototype.some.call(fpExpressBoxes(), function (box) { return !fpExpressOwn(box, tok); });
  }
  // Rule 2: remove every element that names another cart, and show a box exactly when it holds
  // this cart's current element (rule 6: with the consent this cart needs).
  function fpExpressMark(tok) {
    var sp = fpCartSp(tok);
    Array.prototype.forEach.call(fpExpressBoxes(), function (box) {
      var el = box.querySelector(EXP_EL);
      if (el && tok && el.getAttribute('cart-id') !== tok) {
        box.innerHTML = '';
        box.removeAttribute('data-fp-express-stale');
        el = null;
      }
      if (el && sp != null) fpExpressSpCheck(box, sp);
      else box.removeAttribute(EXP_SP);
      box.hidden = !fpExpressOwn(box, tok);
    });
  }
  // Rule 6, for a box that holds this cart's element, when the cart's selling-plan state is known.
  // Decided from what the box HOLDS, never from a memo of what it was rendered for (fix round 3:
  // a box adopted while the cart was empty kept no memo, and a Subscribe add then showed that
  // empty-cart element, which has no consent, until the next render; for good when renders
  // failed). data-fp-express-sp only records the state the box was last checked against.
  function fpExpressSpCheck(box, sp) {
    var c = box.querySelector('#shopify-buyer-consent');
    if (sp === '0' && c && c.parentNode) c.parentNode.removeChild(c); // removing adopts nothing
    // The cart needs a consent this box does not hold: out of date (hidden) until a render of
    // this cart re-creates it (the chain asks: fpExpressApply reports it as not done).
    else if (sp === '1' && !c) box.setAttribute('data-fp-express-stale', '');
    box.setAttribute(EXP_SP, sp);
  }
  // The fresh box for one page box: from the drawer render when there is one (it serves every
  // box on the page), else from the box's own section, by position. DOMParser documents never
  // upgrade custom elements, so a parsed element that is not adopted never runs.
  function fpExpressFresh(sections, req, box, parsed) {
    var id = req.src || fpSectionId(box);
    if (!id || typeof sections[id] !== 'string') return null;
    if (!parsed[id]) parsed[id] = new DOMParser().parseFromString(sections[id], 'text/html').querySelectorAll('[data-fp-express]');
    if (req.src) return parsed[id][0] || null;
    var own = document.getElementById('shopify-section-' + id);
    var k = own ? Array.prototype.indexOf.call(own.querySelectorAll('[data-fp-express]'), box) : -1;
    return k >= 0 ? parsed[id][k] || null : null;
  }
  // What a rendered box holds: the content of its <template data-fp-express-src> (rule 2), or the
  // box itself for markup without one.
  function fpExpressHolder(nb) {
    var t = nb ? nb.querySelector('template[data-fp-express-src]') : null;
    return t && t.content ? t : null;
  }
  function fpExpressSrc(nb) {
    var t = fpExpressHolder(nb);
    return t ? t.content : nb;
  }
  // Rule 1 has said yes: the box takes the rendered markup. As HTML, parsed into THIS document,
  // never as cloned nodes: an element cloned out of template content (document.importNode) was
  // never upgraded by Shopify's definition and sat on its loading skeleton for good (measured in
  // WebKit, fix round 3), while the same markup set as innerHTML renders its wallets.
  function fpExpressPut(box, nb) {
    var t = fpExpressHolder(nb);
    box.innerHTML = t ? t.innerHTML : nb.innerHTML;
  }
  // Applies one reply. True when nothing more is needed: every box holds this cart's current
  // element, or a newer cart change has taken over (seq). False asks the chain to go again.
  function fpExpressApply(sections, token, req, seq) {
    // A newer cart change has taken over; it brings its own render. What the boxes show must still
    // match the cart the page now shows (rule 2, and rule 6's consent): the reply of an older
    // change can land, and set the cart, after a newer change was sent.
    if (seq !== expSeq) {
      if (shopifyCart && shopifyCart.token) fpExpressMark(fpCartTok(shopifyCart.token));
      return true;
    }
    var tok = fpCartTok(token);
    if (!tok || !req || !sections || typeof sections !== 'object') return false;
    var done = true, parsed = {};
    Array.prototype.forEach.call(fpExpressBoxes(), function (box) {
      var oldEl = box.querySelector(EXP_EL);
      var oldOwn = fpExpressOwn(box, tok);
      var nb = fpExpressFresh(sections, req, box, parsed);
      var src = fpExpressSrc(nb);
      var newEl = src && src.querySelector(EXP_EL);
      if (!newEl) { if (!oldOwn) done = false; return; } // nothing rendered for this box
      var sameEl = !!oldEl && fpExpressFacts(oldEl) === fpExpressFacts(newEl);
      var same = sameEl && fpExpressConsent(box) === fpExpressConsent(src); // rules 3 and 6
      if (newEl.getAttribute('cart-id') === tok) {
        if (!(oldOwn && same)) fpExpressPut(box, nb); // rule 3
        box.removeAttribute('data-fp-express-stale');
        return;
      }
      // Rule 1: another cart's element is never adopted.
      if (oldOwn && same) return; // ours says the same: it is current
      // Rule 6: ours differs only by a consent these lines no longer get (and this cart, as the
      // cart API last reported it, holds no selling-plan line). fpExpressMark below drops it,
      // which adopts nothing.
      if (oldOwn && sameEl && fpExpressConsent(box) && !fpExpressConsent(src) && fpCartSp(tok) === '0') return;
      if (oldEl && oldEl.getAttribute('cart-id') === tok) box.setAttribute('data-fp-express-stale', '');
      done = false;
    });
    // Rule 6 for every box, by what it now holds; a box it leaves without this cart's current
    // element (a render that lacks the consent this cart needs) is not done, so the chain asks again.
    fpExpressMark(tok);
    return done && !fpExpressNeed(tok);
  }
  // What realChange adds to its /cart/change.js body so the reply carries the fresh render
  // (Shopify's bundled section rendering). Null when the page has no express box.
  function fpExpressBundle() {
    var req = window.DOMParser ? fpExpressReq() : null;
    if (!req) return null;
    req.seq = ++expSeq;
    return req;
  }
  // Rules 4 and 5: a chain of attempts for cart state `seq`. Starting one supersedes the one
  // before it (expChain); a reply that lands for a superseded chain is still applied (it is
  // guarded by seq like any other), it just does not schedule another attempt.
  function fpExpressStart(seq, plan) {
    var id = ++expChain;
    expLive = true;
    fpExpressStep(seq, 0, plan, id);
  }
  function fpExpressStep(seq, n, plan, id) {
    if (id !== expChain) return;
    if (seq !== expSeq || n >= plan.length) { expLive = false; return; }
    window.clearTimeout(expTimer);
    expTimer = window.setTimeout(function () {
      if (id !== expChain) return;
      var stop = function () { if (id === expChain) expLive = false; };
      if (seq !== expSeq) { stop(); return; }
      // An empty cart shows no wallets (the drawer wrapper and the /cart grid are hidden), and
      // nothing is requested for a cart that has nothing in it.
      if (shopifyCart && !(shopifyCart.item_count > 0)) { stop(); return; }
      // A re-armed chain has nothing left to do once every box holds this cart's element.
      if (plan === EXP_REARM && shopifyCart && !fpExpressNeed(fpCartTok(shopifyCart.token))) { stop(); return; }
      var req = fpExpressReq();
      if (!req) { stop(); return; }
      if (expPostSeq !== seq) { expPostSeq = seq; expPosts = 0; }
      var post = plan[n][1] === 'P' && expPosts < EXP_POST_MAX;
      if (post) expPosts++;
      var next = function () { if (id === expChain) fpExpressStep(seq, n + 1, plan, id); };
      var call = post
        ? fetch('/cart/update.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sections: req.sections, sections_url: req.url })
          })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (c) { return c && c.sections ? { sections: c.sections, token: c.token } : null; })
        // No Accept header: with one, ?sections= on a collection URL answers the collection JSON.
        : fetch(req.url + '?sections=' + encodeURIComponent(req.sections), { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (j) { return j && shopifyCart && shopifyCart.token ? { sections: j, token: shopifyCart.token } : null; });
      call.then(function (res) {
        var ok = !!res && fpExpressApply(res.sections, res.token, req, seq);
        if (id !== expChain) return;
        if (ok) stop(); else next();
      }, next);
    }, plan[n][0]);
  }
  // A new cart state (an add, a plan change, a re-read) or a page element that is not this
  // cart's: re-render the boxes. Rule 2 at once, not when the render lands: the first add
  // replaces a visitor's throwaway cart with a real one, and the element on the page still
  // names the throwaway.
  function fpExpressRefresh() {
    if (!document.querySelector('[data-fp-express]') || !window.fetch || !window.DOMParser) return;
    if (shopifyCart && shopifyCart.token) fpExpressMark(fpCartTok(shopifyCart.token));
    fpExpressStart(++expSeq, EXP_MAIN);
  }
  // The same check at load. The boxes are server-rendered `hidden` (cart-drawer.liquid,
  // main-cart.liquid), because the page HTML comes from the same cache, and the inline script
  // has already kept out an element that named another cart; this is what shows them. Each
  // element's cart-id is compared with the token /cart.js reports: a match shows the box and
  // costs nothing; a box without this cart's element (removed, or never there) starts the chain.
  // An empty cart leaves the box hidden (its wrapper or grid is hidden too); the first add
  // re-renders it through fpExpressRefresh.
  function fpExpressCheckCart() {
    if (!shopifyCart || !shopifyCart.token || !(shopifyCart.item_count > 0)) return;
    var tok = fpCartTok(shopifyCart.token);
    fpExpressMark(tok);
    if (fpExpressNeed(tok)) fpExpressRefresh();
  }
  // Rule 5. Called from openDrawer() and on visibilitychange (below).
  function fpExpressRearm() {
    if (expLive || !window.fetch || !window.DOMParser) return;
    if (!cartLoaded || !shopifyCart || !shopifyCart.token || !(shopifyCart.item_count > 0)) return;
    if (chgPending() || !fpExpressNeed(fpCartTok(shopifyCart.token))) return;
    var now = Date.now();
    if (now - expRearmAt < EXP_REARM_GAP) return;
    expRearmAt = now;
    fpExpressStart(expSeq, EXP_REARM);
  }
  document.addEventListener('visibilitychange', function () { if (!document.hidden) fpExpressRearm(); });

  // FP51 Klaviyo cart nudge. The welcome popup (Klaviyo form, 8 s delay) is targeted "number of
  // items in cart is less than 1" (owner, 2026-10-21: no popup while the cart holds something), and
  // Klaviyo judges that from its OWN read of /cart.js: one read when its triggers start, then a
  // re-read only after a click on a button matched by `form[action*='/cart'] button` (the
  // cart_content listener of its onsite bundle; it also attaches to such buttons added later, and
  // after the click re-fetches /cart.js up to 5 times until the cart differs, then re-evaluates the
  // form). Every add and change on this theme is a fetch() from a button in no such form, so a
  // shopper who added mid-visit still got the popup over the open drawer ~9 s after landing
  // (measured on live FP50). So the page carries one hidden `<form action="/cart">` holding a
  // type="button" (it cannot submit; not /cart/add, which Shopify's own add-to-cart listeners key
  // on), and fpCartHeard() clicks it when a cart this page adopts differs in CONTENT from the last
  // one it read (the token is left out: a cookie-less read can carry a fresh one each time). Never
  // for the page-load read (`base`: Klaviyo makes its own), never twice for one cart.
  // The form is server HTML near the top of <body> (layout/theme.liquid), and fpCartNudgeMount()
  // only adopts it. It must exist BEFORE Klaviyo's triggers start: Klaviyo attaches at once to the
  // buttons present then, but to a button added later only once the document has gone 100 ms
  // without a childList mutation, and on a PDP in Chromium Shopify's checkout prefetch (~190 links
  // appended one at a time) kept that from happening until 6.8-9.7 s. A form made here at boot
  // (fix round 1) was therefore deaf whenever this file arrived after Klaviyo started, and the
  // popup opened over the drawer (3 of 3, 2026-10-21). Making it here stays only as the fallback
  // for a layout without it.
  // The click is a non-bubbling event on the button, so no bubble-phase handler sees it (this
  // file's delegation, the mega menu's outside-click close). Capture-phase listeners on window and
  // document still receive it: Shopify's Web Pixels Manager and perf-kit, the OpenAI pixel (which
  // reads the button's form) and the Meta pixel; measured 2026-10-21 in Chromium and WebKit, none
  // of them sends anything for it. Do not add a capture-phase click handler on window/document
  // that would act on it. Klaviyo detail only: should Klaviyo change that selector, the popup can
  // open after a mid-visit add again, as before; the cart itself never depends on this.
  var cartHeard = null; // fpCartHeardSig of the last cart this page read; null before the first
  var cartNudgeBtn = null;
  function fpCartNudgeMount() {
    if (cartNudgeBtn || !document.body) return;
    var f = document.querySelector('form[data-fp-cart-nudge]');
    var b = f && f.querySelector('button');
    if (!b) {
      f = document.createElement('form');
      f.setAttribute('action', '/cart');
      f.setAttribute('data-fp-cart-nudge', '');
      f.setAttribute('aria-hidden', 'true');
      f.hidden = true;
      f.style.display = 'none';
      b = document.createElement('button');
      b.type = 'button';
      b.tabIndex = -1;
      f.appendChild(b);
      document.body.appendChild(f);
    }
    // A type="button" never submits; this only makes sure nothing here can ever be posted.
    f.addEventListener('submit', function (e) { e.preventDefault(); });
    cartNudgeBtn = b;
  }
  function fpCartHeardSig(c) {
    if (!c || !Array.isArray(c.items)) return '';
    return JSON.stringify([c.item_count, c.total_price, c.items.map(function (i) {
      var sp = i && i.selling_plan_allocation && i.selling_plan_allocation.selling_plan;
      return i ? [i.key, i.quantity, sp ? sp.id : 0] : null;
    })]);
  }
  function fpCartHeard(c, base) {
    var sig = fpCartHeardSig(c);
    if (!sig) return;
    var prev = cartHeard;
    cartHeard = sig;
    // prev === null without `base`: the page-load read failed, so what Klaviyo saw is unknown and
    // one click is cheap (Klaviyo only re-reads /cart.js).
    if (base || sig === prev || !cartNudgeBtn) return;
    try { cartNudgeBtn.dispatchEvent(new MouseEvent('click')); } catch (e) { /* old engine: the old behaviour */ }
  }

  var cartRetry = 0;
  // base: the page-load read (boot, the /cart module), which only records what the cart was.
  function fetchRealCart(then, base) {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      // An error body ({status,message,description}) parses fine as JSON but
      // carries no items array — assigning it makes the next render throw on
      // shopifyCart.items and freezes every control on the page.
      if (!c || !Array.isArray(c.items)) throw new Error('cart shape');
      // A re-read after a change (add, recovery) vs the first read of this page load: the
      // express buttons re-render after a change, and are only checked against the token
      // on the first read (fpExpressRefresh / fpExpressCheckCart above).
      var reread = cartLoaded;
      shopifyCart = c;
      cartLoaded = true;
      fpCartHeard(c, base);
      // The other half of the empty-cart delay. sections/main-cart.liquid marks its
      // empty panel .fpCartEmptyLate only when the SERVER said the cart had items, and
      // holds it back .8s with `html:not(.fp-cart-known) .fpCartEmptyLate{...}` so an
      // unresolved /cart.js can never flash "your cart is empty" at a shopper who has
      // some. Once the real cart HAS landed that guess is no longer needed, and without
      // this stamp the selector matched forever — leaving a shopper who removed their
      // last line staring at a blank panel for .8s. Placed after cartLoaded and before
      // render() on purpose: the stamp is in place before anything repaints, while a
      // demo (REAL-false) page never reaches here and keeps the delay as written.
      document.documentElement.classList.add('fp-cart-known');
      cartRetry = 0;
      render();
      if (reread) fpExpressRefresh(); else fpExpressCheckCart();
      if (then) then();
    }).catch(function () {
      // Two retries before giving up — 1.2 s for a dropped packet, then 8 s for a
      // rate limit or a brief outage. Bounded on purpose: an open-ended re-arm turns
      // a dead endpoint into a permanent request loop and a repeating toast.
      if (cartRetry < 2) {
        cartRetry++;
        window.setTimeout(function () { fetchRealCart(then, base); }, cartRetry === 1 ? 1200 : 8000);
        return;
      }
      // There are THREE cart states — known-full, known-empty and UNKNOWN — and
      // setting cartLoaded here collapsed the third into the second: shopifyCart is
      // still null, so `noItems` and `hasItems` both went true and a shopper with a
      // full cart was told it was empty, one click before checkout. Leaving
      // cartLoaded false keeps both false, so the surface renders its heading and no
      // body — silent, never a lie — and the shopper is told out loud instead.
      fpToast('We could not load your cart just now — your items are safe. Refresh to try again.');
      render();
    });
  }
  // FP50 (F3). The drawer is on every page since FP47, and nothing re-read the cart while a page
  // sat in a background tab: a change made in another tab or on another surface (a /cart page
  // the shopper went Back from) left this page's lines, badge, totals and CHECKOUT amount stale
  // until its next own change (measured on live FP49 2026-10-20). So the cart is re-read when the
  // page becomes visible again (visibilitychange, and pageshow from the back/forward cache), on
  // the pages that show the Shopify cart (boot's own rule: REAL, or no Best Sellers blob).
  // Bounded: one read in flight; at most one per CART_REVISIT_GAP, a return inside the gap
  // queuing ONE trailing read at its end (so a change made in between is still seen); none while
  // a quantity change or an add is unsettled (its own reply carries the whole cart). A reply is
  // dropped when anything else set the cart while it was out (a change, an add, boot's read),
  // and costs nothing when the cart is unchanged (fpCartSig). Otherwise the drawer, the badge and
  // the /cart grid render from it, and the express boxes follow the usual rules
  // (fpExpressRefresh: own element only, rule 2 at once when the cart token changed).
  // Not on every drawer open: the drawer can only open from a visible page, and a page that has
  // stayed visible has seen every change made through it.
  var CART_REVISIT_GAP = 2000;
  // revisitAway: the page has been hidden (or put in the back/forward cache) since the last re-read
  // started. A return without an absence in between (pageshow and visibilitychange both firing on
  // one restore) asks for nothing.
  var revisitBusy = false, revisitAt = 0, revisitTimer = null, revisitAway = true;
  function fpCartSig(c) {
    if (!c || !Array.isArray(c.items)) return '';
    return JSON.stringify([fpCartTok(c.token), c.item_count, c.total_price, c.total_discount, c.original_total_price,
      c.items.map(function (i) {
        var sp = i && i.selling_plan_allocation && i.selling_plan_allocation.selling_plan;
        return i ? [i.key, i.quantity, i.final_line_price, sp ? sp.id : 0] : null;
      })]);
  }
  function fpCartBusy() {
    if (chgPending()) return true;
    for (var k in realAddBusy) { if (realAddBusy[k]) return true; }
    return false;
  }
  function fpCartRevisit() {
    if (document.hidden || !window.fetch || revisitTimer || !revisitAway) return;
    if (!(REAL || BEST.length === 0)) return; // a demo-cart page keeps its own cart
    var wait = Math.max(revisitAt + CART_REVISIT_GAP - Date.now(), revisitBusy ? CART_REVISIT_GAP : 0);
    if (wait > 0) {
      revisitTimer = window.setTimeout(function () { revisitTimer = null; fpCartRevisit(); }, wait);
      return;
    }
    if (fpCartBusy()) return;
    revisitBusy = true;
    revisitAway = false;
    revisitAt = Date.now();
    var before = shopifyCart;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        revisitBusy = false;
        if (!c || !Array.isArray(c.items)) return;
        if (shopifyCart !== before || fpCartBusy()) return; // something newer has set the cart, or will
        if (PAGE.cart) PAGE.cart(c);
        // boot's rule: a page that is not REAL adopts the live cart only when it holds something
        if (!REAL && !(c.items.length > 0)) return;
        if (cartLoaded && fpCartSig(c) === fpCartSig(shopifyCart)) return;
        var first = !cartLoaded;
        shopifyCart = c;
        cartLoaded = true;
        REAL = true;
        fpCartHeard(c); // changed in another tab or surface: Klaviyo here has not seen it
        document.documentElement.classList.add('fp-cart-known');
        render();
        if (first) fpExpressCheckCart(); else fpExpressRefresh();
      }, function () { revisitBusy = false; });
  }
  document.addEventListener('visibilitychange', function () { if (document.hidden) revisitAway = true; else fpCartRevisit(); });
  window.addEventListener('pagehide', function () { revisitAway = true; });
  window.addEventListener('pageshow', function (e) { if (e && e.persisted) fpCartRevisit(); });
  // GA4 funnel. The store was emitting page_view only, so with ~6,200 sessions and ~5
  // orders there was no way to see WHERE people leave - which is the one question the
  // owner needs answered. Silent no-op when gtag is absent: the tag is injected only
  // after the geo gate allows and Consent Mode is satisfied, and this must never throw
  // on a blocked or non-consenting visitor, nor block a purchase if analytics is down.
  function fpTrack(name, params) {
    try {
      if (typeof window.gtag !== 'function') return;
      // FAIL CLOSED. window.gtag can exist without this theme having configured anything: the
      // Google & YouTube channel pixel defines it in this same window. Without an ID to stamp,
      // the event below would carry no send_to and gtag would deliver it to EVERY destination on
      // the page - the channel's GA4 property, its Ads tag and its GTM container. Staying silent
      // is the correct behaviour when the theme has no property of its own.
      if (!window.FP_GA4_ID) return;
      // An event with no send_to is delivered to EVERY destination configured in this
      // window, and the Google & YouTube channel app shares it with its own GA4
      // property, an Ads tag and a GTM container — so the theme's funnel was also
      // being written into properties that never asked for it. Stamp our own
      // measurement id, published by snippets/google-analytics.liquid at parse time.
      // Copy, never mutate: fpGoCheckout reads `params` again after this returns.
      var p = {};
      for (var k in params) { if (Object.prototype.hasOwnProperty.call(params, k)) p[k] = params[k]; }
      if (window.FP_GA4_ID) p.send_to = window.FP_GA4_ID;
      window.gtag('event', name, p);
    } catch (e) { /* analytics must never break the store */ }
  }
  function fpItem(o) {
    var it = { item_name: o.name || '', quantity: o.qty || 1 };
    if (o.id) it.item_id = String(o.id);
    if (o.variant) it.item_variant = o.variant;
    if (typeof o.price === 'number') it.price = Math.round(o.price * 100) / 100;
    return it;
  }
  // A failure the shopper can read. Used where there is no inline slot next to the
  // control (the home-page cards). Polite live region, auto-dismisses, never blocks.
  // Created ONCE at boot, empty and hidden — same rule as fpLiveRegion() above, and
  // for the same reason: a polite region that is inserted in the same tick as its
  // first message is routinely swallowed, so the FIRST failure the shopper hit was
  // the one nobody heard. Every later one worked, because by then the node existed.
  // Its position is not fixed for good: two classes on <html> move it with !important rules,
  // fp-cart-bar-on above the /cart sticky bar (main-cart.liquid) and fp-cd-open above the open
  // drawer's footer (cart-drawer.liquid, fpDrawerGeo).
  var fpToastEl = null;
  function fpToastRegion() {
    if (fpToastEl && fpToastEl.parentNode) return fpToastEl;
    fpToastEl = document.getElementById('fpToast');
    if (!fpToastEl) {
      fpToastEl = document.createElement('div');
      fpToastEl.id = 'fpToast';
      fpToastEl.setAttribute('role', 'status');
      fpToastEl.setAttribute('aria-live', 'polite');
      fpToastEl.setAttribute('aria-atomic', 'true');
      fpToastEl.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:120;'
        + 'max-width:min(420px,calc(100vw - 32px));background:#3D1409;color:#FFF6E9;'
        + 'padding:12px 18px;border-radius:14px;font-family:var(--fp-font-body),sans-serif;'
        + 'font-size:13.5px;font-weight:700;line-height:1.35;box-shadow:0 12px 30px rgba(61,20,9,.32);'
        + 'text-align:center;display:none';
      document.body.appendChild(fpToastEl);
    }
    return fpToastEl;
  }
  function fpToast(text) {
    if (!text) return;
    var el = fpToastRegion();
    // Clear, then write on a timeout: the same two-step fpAnnounce uses, so a second
    // identical failure is audible and the write is a change to a region that was
    // already registered. The pill stays hidden until the text lands, or it would
    // flash as an empty box for a frame.
    el.textContent = '';
    window.clearTimeout(el._w);
    window.clearTimeout(el._t);
    el._w = window.setTimeout(function () { el.textContent = text; el.style.display = ''; }, 60);
    el._t = window.setTimeout(function () { el.style.display = 'none'; el.textContent = ''; }, 6060);
  }
  // One checkout hand-off for both surfaces (drawer and /cart), so begin_checkout
  // is emitted exactly once per click and neither surface can drift from the other.
  //
  // The old path slept a fixed 450 ms with no pressed, disabled or loading state,
  // so the highest-intent click in the store felt dead. The delay was never about
  // the bark: gtag.js is injected late (after the geo gate + Consent Mode), so an
  // immediate location.href can drop the hit. Bound the wait on the tag's own
  // callback instead, with a short failsafe, and show the shopper it is working.
  var checkoutGoing = false;
  function fpGoCheckout(el, totals) {
    if (checkoutGoing) return;
    checkoutGoing = true;
    var release = fpLock(el);
    var prevLabel = null;
    // FP47 (CART-13): the CHECKOUT buttons carry a lock icon and a live amount binding, so
    // overwriting textContent would delete both for good (the restore below puts back plain
    // text only). Those buttons hold their label in [data-fp-cko-idle] and the busy text in
    // [data-fp-cko-busy]; swap the two instead. Any other button keeps the old behaviour.
    var ckoIdle = el && el.querySelector ? el.querySelector('[data-fp-cko-idle]') : null;
    var ckoBusy = el && el.querySelector ? el.querySelector('[data-fp-cko-busy]') : null;
    if (ckoIdle && ckoBusy) {
      ckoIdle.hidden = true;
      ckoBusy.hidden = false;
    } else if (el && el.tagName === 'BUTTON' && !el.hasAttribute('data-fp-t')) {
      prevLabel = el.textContent;
      el.textContent = 'ONE MOMENT…';
    }
    fpAnnounce('Going to checkout.');
    var t = totals || cartTotals();
    var went = false;
    var goNow = function () {
      if (went) return;
      went = true;
      window.location.href = '/checkout';
    };
    // A quantity the shopper asked for can still be sitting in chgWant with no request
    // ever issued: realChange coalesces presses and only re-POSTs the queued value from
    // the PREVIOUS response's handler. Navigating on top of that checks them out at a
    // number they did not stop at — and bump() has already announced the number they DID
    // stop at into the live region, so a screen-reader shopper was told a quantity the
    // cart would never hold. Hold the hand-off until the queue drains. Bounded at 3s, and
    // strictly inside the 4s release below, so a stuck request can never trap the click.
    // Placed after the lock and the ONE MOMENT… label, so the wait is visible.
    var goBy = Date.now() + 3000;
    var go = function () {
      if (went) return;
      if (chgPending() && Date.now() < goBy) { window.setTimeout(go, 100); return; }
      goNow();
    };
    var params = {
      currency: FP_CCY, value: t.payable != null ? t.payable : t.subtotal,
      // id is the numeric variant id (cartEntries._vid). Without it GA4 has no item
      // key on the last step, so the checkout rows cannot be joined to the view_item
      // and add_to_cart rows before them — which is exactly the per-product
      // cart-to-checkout drop-off this instrumentation exists to expose.
      items: cartEntries().map(function (e) { return fpItem({ id: e._vid, name: e.name, qty: e.qty, price: e.line / (e.qty || 1) }); })
    };
    if (typeof window.gtag === 'function') {
      params.event_callback = go;
      fpTrack('begin_checkout', params);
      window.setTimeout(go, 120);
    } else {
      fpTrack('begin_checkout', params);
      go();
    }
    // A navigation that never happens (blocked, offline) must not leave a dead
    // button behind.
    window.setTimeout(function () {
      checkoutGoing = false;
      release();
      if (ckoIdle && ckoBusy) { ckoIdle.hidden = false; ckoBusy.hidden = true; }
      if (prevLabel != null && el) el.textContent = prevLabel;
    }, 4000);
  }

  // The home-page cards publish every selling plan, so the stepper can only offer a
  // cadence that exists and the add can send the one the shopper chose. Week count is
  // parsed off the plan name with the same rule the product page uses (fpPlans), so
  // the two surfaces cannot drift apart.
  function bestPlans(b) {
    return (((b && b.sellingPlans) || []).map(function (p) {
      var m = /(\d+)/.exec(p.label || '');
      return { id: p.id, label: p.label || '', weeks: m ? parseInt(m[1], 10) : 0 };
    })).filter(function (p) { return p.weeks > 0; });
  }
  function bestWeekRange(i) {
    var ps = bestPlans(BEST[i]);
    if (!ps.length) return null;
    var w = ps.map(function (p) { return p.weeks; });
    return { min: Math.min.apply(null, w), max: Math.max.apply(null, w) };
  }
  function bestPlanForWeeks(b, weeks) {
    var ps = bestPlans(b);
    for (var i = 0; i < ps.length; i++) if (ps[i].weeks === weeks) return ps[i];
    return null;
  }
  var realAddBusy = {};
  function realAdd(i, sub, freq, el) {
    var b = BEST[i];
    if (!b) return;
    // Two guards, because they cover different holes: the element lock stops a
    // double-tap on THIS button, the per-card flag stops the same card being added
    // from a second control (card + sticky bar) while the first request is open.
    if (realAddBusy[i] || fpBusy(el)) return;
    realAddBusy[i] = true;
    var release = fpLock(el);
    var done = function () { realAddBusy[i] = false; release(); };
    var line = { id: b.variantId, quantity: 1 };
    // _freq belongs to subscriptions only. Shopify merges cart lines on variant
    // AND properties, so carrying it on a one-time add made the same chew land
    // as two identical-looking rows when it was added from two surfaces.
    if (sub && b.sellingPlanId) {
      // The plan the shopper SELECTED, never plans[0]. Shopify returns plans
      // shortest-first, so sending plans[0] billed every "deliver every 4 weeks"
      // choice on the WEEKLY plan. The product page was fixed for this; this
      // surface was not.
      var chosen = bestPlanForWeeks(b, freq);
      line.selling_plan = chosen ? chosen.id : b.sellingPlanId;
      line.properties = { _freq: String(chosen ? chosen.weeks : freq) };
    }
    // Same rule as the product page: fetch() does not reject on 4xx, so without this
    // a 422 opened the cart drawer as though the add had succeeded.
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [line] }) })
      .then(function (r) {
        if (r.ok) return r.json();
        return r.json().then(
          function (e) { throw new Error((e && (e.description || e.message)) || 'Could not add this to your cart.'); },
          function () { throw new Error('Could not add this to your cart.'); }
        );
      })
      .then(function () {
        // The value that was actually BILLED. A subscribed line is charged at
        // (100 - subscribeSavePercent)% of the one-time price, so reporting the
        // one-time price over-valued every subscription add from this row by 25%
        // against the same product added from its product page, which uses unit().
        // Gated on the same condition the cart line used above, not merely on the
        // `sub` flag: a card with no selling plan falls back to a one-time line, and
        // must not report a discount the cart did not apply.
        var addValue = (sub && b.sellingPlanId) ? subOf(bestPrice(i)) : bestPrice(i);
        fpTrack('add_to_cart', {
          currency: FP_CCY, value: addValue,
          items: [fpItem({ id: b.variantId, name: b.name, price: addValue })]
        });
        fetchRealCart(function () {
          fpAnnounce((b.name || 'Item') + ' added to your cart. Cart subtotal ' + money(cartTotals().subtotal) + '.');
          if (S.openCartOnAdd) openDrawer();
        });
      })
      .catch(function (err) { fpToast((err && err.message) || 'Could not add this to your cart. Please try again.'); })
      // finally-equivalent: a failed add must never leave the button disabled.
      .then(done, done);
  }
  // One change in flight per line, plus the quantity we last asked for. Two taps
  // on "+" both read the same network snapshot and both posted quantity 2, so
  // the second tap vanished and the control read as broken.
  var chgBusy = {}, chgWant = {}, chgQty = {};
  // Is any quantity the shopper asked for still unsettled? chgBusy holds `false` after a
  // request lands rather than being deleted, so test the VALUES, not the key count.
  function chgPending() {
    var k;
    for (k in chgBusy) { if (chgBusy[k]) return true; }
    for (k in chgWant) { if (chgWant[k] != null) return true; }
    return false;
  }
  function realChange(lineKey, qty) {
    chgQty[lineKey] = qty;
    if (chgBusy[lineKey]) { chgWant[lineKey] = qty; return; }
    chgBusy[lineKey] = true;
    // FP47: the change carries the express section render with it (bundled section
    // rendering, at a unique path: fpExpressReq), so the wallets follow the cart without a
    // second request. Should that render still name another cart, rule 1 refuses it, and when
    // ours is then out of date the reply continues as a chain (fpExpressStart).
    var chgBody = { id: lineKey, quantity: qty };
    var exB = fpExpressBundle();
    if (exB) { chgBody.sections = exB.sections; chgBody.sections_url = exB.url; }
    fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(chgBody) })
      // fetch() does not reject on 4xx. Every quantity change and every Remove
      // used to swallow its own failure: the tap simply vanished with the row
      // still showing the old number and nothing said.
      .then(function (r) {
        return r.json().then(
          function (c) { return { ok: r.ok, status: r.status, c: c }; },
          function () { return { ok: r.ok, status: r.status, c: null }; }
        );
      })
      .then(function (res) {
        // A stale line key does NOT answer 404. Measured against the live store
        // 2026-08-17: /cart/change.js answers a retired key with HTTP 400 and
        // {status:"bad_request", message:"no valid id or line parameter"} and no items
        // array, so the 404 exemption alone never fired and the one case that was meant
        // to be silent was the one that toasted — printing that raw API string at the
        // shopper. Keys go stale in normal use (they rotate whenever a line's selling
        // plan or properties change), and that case self-heals: the line is already gone
        // and re-reading shows the truth. Every OTHER failure (422 line limit, 429, 5xx)
        // is the shopper's tap being refused, and has to say so.
        if (res.c && Array.isArray(res.c.items)) {
          var exS = res.c.sections;
          delete res.c.sections;
          shopifyCart = res.c; render();
          fpCartHeard(res.c);
          // Rule 4: a bundled render that is not this cart's continues as a chain (GET next).
          if (exB && exS) { if (!fpExpressApply(exS, res.c.token, exB, exB.seq)) fpExpressStart(exB.seq, EXP_MAIN); }
          else fpExpressRefresh();
          return;
        }
        fetchRealCart();
        var stale = res.status === 404 || (res.c && res.c.status === 'bad_request');
        if (!res.ok && !stale) {
          fpToast((res.c && (res.c.description || res.c.message)) || 'Could not update your cart. Please try again.');
        }
      })
      .catch(function () { fpToast('Could not update your cart. Please try again.'); fetchRealCart(); })
      .then(function () {
        chgBusy[lineKey] = false;
        if (chgWant[lineKey] != null) { var q = chgWant[lineKey]; delete chgWant[lineKey]; realChange(lineKey, q); }
        else delete chgQty[lineKey];
      });
  }
  function pushKey(key) {
    st.cart[key] = (st.cart[key] || 0) + 1;
    saveDemoCart();
    var p = PRODUCTS[key.split(':')[0]] || {};
    fpAnnounce((p.name || 'Item') + ' added to your cart. Cart subtotal ' + money(cartTotals().subtotal) + '.');
    if (S.openCartOnAdd) openDrawer();
    else render();
  }
  function bump(item, d) {
    if (REAL) {
      var entry = cartEntries().filter(function (e) { return e._key === item._key; })[0];
      // Step from the quantity we last ASKED for, not from the last snapshot the
      // network returned, so rapid taps accumulate instead of overwriting.
      var base = chgQty[item._key] != null ? chgQty[item._key] : (entry ? entry.qty : 0);
      var next = Math.max(0, base + d);
      // The row is about to be rebuilt underneath the shopper. Say what changed —
      // a refused change toasts on its own (also role="status"), so the two
      // cannot both go unheard.
      fpAnnounce(next <= 0
        ? ((item.name || 'Item') + ' removed from your cart.')
        : ((item.name || 'Item') + ', quantity ' + next + '.'));
      realChange(item._key, next);
      return;
    }
    var q = (st.cart[item._key] || 0) + d;
    if (q <= 0) delete st.cart[item._key];
    else st.cart[item._key] = q;
    saveDemoCart();
    render();
  }

  // ---------- decorative ----------
  var pawDone = {};
  function celebrate(target, id) {
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (pawDone[id]) return;
      pawDone[id] = true;
      if (!target || !target.getBoundingClientRect) return;
      var r = target.getBoundingClientRect();
      var cx = r.left + r.width / 2;
      var cy = r.top + r.height / 2;
      var colors = ['#F58220', '#FFB92E', '#FFD34D'];
      for (var i = 0; i < 9; i++) {
        var s = document.createElement('span');
        var size = Math.round(12 + Math.random() * 8);
        s.innerHTML = '<svg width="' + size + '" height="' + Math.round(size * 0.85) + '" viewBox="0 0 40 34" fill="' + colors[i % 3] +
          '"><ellipse cx="8" cy="13" rx="4.6" ry="5.6"/><ellipse cx="17.5" cy="7" rx="5" ry="6"/><ellipse cx="28" cy="8.5" rx="4.8" ry="5.8"/><ellipse cx="35.5" cy="16" rx="4" ry="4.8"/><path d="M21 15c6 0 12 5 12 10.5 0 4-3 6.5-6.5 6.5-2.6 0-4.4-1.4-6.5-1.4s-3.6 1.4-6 1.4c-3.4 0-6.5-2.4-6.5-6.5C7.5 20 14 15 21 15Z"/></svg>';
        s.style.cssText = 'position:fixed;left:' + cx + 'px;top:' + cy + 'px;z-index:150;pointer-events:none;--dx:' +
          ((Math.random() - 0.5) * 150).toFixed(0) + 'px;--dy:' + (-(46 + Math.random() * 84)).toFixed(0) +
          'px;--rot:' + ((Math.random() - 0.5) * 70).toFixed(0) + 'deg;animation:fpPawPop .9s ease-out forwards;';
        document.body.appendChild(s);
        window.setTimeout((function (n) { return function () { n.remove(); }; })(s), 1000);
      }
    } catch (e) { /* decorative only */ }
  }

  var stripDone = false;
  var stripTimer;
  function showSubStrip() {
    if (stripDone) return;
    stripDone = true;
    patch({ subCongrats: true });
    window.clearTimeout(stripTimer);
    stripTimer = window.setTimeout(function () { patch({ subCongrats: false }); }, 6000);
  }

  var barkAt = 0;
  var barkAudio = null;
  function playBark(minGap) {
    // default 8s anti-spam (join widget); cart/checkout pass a short gap so a
    // natural cart->checkout tap sequence barks both times
    var gap = typeof minGap === 'number' ? minGap : 8000;
    var now = performance.now();
    if (barkAt && now - barkAt < gap) return;
    barkAt = now;
    try {
      if (!barkAudio) {
        barkAudio = new Audio(S.barkUrl || '');
        // preload='none'. new Audio(url) runs the resource-selection algorithm on
        // its own, so 'auto' pulled the whole 33 KB clip on every page — on a
        // policy page it was 46% of everything fetched, for a sound that page can
        // never play. The cost of 'none' is latency on the very first bark.
        barkAudio.preload = 'none';
        barkAudio.volume = 0.85;
      }
      barkAudio.currentTime = 0;
      barkAudio.play().catch(function () { /* user gesture required */ });
    } catch (e) { /* audio unavailable */ }
  }

  // ---------- pack sound (design policy) ----------
  var packOnScreen = true;
  function packVid(clipIdx) { return document.getElementById('fpPackVid' + clipIdx); }
  function muteAllPack() {
    [0, 1, 2, 3].forEach(function (ci) {
      var el = packVid(ci);
      if (el) el.muted = true;
    });
    if (st.pkSnd) patch({ pkSnd: false });
  }
  function activeClipIndex() {
    var ci = PACK.clipStages.indexOf(st.packStage);
    return ci === -1 ? null : ci;
  }
  function sndToggle() {
    var on = !st.pkSnd;
    var act = activeClipIndex();
    // A sharper file loading for the clip stops now: it could not take over while the clip has
    // sound (iOS pauses a video unmuted outside a tap). It is looked for again once sound is off.
    if (on && packHq) { var hv = packHq.from; packHqDrop(); packHqLater(hv, PACK_HQ_CHECK); }
    [0, 1, 2, 3].forEach(function (ci) {
      var el = packVid(ci);
      if (el) el.muted = !(on && ci === act);
    });
    if (act != null) {
      var av = packVid(act);
      if (av && on && av.paused) av.play().catch(function () { /* noop */ });
    }
    patch({ pkSnd: on });
  }

  // ---------- pack quality upgrade (owner, 2026-10-09) ----------
  // Start small, then sharpen. Every stage clip starts on its 480p file at every width
  // (sections/pack-in-action.liquid), so the first frame comes as soon as the smallest file
  // allows. Once the clip on stage has played for PACK_HQ_DELAY and the line is clearly fast
  // enough, the file this width deserves (data-fp-hq1080 from 1440px, else data-fp-hq720)
  // plays muted and unseen in a second element; it is played rather than only preloaded
  // because iOS Safari does not buffer an unplayed video. When it can play through without
  // stalling (canplaythrough), it is paused on a frame PACK_HQ_LEAD ahead of the clip, started
  // as the clip reaches that frame, faded in once the two agree to PACK_HQ_DRIFT and handed the
  // clip's identity (id, data-fp-stage, bindings); the 480p element is removed and stops
  // loading. Not on Data Saver, a 2g/3g effective connection or reduced motion; never while
  // the clip has sound (iOS pauses a video unmuted outside a tap, which froze picture and
  // sound in the review: the swap waits for the sound to be off); dropped when the clip leaves
  // the stage or the screen, is paused or the page goes to the background, and tried again
  // later; given up for the page view
  // (data-fp-hq-done="no") after PACK_HQ_WAIT or an error, so a slow line does not fetch the
  // same megabytes at every replay.
  // "Clearly fast enough": the media seconds the 480p file buffers per wall second AFTER it was
  // asked to play (what preload="metadata" and the thumbnail fetched before play is no line
  // speed: counted, it passed a line exactly at the clip's bitrate in Chrome), against the
  // bitrate ratio of the two files, which Shopify writes into their names (SD-480p-1.5Mbps,
  // HD-720p-4.5Mbps: 3x), plus a quarter. Without it a 600 kbps line stalled the 480p clip
  // while a 720p file it never swapped to loaded for 20 s. A start file that was already
  // complete when play began (the HTTP cache) says nothing about the line but competes with
  // nothing, so the attempt goes ahead. From 1440px a line too slow for 1080p tries 720p.
  var PACK_HQ_DELAY = 1000, PACK_HQ_CHECK = 2000, PACK_HQ_WAIT = 20000;
  var PACK_HQ_LEAD = 0.5, PACK_HQ_LEAD_MAX = 4, PACK_HQ_SEEK = 4000, PACK_HQ_DRIFT = 0.12, PACK_HQ_TRIES = 3, PACK_HQ_SYNC = 12000, PACK_HQ_AHEAD = 10, PACK_HQ_BANKED = 6, PACK_HQ_STARVE = 2;
  var packHq = null;   // { from: <video> on stage, el: <video> with the sharper file, timer, tries }
  function packHqNow() { return window.performance && performance.now ? performance.now() : Date.now(); }
  function packHqMbps(url) { var m = /-(\d+(?:\.\d+)?)Mbps-/.exec(url || ''); return m ? parseFloat(m[1]) : 0; }
  function packHqEnd(v) { var b = v.buffered; return b && b.length ? b.end(b.length - 1) : 0; }
  // The end of the loaded range that holds t (-1 if none). A sharper file sought to the clip's
  // second has two: the header fetch at 0 and the range from that second on, with a gap between.
  function packHqEndAt(el, t) {
    var b = el.buffered;
    for (var i = 0; b && i < b.length; i++) if (b.start(i) <= t + 0.05 && b.end(i) >= t) return b.end(i);
    return -1;
  }
  // Whether the start of the file is loaded (two seconds of it), so the loop does not wait on
  // the network.
  function packHqHasStart(el) {
    var d = el.duration || 0;
    return packHqEndAt(el, 0) >= Math.min(2, d ? d - 0.1 : 2);
  }
  // Two measures, the better one kept: since play, and over windows; both over at least a second
  // (a shorter one is one burst, not the line: 0.7 s around one read a 2x line as 4x, a final
  // 0.3 s one 5x), the since-play one also at the sample that completes the file. The window is
  // what the line can do while the engine actually downloads: WebKit sometimes fetched a 480p
  // file slowly for 3 s and then in a burst, which averaged from play read 3.46x, just under the
  // 3.75x a 720p file needs. Both stop at the sample that completes the file (later ones only
  // shrink). A file that was all but
  // complete when play began (WebKit fetches most of a preload="metadata" file on a fast line)
  // is treated like a cached one: nothing is left for the sharper download to compete with.
  function packHqRate(v) {
    if (v.__fpHqT0 == null || v.__fpHqFull) return v.__fpHqRate || 0;
    var now = packHqNow(), end = packHqEnd(v), d = v.duration || 0;
    var wall = (now - v.__fpHqT0) / 1000;
    var full = !!d && end >= d - 0.25;
    var best = v.__fpHqRate || 0;
    if (end > v.__fpHqB0 && (wall >= 1 || full)) best = Math.max(best, (end - v.__fpHqB0) / Math.max(wall, 0.25));
    var w = v.__fpHqWin || (v.__fpHqWin = { t: v.__fpHqT0, end: v.__fpHqB0 });
    var span = (now - w.t) / 1000;
    if (span >= 1) {
      if (end > w.end) best = Math.max(best, (end - w.end) / Math.max(span, 0.25));
      v.__fpHqWin = { t: now, end: end };
    }
    v.__fpHqRate = best;
    if (full) {
      v.__fpHqFull = true;
      if (d - v.__fpHqB0 < 2) v.__fpHqCached = true;
    }
    return best;
  }
  function packHqFast(v, url) {
    if (v.__fpHqCached) return true;
    var to = packHqMbps(url), from = packHqMbps(v.currentSrc);
    var need = to && from ? to / from : 3;
    return packHqRate(v) >= need * 1.25;
  }
  // And only once the start file leaves the line free: PACK_HQ_AHEAD seconds buffered ahead of
  // the playhead (or all of it), or the engine has stopped building its buffer with
  // PACK_HQ_BANKED seconds in hand (the buffer ahead no bigger than at the previous look). The
  // sharper download is greedy, and with a thin cushion it starved the 480p clip on a 12 Mbps
  // line in Chromium. Waiting for the whole file instead held the upgrade for 15-20 s (Chromium
  // stops reading ahead at about 18 s of a 480p clip), and an engine that keeps fewer than 10 s
  // ahead held it until the file was complete. Called once per look.
  function packHqRoom(v) {
    var ahead = packHqEnd(v) - v.currentTime, last = v.__fpHqAhead;
    v.__fpHqAhead = ahead;
    if (v.__fpHqCached || v.__fpHqFull) return true;
    var left = (v.duration || 0) - v.currentTime - 0.25;
    return ahead >= Math.min(PACK_HQ_AHEAD, left) || (last != null && ahead >= PACK_HQ_BANKED && ahead <= last + 0.5);
  }
  // One pending check per clip: a stall ('waiting', then 'playing' again) must not start a
  // second chain of checks.
  function packHqLater(v, ms) {
    window.clearTimeout(v.__fpHqTimer);
    v.__fpHqTimer = window.setTimeout(function () { packHqStart(v); }, ms);
  }
  ['play', 'progress'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var v = e.target;
      if (!v || !v.matches || !v.matches('#dogs video[data-fp-stage]:not([data-fp-hq-done])')) return;
      if (type === 'play') {
        // Every play starts a fresh measurement (the best rate so far is kept): a clip that
        // left the stage and came back must not count the time it was away.
        if (v.__fpHqFull) return;
        v.__fpHqT0 = packHqNow();
        v.__fpHqB0 = packHqEnd(v);
        v.__fpHqWin = null;
        v.__fpHqAhead = null;
        var d = v.duration || 0;
        if (d && v.__fpHqB0 >= d - 0.25) v.__fpHqCached = true;
      } else if (v.__fpHqT0 != null) packHqRate(v);
    }, true);
  });
  // A file given up on (too slow, an error) is skipped for the rest of the session, so a slow
  // line with the 480p file in its cache does not fetch 20 s of it again on every page.
  function packHqSkipped(u) { try { return window.sessionStorage.getItem('fp.hq.no:' + u) === '1'; } catch (e) { return false; } }
  function packHqSkip(u) { try { window.sessionStorage.setItem('fp.hq.no:' + u, '1'); } catch (e) { /* private mode */ } }
  // The files this width may move up to, best first; never the file playing now or a smaller one.
  function packHqTargets(v) {
    var wide = !!(window.matchMedia && window.matchMedia('(min-width: 1440px)').matches);
    var cur = v.currentSrc || '', curMbps = packHqMbps(cur);
    return (wide ? ['data-fp-hq1080', 'data-fp-hq720'] : ['data-fp-hq720']).map(function (a) {
      return v.getAttribute(a) || '';
    }).filter(function (u, i, all) {
      if (!u || all.indexOf(u) !== i || packHqSkipped(u)) return false;
      if (cur && (u === cur || cur.indexOf(u.split('?')[0]) !== -1)) return false;
      var m = packHqMbps(u);
      return !(m && curMbps && m <= curMbps);
    });
  }
  function packHqAllowed() {
    if (fpSaveData() || fpReduceMotion()) return false;
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !(c && c.effectiveType === '3g');
  }
  // Stops a video's download for good: with only src removed, load() would select its
  // <source> again and fetch that file.
  function packHqStop(el) {
    try {
      el.pause();
      el.removeAttribute('src');
      Array.prototype.slice.call(el.querySelectorAll('source')).forEach(function (s) { el.removeChild(s); });
      el.load();
    } catch (e) { /* already gone */ }
    if (el.parentNode) el.parentNode.removeChild(el);
  }
  function packHqDrop(giveUp) {
    if (!packHq) return;
    window.clearTimeout(packHq.timer);
    var el = packHq.el, v = packHq.from;
    packHq = null;
    if (giveUp) {
      if (v.setAttribute) v.setAttribute('data-fp-hq-done', 'no');
      if (giveUp === true) packHqSkip(el.getAttribute('src') || '');
    }
    // Already fading in: fade back out rather than snap from sharp to soft.
    if (el.style.opacity && el.style.opacity !== '0') {
      el.style.transition = 'opacity .35s ease';
      el.style.opacity = '0';
      window.setTimeout(function () { packHqStop(el); }, 360);
    } else packHqStop(el);
  }
  // Sound came on, or the sync ran out of time: drop the job and look again later; the start
  // waits (uncounted) while the clip has sound.
  function packHqRetry(v) { packHqDrop(); packHqLater(v, PACK_HQ_CHECK); }
  function packHqGiveUp() { packHqDrop(true); }
  function packHqOnStage(v) {
    return !!v && !!v.parentNode && v.getAttribute('data-fp-stage') === String(st.packStage) && !v.paused && !st.pkPaused && !document.hidden;
  }
  // Whatever the measures said, the sharper download must never stall the clip on stage: while a
  // job runs, a start file that is not complete and has under PACK_HQ_STARVE seconds loaded ahead
  // ends it as a line too slow for both (skipped for the session).
  function packHqWatch(job) {
    window.setTimeout(function () {
      if (packHq !== job) return;
      var v = job.from, d = v.duration || 0, end = packHqEnd(v);
      if (!(d && end >= d - 0.25) && end - v.currentTime < PACK_HQ_STARVE) { packHqDrop(true); return; }
      packHqWatch(job);
    }, 500);
  }
  function packHqStart(v) {
    if (packHq || !packHqOnStage(v) || v.hasAttribute('data-fp-hq-done') || !packHqAllowed()) return;
    if (!v.muted) { packHqLater(v, PACK_HQ_CHECK); return; }   // waits for the sound to go off
    var all = packHqTargets(v);
    if (!all.length) { if (v.getAttribute('data-fp-hq720') || v.getAttribute('data-fp-hq1080')) v.setAttribute('data-fp-hq-done', 'no'); return; }
    packHqRate(v);
    var url = all.filter(function (u) { return packHqFast(v, u); })[0];
    var room = packHqRoom(v);
    if (!url || !room) {
      // Only a slow line counts toward giving up after PACK_HQ_WAIT: a fast one waiting for room
      // in the start file is no reason to, though it too stops looking after three times as long.
      v.__fpHqChecks = (v.__fpHqChecks || 0) + 1;
      if (!url) v.__fpHqSlow = (v.__fpHqSlow || 0) + 1;
      if ((v.__fpHqSlow || 0) * PACK_HQ_CHECK >= PACK_HQ_WAIT || v.__fpHqChecks * PACK_HQ_CHECK >= 3 * PACK_HQ_WAIT) v.setAttribute('data-fp-hq-done', 'no');
      else packHqLater(v, PACK_HQ_CHECK);
      return;
    }
    var el = document.createElement('video');
    el.muted = true; el.loop = true; el.playsInline = true; el.preload = 'auto';
    el.setAttribute('muted', ''); el.setAttribute('loop', ''); el.setAttribute('playsinline', '');
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('data-fp-hq-pending', '');
    el.style.cssText = v.style.cssText;
    el.style.transition = 'none'; el.style.opacity = '0'; el.style.pointerEvents = 'none';
    el.src = url;
    v.parentNode.insertBefore(el, v.nextSibling);
    var job = packHq = { from: v, el: el, timer: window.setTimeout(packHqGiveUp, PACK_HQ_WAIT), tries: 0, refades: 0, seams: 0 };
    packHqWatch(job);
    el.addEventListener('error', function () { if (packHq === job) packHqGiveUp(); });
    // Loaded from where the clip will be when they meet (PACK_HQ_LEAD ahead), not from 0: the
    // clip is often seconds in by now, and what a download from 0 fetched was thrown away by the
    // seek to the clip's second (2.7 s lost in iOS Safari, 6-8 s in WebKit). Near the loop end
    // it stays at 0, where the clip is about to go.
    el.addEventListener('loadedmetadata', function () {
      if (packHq !== job) return;
      var d = el.duration || v.duration || 0, at = v.currentTime + (v.__fpHqLead || PACK_HQ_LEAD);
      if (v.currentTime > 0.5 && !(d && at > d - 1)) el.currentTime = at;
    }, { once: true });
    el.addEventListener('canplaythrough', function () { if (packHq === job) packHqSync(job); }, { once: true });
    // A refused play (Low Power Mode) ends the job. Once the sync has begun, a rejection is the
    // AbortError of the sync's own pause: iOS Safari keeps this play() pending until playback
    // starts, and a file sought to the clip's second may reach canplaythrough before it has; the
    // iPhone 17 simulator dropped the job there and never looked again.
    var p = el.play();
    if (p && p.catch) p.catch(function () { if (packHq === job && !job.budget) packHqDrop(); });
  }
  // The sync budget ran out. Waiting on the sharper file's download, the line cannot carry it:
  // a slow-line give-up, skipped for the session. Anything else is a failed sync.
  function packHqOutOfTime(job) {
    if (packHq !== job) return;
    if (job.starved) packHqDrop(true); else packHqSyncFailed(job.from);
  }
  // Fade the sharper file back out (the 480p is still under it) and sync it again once the fade
  // is done, with fresh tries and a fresh budget.
  function packHqResync(job) {
    var wait = 360;
    var el = job.el;
    el.style.transition = 'opacity .35s ease';
    el.style.opacity = '0';
    job.tries = 0;
    window.clearTimeout(job.timer);
    job.timer = window.setTimeout(function () { packHqOutOfTime(job); }, wait + PACK_HQ_SYNC);
    window.setTimeout(function () { if (packHq === job) packHqSync(job); }, wait);
  }
  // A clip whose sharper file fails to sync in two jobs gives up for the page view, not the
  // session: the line proved fast, unlike a give-up for speed or an error.
  function packHqSyncFailed(v) {
    v.__fpHqSyncFails = (v.__fpHqSyncFails || 0) + 1;
    if (v.__fpHqSyncFails >= 2) packHqDrop('page'); else packHqRetry(v);
  }
  // Pause the sharper file on a frame a lead ahead (PACK_HQ_LEAD at first), start it as the
  // clip reaches that frame, and fade only once the two agree: a single seek-then-play left it
  // 0.2-2.2 s behind in Chromium and 0.18 s ahead in WebKit, both visible during the fade.
  function packHqSync(job) {
    var v = job.from, el = job.el;
    if (packHq !== job) return;
    if (!packHqOnStage(v)) { packHqDrop(); return; }
    if (!v.muted) { packHqRetry(v); return; }
    if (++job.tries > PACK_HQ_TRIES) { packHqSyncFailed(v); return; }
    if (!job.budget) {
      // The line proved fast; the 20 s give-up no longer applies. A sync that does not land in
      // PACK_HQ_SYNC (a stall, a background tab) counts as a failed job, like running out of tries.
      // Armed once per job: the waits below hand their try back, and must not restart the clock.
      job.budget = true;
      window.clearTimeout(job.timer);
      job.timer = window.setTimeout(function () { packHqOutOfTime(job); }, PACK_HQ_SYNC);
    }
    var d = el.duration || v.duration || 0;
    var lead = job.lead || v.__fpHqLead || PACK_HQ_LEAD;
    var target = v.currentTime + lead;
    // Past the loop: wait for it, without spending a try (waiting for the loop is no slow line).
    // A sharper file without its start waits further out: met in the last seconds it would still
    // be on top, frozen, when it loops (the 1.5 s hand-off, the 0.4 s adopt delay, the fade).
    if (d && target > d - (packHqHasStart(el) ? 0.3 : 2.3)) { job.starved = false; window.setTimeout(function () { packHqSync(job); }, 700); job.tries--; return; }
    // Seek only into what the sharper file has loaded, in the range that holds the frame and a
    // second past it: a seek beyond it waits for the network (WebKit took 1.4 s and more) while
    // the clip moves on. Looked at again shortly, without spending a try; the PACK_HQ_SYNC budget
    // still runs, and running out while waiting here is a line too slow for the file.
    if (packHqEndAt(el, target) < Math.min(target + 1, d ? d - 0.05 : target + 1)) { job.starved = true; window.setTimeout(function () { packHqSync(job); }, 250); job.tries--; return; }
    job.starved = false;
    el.pause();
    // Each attempt's poll ends by starting the next attempt or the fade, so one runs at a time.
    var mine = function () { return packHq === job; };
    // The seek has landed when the element is no longer seeking and has the frame. Polled rather
    // than taken from 'seeked', which WebKit at times never fired for a file still loading. The
    // lead follows the measured seek time: WebKit took 1.4 s and more to seek in a file it was
    // still downloading, so a 0.5 s lead was always missed; the next attempt leads by half as
    // much again as the last seek took (up to PACK_HQ_LEAD_MAX). A seek that has not landed in
    // PACK_HQ_SEEK is tried again with the longest lead.
    var seekAt = packHqNow(), took = 0;
    var landed = function () {
      if (!mine()) return;
      if (el.seeking || el.readyState < 2) {
        if (packHqNow() - seekAt > PACK_HQ_SEEK) { job.lead = v.__fpHqLead = PACK_HQ_LEAD_MAX; packHqSync(job); return; }
        window.setTimeout(landed, 16); return;
      }
      took = (packHqNow() - seekAt) / 1000;
      wait();
    };
    var wait = function () {
      if (!mine()) return;
      if (!packHqOnStage(v)) { packHqDrop(); return; }
      if (!v.muted) { packHqRetry(v); return; }
      var ct = v.currentTime;
      if (ct > target + 0.25) { job.lead = v.__fpHqLead = Math.min(PACK_HQ_LEAD_MAX, Math.max(lead, took * 1.5 + 0.3)); packHqSync(job); return; }   // missed it
      if (ct < target - lead - 0.25) { packHqSync(job); return; }   // looped
      if (ct < target - 0.03) { window.setTimeout(wait, 16); return; }
      var p = el.play();
      var go = function () {
        if (!mine()) return;
        if (Math.abs(el.currentTime - v.currentTime) > PACK_HQ_DRIFT) { packHqSync(job); return; }
        el.style.transition = 'opacity .35s ease';
        el.style.opacity = v.style.opacity || '1';
        window.clearTimeout(job.timer);
        job.timer = window.setTimeout(function () { if (packHq === job) packHqAdopt(job); }, 400);
      };
      if (p && p.then) p.then(go, function () { if (mine()) packHqDrop(); }); else go();
    };
    el.currentTime = d ? target % d : target;
    window.setTimeout(landed, 16);
  }
  function packHqAdopt(job) {
    var v = job.from, el = job.el;
    if (!packHqOnStage(v)) { packHqDrop(); return; }
    if (!v.muted) { packHqRetry(v); return; }   // sound came on during the fade
    // Started, it must be playing and with the clip: one that stalled or started late is faded
    // back out and synced again; the second time in one job counts as a failed sync.
    if (el.paused || Math.abs(el.currentTime - v.currentTime) > 2 * PACK_HQ_DRIFT) {
      if (++job.refades > 1) { packHqSyncFailed(v); return; }
      packHqResync(job);
      return;
    }
    // Sought to the clip's second, it may not have its start: WebKit on a desktop left that gap
    // unloaded and froze 0.6 s at the first loop. Until the start is there the 480p stays under
    // it and takes the loop: the sharper file fades out shortly before; its sync waits for the
    // loop (see packHqSync) and it is adopted once its start is there.
    if (!packHqHasStart(el)) {
      var d = el.duration || v.duration || 0;
      if (d && d - v.currentTime < 1.5) {
        if (++job.seams > 2) { packHqSyncFailed(v); return; }
        packHqResync(job);
      } else job.timer = window.setTimeout(function () { if (packHq === job) packHqAdopt(job); }, 250);
      return;
    }
    packHq = null;
    ['id', 'data-vsrc', 'data-fp-stage', 'data-fp-a', 'data-fp-s', 'aria-hidden', 'poster'].forEach(function (a) {
      if (v.hasAttribute(a)) el.setAttribute(a, v.getAttribute(a));
    });
    el.removeAttribute('data-fp-hq-pending');
    el.setAttribute('data-fp-hq-done', '');
    el.style.pointerEvents = '';
    el.style.transition = v.style.transition;
    // Its bindings (opacity:pkOpN, aria-hidden:%pkHidN%) follow it; the old element's go.
    for (var i = boundNodes.length - 1; i >= 0; i--) if (boundNodes[i].el === v) boundNodes.splice(i, 1);
    var spec = parseHooks(el, null);
    if (spec) boundNodes.push(spec);
    v.removeAttribute('id');
    packHqStop(v);
    render();
  }
  // Media events do not bubble: one capture listener sees every stage clip start playing,
  // the upgraded ones included (they carry data-fp-hq-done and are skipped).
  document.addEventListener('playing', function (e) {
    var v = e.target;
    if (!v || !v.matches || !v.matches('#dogs video[data-fp-stage]:not([data-fp-hq-done])')) return;
    packHqLater(v, PACK_HQ_DELAY);
  }, true);
  // A page in the background does not upgrade: its timers run late (Chromium once a second, iOS
  // not at all), which turned sync attempts into failures on a fast line. Hidden, the job is
  // dropped uncounted; visible again, the clip on stage is looked at afresh.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) { packHqDrop(); return; }
    var v = document.querySelector('#dogs video[data-fp-stage="' + st.packStage + '"]:not([data-fp-hq-done])');
    if (v && packHqOnStage(v)) packHqLater(v, PACK_HQ_DELAY);
  });

  // Read at call time, not captured at boot: a shopper can turn the OS preference
  // on while the page is open, and a snapshot would keep moving for the rest of
  // the session.
  function fpReduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  // One connection policy for the whole theme. It existed only inside the deferred-
  // image pre-warm, so the store refused a 402 KB image pre-fetch on Data Saver and
  // then auto-played 2.2 MB of homepage video on the same connection — two policies
  // contradicting each other in one file. Read at call time, like fpReduceMotion.
  function fpSaveData() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    var et = c.effectiveType || '';
    return et === 'slow-2g' || et === '2g';
  }

  // ---------- hero ----------
  var heroTicker;
  function heroRotateMs() {
    var sec = CORE.heroRotateSeconds || 5;
    var el = document.querySelector('[data-fp-rotate]');
    if (el) {
      var v = parseFloat(el.getAttribute('data-fp-rotate'));
      if (v > 0) sec = v;
    }
    return sec * 1000;
  }
  // FP40 (2026-10-08). The slides that exist and whether they advance on their own
  // come from sections/hero.liquid (data-fp-slides, data-fp-autorotate). heroIndex
  // stays a PHYSICAL index 0-2, because heroOp/heroHid/heroCur/heroDot key on it;
  // the list only says which of the three are rendered — "0,2" since the video slide
  // ships off — so the ticker, the swipe and pickHero step over a missing slide
  // instead of fading to an element that is not there.
  function heroSection() { return document.querySelector('[data-fp-rotate]'); }
  function heroSlides() {
    var el = heroSection();
    var raw = el ? (el.getAttribute('data-fp-slides') || '0,1,2') : '0,1,2';
    var list = raw.split(',').map(function (x) { return parseInt(x, 10); })
      .filter(function (n) { return n >= 0 && n <= 2; });
    return list.length ? list : [0];
  }
  function heroStep(dir) {
    var list = heroSlides();
    var at = list.indexOf(st.heroIndex);
    if (at === -1) return list[0];
    return list[(at + dir + list.length) % list.length];
  }
  // Absent attribute = the markup predates the setting; rotate as it always did.
  function heroAutoRotates() {
    var el = heroSection();
    return !!el && el.getAttribute('data-fp-autorotate') !== 'false';
  }
  // Slide 2 (opacity:heroOp1 in sections/hero.liquid) is the only slide that
  // shows the video. It is declared preload="none" so it costs nothing until
  // someone calls play() — which the blanket autoplay kick used to do at boot,
  // streaming 800 KB against the LCP image for a slide many shoppers never see.
  // The explicit pause is a GUARD, never an early return: returning early would leave
  // the clip playing after a dot click moved the rotator off the video slide.
  // fpSaveData() is the same guard for the same reason: the theme's one connection
  // policy already refuses the image pre-warm and the pack clips on Data Saver / 2g,
  // and this was the one call site still streaming 822 KB of video on that connection —
  // the exact contradiction that policy exists to close. Refusing here shows the
  // section's own orange gradient, which hero.liquid:5-11 documents and accepts.
  function syncHeroVideo() {
    var hv = document.getElementById('fpHeroVideo');
    if (!hv) return;
    if (st.heroIndex % 3 === 1 && !st.heroPaused && !fpSaveData()) {
      hv.muted = true;
      if (hv.paused) hv.play().catch(function () { /* autoplay policy */ });
    } else if (!hv.paused) hv.pause();
  }
  function startHeroTicker() {
    window.clearInterval(heroTicker);
    // No hero on this page: the ticker still fired a full render() every 5s on
    // the cart, every product page and all seven policy pages.
    if (!document.querySelector('[data-fp-rotate]')) return;
    // A carousel that cannot be paused fails WCAG 2.2.2, so reduced motion holds
    // the slide the shopper is reading (the dots still move it by hand). Publish that
    // hold as state before returning: the control is rendered unconditionally and was
    // announcing aria-pressed="false" — i.e. "a slideshow is running" — over a hero
    // that never moves, and pressing it twice returned it to "playing" over the same
    // static hero. Guarded so the repeat calls from pickHero() cost no extra render.
    // Deliberate second effect: syncHeroVideo() reads the same flag, so a reduced-motion
    // shopper who clicks dot 2 by hand now gets the section's gradient instead of an
    // auto-playing loop — which is the preference being honoured, not a loss.
    if (fpReduceMotion()) { if (!st.heroPaused) patch({ heroPaused: true }); return; }
    // FP40: rotation is opt-in ("Rotate slides automatically", off by default). A
    // still hero needs no interval at all; dots and swipe go through pickHero().
    if (!heroAutoRotates()) return;
    // The explicit pause, which is the half 2.2.2 actually requires: it must hold
    // for a shopper who set no OS preference. This is also why pickHero() can keep
    // calling startHeroTicker() unconditionally — the pause is respected here, at
    // the single point that owns the interval.
    // FP40 (A5): the data-fp20-hold checks that stood here and in pickHero() are gone.
    // They let the FP entrance freeze the carousel — and when Safari refused even
    // muted autoplay (Low Power Mode) the hold never released, so the hero sat on
    // slide 1 with its dots hidden and swipe dead. The carousel waits on nothing now.
    if (st.heroPaused) return;
    heroTicker = window.setInterval(function () {
      // A hidden tab has no hero to read. Chrome's timer clamp is 1/second, well
      // under this 5 s period, so without the check a backgrounded homepage kept
      // running a full patch() re-render plus a video play() at its normal rate.
      if (document.hidden) return;
      // Slide 3 ships with data-fpsrc and no src, and the idle pre-warm REFUSES on
      // Data Saver / slow-2g / 2g. Without this the rotator would reach an empty box
      // and paint the bare gradient for those visitors. Idempotent: after the first
      // call it finds nothing left to promote.
      fpPromoteGallery();
      if (heroPending) return; // a dot/swipe pick is waiting on its slide (heroWhenReady)
      var nx = heroStep(1);
      heroWhenReady(nx, function () { patch({ heroIndex: nx }); syncHeroVideo(); });
    }, heroRotateMs());
  }
  // FP40 review fix 2026-10-08 (L1): hold the crossfade until the slide's photo can paint,
  // at most 800 ms. Promoting on the press started a 245 KB-1.1 MB download that the 1.2 s
  // opacity fade outran, so the fade landed on the bare gradient. decode() resolves once the
  // selected <source>/srcset candidate is loaded and decoded (rejects on error: go anyway);
  // past 800 ms the press must visibly answer, so the fade starts and the photo fills in.
  // A later pick supersedes a pending one (heroPickSeq), and the ticker skips while one
  // waits, so a slow photo can never land after the shopper has moved on. Slide 2 (video)
  // and a loaded photo go at once.
  var heroPickSeq = 0, heroPending = false;
  function heroWhenReady(i, go) {
    var seq = ++heroPickSeq;
    var im = document.querySelector('[data-screen-label="Hero"] img[data-fp-s="opacity:heroOp' + i + '"]');
    if (!im || (im.complete && im.naturalWidth > 0) || typeof im.decode !== 'function') {
      heroPending = false; go(); return;
    }
    var done = false;
    heroPending = true;
    var fire = function () {
      if (done) return;
      done = true;
      if (seq !== heroPickSeq) return;
      heroPending = false;
      go();
    };
    window.setTimeout(fire, 800);
    im.decode().then(fire, fire);
  }
  function pickHero(i) {
    if (heroSlides().indexOf(i) === -1) return; // a slide this hero does not render
    fpPromoteGallery(); // same reason as the ticker: never show a slide with no src
    heroWhenReady(i, function () {
      patch({ heroIndex: i });
      syncHeroVideo();
      startHeroTicker();
    });
  }
  var touchX = null;

  // ---------- handlers ----------
  function handler(name, ev, item, el) {
    var m;
    if (PAGE.handler && PAGE.handler(name, ev, item, el) === true) return;
    switch (name) {
      case 'mnavToggle': setMnav(!st.mnav); return;
      case 'mnavClose': setMnav(false); return;
      // The header CART (sections/header.liquid) is a <button> on home and PDP and a link to
      // /cart everywhere else, so there it works before this file runs and if it never does;
      // here it opens the drawer instead. On the link a modified or middle click, or a page
      // without the drawer section, keeps the link's own navigation.
      case 'openCart':
        if (el && el.tagName === 'A') {
          if (!fpDrawerEl() || (ev && (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button > 0))) return;
          if (ev && ev.preventDefault) ev.preventDefault();
        }
        playBark(400); openDrawer(); return;
      case 'closeCart': closeDrawer(); return;
      // searchLive is cleared with the query, not with each keystroke: an empty
      // box must show the local fallback, not the last person's results.
      case 'toggleSearch':
        if (st.searchOpen) closeSearchOverlay(); else openSearchOverlay();
        return;
      case 'closeSearch': closeSearchOverlay(); return;
      case 'panelClick': ev.stopPropagation(); return;
      // Keep the previous results on screen while the next request is in flight.
      // Wiping searchLive on every keystroke made the panel flash the four-product
      // local fallback — or a "nothing found" screen — between every character.
      case 'searchType': { var sq = ev.target.value; patch({ searchQ: sq }); fetchPredictive(sq); return; }
      case 'searchKey':
        if (ev.key === 'Escape') closeSearchOverlay();
        if (ev.key === 'Enter') {
          var sq = st.searchQ.trim();
          if (sq) {
            // full-catalog search page — the overlay only suggests the flagship four
            patch({ searchOpen: false, searchQ: '', searchLive: null });
            window.location.href = '/search?q=' + encodeURIComponent(sq);
          }
        }
        return;
      // An explicit behavior:'smooth' OVERRIDES the CSS scroll-behavior:auto that
      // assets/foodiepaws.css sets under prefers-reduced-motion, so these animated
      // regardless. Routed through the helper rather than deleted: with the key
      // absent every OTHER user drops to 'auto' inside a scroller that does not
      // inherit html's value.
      case 'prevReviews': { var r1 = document.getElementById('fpReviewsRail'); if (r1) r1.scrollBy({ left: -374, behavior: fpReduceMotion() ? 'auto' : 'smooth' }); return; }
      case 'nextReviews': { var r2 = document.getElementById('fpReviewsRail'); if (r2) r2.scrollBy({ left: 374, behavior: fpReduceMotion() ? 'auto' : 'smooth' }); return; }
      case 'pkSndToggle': sndToggle(); return;
      // WCAG 2.2.2. The hero rotator and the Pack In Action clips both start
      // moving on their own, so both need a control that stops them for a shopper
      // who set no OS preference. State only — the two labelled buttons belong to
      // the section owners, and shipping a dead control was the alternative.
      case 'heroPause':
        patch({ heroPaused: !st.heroPaused });
        if (st.heroPaused) window.clearInterval(heroTicker); else startHeroTicker();
        // The rotation is only half of what is moving. Slide 2 is a `loop` video, so
        // pausing while it was showing froze the rotator ON the video and left it
        // playing with no control that could stop it (WCAG 2.2.2).
        syncHeroVideo();
        return;
      case 'pkPauseToggle': {
        patch({ pkPaused: !st.pkPaused });
        var pkSect = document.getElementById('dogs');
        if (!pkSect) return;
        // STAGE clips only. This walked every <video> in the section, which is four
        // elements — two stage clips plus two thumbnails sharing their URLs — so
        // resuming started the whole set and kept four decode pipelines running for
        // two visible frames. Thumbnails are stills here; they never play.
        Array.prototype.slice.call(pkSect.querySelectorAll('video[data-fp-stage]')).forEach(function (v) {
          var onStage = v.getAttribute('data-fp-stage') === String(st.packStage);
          // Resuming still honours reduced motion, Data Saver and the on-screen
          // observer: the explicit pause is one more reason to stay stopped, never a
          // reason to start.
          if (st.pkPaused || !onStage) { if (!v.paused) v.pause(); }
          else if (packOnScreen && !fpReduceMotion() && !fpSaveData() && v.paused) v.play().catch(function () { /* noop */ });
        });
        if (st.pkPaused && st.pkSnd) muteAllPack();
        if (st.pkPaused) packHqDrop();
        return;
      }
      // FP40 review fix 2026-10-08 (L1): a touch on the hero is the start of a swipe, so
      // the deferred slide starts loading now, a whole gesture ahead of pickHero().
      case 'heroTouchStart': touchX = ev.touches && ev.touches[0] ? ev.touches[0].clientX : null; fpPromoteGallery(); return;
      case 'heroTouchEnd': {
        if (touchX == null) return;
        var t = ev.changedTouches && ev.changedTouches[0] ? ev.changedTouches[0].clientX : null;
        if (t == null) { touchX = null; return; }
        var dx = t - touchX;
        touchX = null;
        if (Math.abs(dx) < 48) return;
        pickHero(heroStep(dx < 0 ? 1 : -1));
        return;
      }
      case 'jwOpen':
        if (st.jwStatus === 'loading') return;
        playBark();
        patch({ jwStatus: 'loading' });
        fpAnnounce(JW.loadingText);
        window.fpJwOpenForm(function () { patch({ jwStatus: 'error' }); fpAnnounce(JW.unavailableText); }, function () { patch({ jwStatus: '' }); });
        return;
      case 'jwStatusDismiss':
        ev.stopPropagation();
        if (fpJwPending) fpJwPending.cancel();
        patch({ jwStatus: '' });
        return;
      // The bubble is a div with role="button" and tabindex="0", so the browser
      // gives it no activation behaviour of its own — it was mouse-only.
      // The ✕ sits INSIDE the bubble, which carries keydown:jwKey. The delegated
      // dispatcher matches the first ancestor carrying the event NAME, so with only a
      // click binding here a keydown walked past the ✕ to the bubble, which
      // preventDefault()s (killing the button's own Enter/Space activation) and then
      // OPENS the card — the dismiss control did the opposite of its label for
      // keyboard users. Same state as jwMinimize; preventDefault also suppresses the
      // native click, so the minimise must happen here rather than be left to it.

      // The ✕ sits INSIDE the bubble, which carries keydown:jwKey. The delegated
      // dispatcher matches the first ancestor carrying the event NAME, so with only a
      // click binding here a keydown walked past the ✕ to the bubble, which
      // preventDefault()s (killing the button's own Enter/Space activation) and then
      // OPENS the card — the dismiss control did the opposite of its label for
      // keyboard users. Same state as jwMinimize; preventDefault also suppresses the
      // native click, so the minimise must happen here rather than be left to it.
      case 'jwMinKey':
        if (ev.key === 'Enter' || ev.key === ' ' || ev.key === 'Spacebar') {
          ev.preventDefault();
          ev.stopPropagation();
          patch({ jw: 'min' });
        }
        return;
      case 'jwMinimize': ev.stopPropagation(); patch({ jw: 'min' }); return;
      // The join-widget's jwSubmit handler was REMOVED here on 2026-08-29, along with the
      // card and done panes it served. It POSTed to https://a.klaviyo.com/client/subscriptions/
      // from the browser, and that request carries no form fingerprint, so Klaviyo's Bot
      // Protection read it as scripted and globally suppressed 18 real, consenting customers
      // between 24 and 28 Aug - every one with a Shopify consent record written within 0-3
      // seconds of the suppression. Nothing can be added to that request to fix it: the
      // endpoint has no form field. The bubble now calls window.fpJwOpenForm, which opens the
      // native Klaviyo form instead.
      // DO NOT reintroduce a client-side subscribe here. The only safe lanes are a native
      // Klaviyo form or a server-side call holding a private key.
      case 'checkout': {
        if (fpBusy(el)) return;
        // The empty-cart markup is gone, so the CHECKOUT button can survive on a
        // stale render after the last line was removed. /checkout with nothing in
        // it bounces straight back to the cart — do not send the shopper there.
        if (cartTotals().count === 0) return;
        playBark(400);
        // The last step the theme can see. Everything after this is Shopify checkout,
        // so a drop between add_to_cart and begin_checkout is a cart problem and a drop
        // after it is a checkout problem - which is the split the owner cannot currently make.
        if (!REAL) {
          // Silently doing nothing on the highest-intent click in the store reads
          // as a broken site. Say why.
          fpToast('Checkout is temporarily unavailable. Please refresh the page.');
          return;
        }
        fpGoCheckout(el, cartTotals());
        return;
      }
    }
    if ((m = name.match(/^pickHero([0-2])$/))) { pickHero(+m[1]); return; }
    if ((m = name.match(/^pkPick(\d+)$/))) {
      var stage = +m[1];
      if (st.packStage !== stage && st.pkSnd) muteAllPack();
      if (st.packStage !== stage) packHqDrop();
      patch({ packStage: stage });
      // Hand the stage over. Every stage clip except the first ships preload="none",
      // so the one arriving may hold no data at all — without this, picking clip 2
      // showed a blank box. The shopper asked for this one by name, so it plays even
      // on a metered connection; the one leaving stops so only ever one clip decodes.
      var pkS = document.getElementById('dogs');
      if (pkS) Array.prototype.slice.call(pkS.querySelectorAll('video[data-fp-stage]')).forEach(function (v) {
        if (v.getAttribute('data-fp-stage') !== String(stage)) { if (!v.paused) v.pause(); return; }
        if (st.pkPaused || fpReduceMotion()) return;
        if (v.paused) v.play().catch(function () { /* noop */ });
      });
      return;
    }
    if ((m = name.match(/^chewPick(\d+)$/))) { patch({ chewIdx: +m[1] }); return; }
    if ((m = name.match(/^q(\d+)\.pick$/))) {
      var qi = +m[1] - 1;
      patch({ faqIdx: st.faqIdx === qi ? -1 : qi });
      return;
    }
    if ((m = name.match(/^best(\d+)\.(pickOne|pickSub|add|addSub|addOne|fDec|fInc)$/))) {
      var i = +m[1];
      var act = m[2];
      var f = st.freq[i] || 4;
      var key = (BEST[i] || {}).key || 'bully';
      // honesty guard: with a real cart but no selling plan on this product,
      // subscribing would charge full price while showing −15% — keep one-time
      var subOk = !REAL || !!(BEST[i] && BEST[i].sellingPlanId);
      switch (act) {
        case 'pickOne': st.sel[i] = 'one'; render(); return;
        case 'pickSub': if (!subOk) return; st.sel[i] = 'sub'; render(); return;
        // Clamp to the cadences this product actually publishes (1-6 here). The old
        // 1..12 range let the card offer "every 11 weeks", which no plan sells.
        case 'fDec': case 'fInc': {
          var fRng = bestWeekRange(i) || { min: 1, max: 12 };
          var fWant = f + (act === 'fInc' ? 1 : -1);
          st.freq[i] = Math.min(fRng.max, Math.max(fRng.min, fWant));
          render(); return;
        }
        // `el` is forwarded on every add so the button can be locked for the
        // duration of the request — a double-tap used to post twice.
        case 'add': {
          var sub = st.sel[i] === 'sub';
          if (fpBusy(el)) return;
          if (sub) { celebrate(el, key); showSubStrip(); }
          if (REAL) realAdd(i, sub, f, el);
          else pushKey(sub ? key + ':sub:' + f : key);
          return;
        }
        case 'addSub':
          if (fpBusy(el)) return;
          if (!subOk) {
            if (REAL) realAdd(i, false, f, el);
            else pushKey(key);
            return;
          }
          celebrate(el, key);
          showSubStrip();
          if (REAL) realAdd(i, true, f, el);
          else pushKey(key + ':sub:' + f);
          return;
        case 'addOne':
          if (fpBusy(el)) return;
          if (REAL) realAdd(i, false, f, el);
          else pushKey(key);
          return;
      }
    }
    if (name.indexOf('item.') === 0 && item) {
      var op = name.slice(5);
      if (op === 'inc') { bump(item, 1); return; }
      if (op === 'dec') { bump(item, -1); return; }
      if (op === 'remove') {
        // Same outcome as "−" at quantity 1, so it must say the same sentence.
        // bump() announces; realChange only toasts on FAILURE, so the ✕ succeeded in
        // silence — the row simply vanished from under a screen-reader user.
        fpAnnounce((item.name || 'Item') + ' removed from your cart.');
        if (REAL) { realChange(item._key, 0); return; }
        delete st.cart[item._key];
        saveDemoCart();
        render();
        return;
      }
    }
  }

  function delegated(evName) {
    return function (ev) {
      var el = ev.target instanceof Element ? ev.target : null;
      while (el && el !== document.documentElement) {
        var spec = el.getAttribute && el.getAttribute('data-fp-on');
        if (spec) {
          var parts = spec.split(';');
          for (var i = 0; i < parts.length; i++) {
            var ci = parts[i].indexOf(':');
            if (ci > 0 && parts[i].slice(0, ci) === evName) {
              var item = null;
              var walk = el;
              while (walk && !item) { item = walk.__fpItem || null; walk = walk.parentElement; }
              handler(parts[i].slice(ci + 1), ev, item, el);
              return;
            }
          }
        }
        el = el.parentElement;
      }
    };
  }

  // ---------- SHOP CHEWS mega menu (desktop hover panel, rendered by sections/header.liquid) ----------
  function initMegaMenu() {
    var trg = document.getElementById('fpMegaTrigger');
    var panel = document.getElementById('fpMega');
    if (!trg || !panel) return;
    var mq = window.matchMedia ? window.matchMedia('(min-width: 1024px)') : { matches: true };
    var openT = 0, closeT = 0;
    function isOpen() { return panel.classList.contains('fp-open'); }
    function setOpen(on) {
      window.clearTimeout(openT); window.clearTimeout(closeT);
      panel.classList.toggle('fp-open', on);
      trg.classList.toggle('fp-mm-on', on);
      trg.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (on) {
        // park the caret notch under the trigger's center
        var r = trg.getBoundingClientRect();
        var p = panel.getBoundingClientRect();
        panel.style.setProperty('--fp-mm-caret', Math.round(r.left + r.width / 2 - p.left - 6) + 'px');
      }
    }
    // hover intent: 120ms to open (grazes don't trigger), 250ms grace to close (diagonal travel survives)
    function wantOpen() { if (!mq.matches) return; window.clearTimeout(closeT); window.clearTimeout(openT); openT = window.setTimeout(function () { setOpen(true); }, 120); }
    function wantClose() { window.clearTimeout(openT); window.clearTimeout(closeT); closeT = window.setTimeout(function () { setOpen(false); }, 250); }
    [trg, panel].forEach(function (el) {
      el.addEventListener('mouseenter', wantOpen);
      el.addEventListener('mouseleave', wantClose);
    });
    trg.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Enter' && ev.key !== ' ' && ev.key !== 'Spacebar') return;
      // Below 1024px the mega panel is display:none, so toggling it does nothing —
      // and preventDefault killed the anchor's own navigation too. Between 744px
      // (where the mobile nav hands over) and 1023px this link was a dead control
      // for keyboard users. There, do exactly what a click does.
      ev.preventDefault();
      if (!mq.matches) {
        if (trg.href) window.location.href = trg.href; else trg.click();
        return;
      }
      var want = !isOpen();
      setOpen(want);
      // The panel is emitted AFTER the whole header row, so six header tab stops sit
      // between the trigger and the panel's first link: a keyboard user who opened
      // the menu had to walk past the search, account and cart controls to reach the
      // thing they just opened. Move focus in, the way a click-driven menu behaves.
      if (want) {
        var first = panel.querySelector('a[href],button:not([disabled])');
        if (first && first.focus) first.focus({ preventScroll: true });
      }
    });
    panel.addEventListener('focusin', function () { window.clearTimeout(closeT); });
    // …and close when focus leaves it, so the panel cannot stay open behind a
    // keyboard user who has tabbed on. relatedTarget is null for a background click,
    // where closing is also what we want. The outside-click handler below may reach
    // the same conclusion in the same gesture; setOpen is idempotent, so a second
    // call is a no-op rather than a double toggle.
    panel.addEventListener('focusout', function (ev) {
      if (!isOpen()) return;
      var to = ev.relatedTarget;
      if (to && (panel.contains(to) || trg === to || trg.contains(to))) return;
      setOpen(false);
    });
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && isOpen()) { setOpen(false); trg.focus(); }
    });
    document.addEventListener('click', function (ev) {
      if (!isOpen()) return;
      var t = ev.target instanceof Element ? ev.target : null;
      if (t && (panel.contains(t) || trg === t || trg.contains(t))) return;
      setOpen(false);
    });
    window.addEventListener('scroll', function () { if (isOpen()) wantClose(); }, { passive: true });
  }

  // ---------- FIND MY CHEW quiz v2 (sections/main-find-my-chew.liquid) ----------
  // competitor-informed: optional name merge, protein exclusion (the lamb line's
  // moment), goal question, match-reason chips — every answer really changes the result.
  function initFindMyChew() {
    var root = document.getElementById('fpQuiz');
    if (!root) return;
    var slots = {}, copy = {};
    try {
      var data = JSON.parse(document.querySelector('script[data-fp-data="fmq"]').textContent);
      slots = data.slots || {};
      copy = data.reasons || {};
    } catch (e) { slots = {}; copy = {}; }
    var ans = { size: '', power: '', protein: '', goal: '' };
    var name = '';
    var cur = 0;
    var pupWord = root.getAttribute('data-fmq-pup') || 'your pup';
    var eyeTplNamed = root.getAttribute('data-fmq-eyename') || "{N}'S PERFECT MATCH";
    var eyeEl = root.querySelector('.fmq-eyebrow');
    var eyeDefault = eyeEl ? eyeEl.textContent : '';
    // Product-page size rules apply before every ranking and fallback. The handle
    // comes from Shopify's selected product, so relabelling a slot cannot bypass it.
    // Unknown products fail closed until their suitability has been reviewed.
    var suitability = {
      '4-bully-sticks-bulk': { sizes: ['small'], beef: true },
      '5-6-inch-thin-bully-sticks-25-count': { sizes: ['small'], beef: true, light: true },
      'bully-stick-bites': { sizes: ['small'], beef: true, training: true },
      '6-bully-sticks': { sizes: ['medium'], beef: true },
      '6-jumbo-bully-sticks': { sizes: ['medium', 'large'], beef: true },
      '12-bully-sticks': { sizes: ['large'], beef: true },
      '12-jumbo-bully-sticks': { sizes: ['large'], beef: true },
      'collagen': { sizes: ['medium', 'large'], beef: false },
      'tripe': { sizes: ['medium', 'large'], beef: false },
      '12-inch-lamb-collagen-sticks-3-count': { sizes: ['large'], beef: false },
      '12-inch-lamb-tripe-sticks-4-count': { sizes: ['large'], beef: false },
      'variety': { sizes: ['medium', 'large'], beef: false }
      // The 15-count pack is excluded from automatic recommendations until its
      // component size suitability is confirmed. Unreviewed handles fail closed.
    };
    function eligible(k) {
      var product = slots[k];
      var rule = product && Object.prototype.hasOwnProperty.call(suitability, product.handle) ? suitability[product.handle] : null;
      if (!rule || product.available === false || rule.sizes.indexOf(ans.size) === -1) return false;
      if ((ans.protein === 'nobeef' || ans.protein === 'unsure') && rule.beef) return false;
      if (rule.light && ans.power === 'power') return false;
      // Bite-size rewards and full-length supervised chews are different jobs.
      return ans.goal === 'training' ? rule.training === true : rule.training !== true;
    }
    function applyName() {
      var who = name || pupWord;
      var tpls = root.querySelectorAll('[data-fmq-tpl]');
      for (var i = 0; i < tpls.length; i++) tpls[i].textContent = tpls[i].getAttribute('data-fmq-tpl').replace('{N}', who);
      if (eyeEl) eyeEl.textContent = name ? eyeTplNamed.replace('{N}', name.toUpperCase()) : eyeDefault;
    }
    function firstSlot(keys) {
      for (var i = 0; i < keys.length; i++) { if (slots[keys[i]]) return { k: keys[i], p: slots[keys[i]] }; }
      return null;
    }
    function planFor() {
      var s = ans.size, p = ans.power, g = ans.goal;
      var nb = ans.protein === 'nobeef';
      var lamb = nb || ans.protein === 'unsure';
      var hero, alts;
      if (g === 'sample') {
        hero = lamb ? ['variety_10', 'variety_15'] : (p === 'power' || s === 'large') ? ['variety_15', 'variety_10'] : ['variety_10', 'variety_15'];
        alts = (lamb || p === 'gentle') ? ['collagen_6', 'tripe_6'] : ['bully_6', 'tripe_6'];
      } else if (g === 'training') {
        hero = nb ? ['tripe_6', 'collagen_6'] : lamb ? ['tripe_6', 'bully_bites'] : ['bully_bites', 'tripe_6'];
        alts = nb ? ['collagen_6', 'variety_10'] : ['bully_thin', 'variety_15'];
      } else if (g === 'marathon') {
        if (nb) hero = s === 'small' ? ['collagen_6', 'collagen_12'] : ['collagen_12', 'collagen_6'];
        // The unsure answer offers a lamb chew in the right size.
        else if (lamb) hero = s === 'small' ? ['collagen_6', 'collagen_12'] : ['collagen_12', 'tripe_12'];
        else if (s === 'large') hero = p === 'gentle' ? ['bully_12', 'collagen_12'] : ['bully_12j', 'bully_12'];
        else if (s === 'medium') hero = p === 'power' ? ['bully_6j', 'bully_12'] : ['bully_12', 'bully_6j'];
        else hero = p === 'power' ? ['bully_6j', 'bully_6'] : ['bully_6', 'bully_6j'];
        alts = lamb ? ['tripe_12', 'variety_10'] : ['collagen_12', 'variety_15'];
      } else { // daily habit
        if (lamb || p === 'gentle') hero = s === 'large' ? ['collagen_12', 'collagen_6'] : ['collagen_6', 'tripe_6'];
        else hero = s === 'large' ? ['bully_12', 'bully_6'] : s === 'small' ? ['bully_6', 'bully_thin'] : ['bully_6', 'bully_6j'];
        alts = nb ? [s === 'large' ? 'tripe_12' : 'tripe_6', 'variety_10'] : ['tripe_6', 'variety_15'];
      }
      if (s === 'small' && !lamb && g !== 'training') {
        hero = p === 'gentle' ? ['bully_thin', 'bully_4'] : ['bully_4', 'bully_thin'];
        alts = ['bully_4', 'bully_thin'];
      }
      function filt(keys) { return keys.filter(eligible); }
      hero = filt(hero); alts = filt(alts);
      var h = firstSlot(hero) || firstSlot(alts) || firstSlot(filt(Object.keys(slots)));
      var seen = h ? [h.p.handle] : [];
      var list = [];
      alts.concat(hero).forEach(function (k) {
        if (eligible(k) && seen.indexOf(slots[k].handle) === -1 && list.length < 2) {
          seen.push(slots[k].handle); list.push({ k: k, p: slots[k] });
        }
      });
      return { hero: h, alts: list };
    }
    // show() is only ever reached from a click or an Enter press — it is never
    // called at init — so moving focus here can never steal it on page load.
    function show(step) {
      cur = step;
      var panes = root.querySelectorAll('[data-fmq-step]');
      var live = null;
      for (var i = 0; i < panes.length; i++) {
        var on = panes[i].getAttribute('data-fmq-step') === String(step);
        panes[i].hidden = !on;
        if (on) live = panes[i];
      }
      var answered = step === 'load' || step === 'empty' || step === 5 ? 4 : (typeof step === 'number' ? Math.max(0, step - 1) : 0);
      var paws = root.querySelectorAll('.fmq-prog svg');
      for (var j = 0; j < paws.length; j++) paws[j].classList.toggle('fmq-on', j < answered);
      var back = root.querySelector('.fmq-back');
      if (back) back.hidden = !(step === 1 || step === 2 || step === 3 || step === 4);
      // Answering hid the pane the focused button lived in, which drops focus to
      // <body> — a keyboard user had to Tab from the top of the page for every
      // one of the five questions. Move focus onto the new pane instead. It is
      // made programmatically focusable here rather than in the Liquid so the two
      // cannot get out of step, and focusing the pane makes a screen reader read
      // it from its <h2 class="fmq-q"> down. Deliberately NOT a live region: with
      // both, every question would be announced twice.
      if (!live) return;
      if (!live.hasAttribute('tabindex')) live.setAttribute('tabindex', '-1');
      if (live.focus) live.focus({ preventScroll: true });
    }
    function reasonChips() {
      var wrap = root.querySelector('.fmq-reasons');
      if (!wrap) return;
      wrap.textContent = '';
      var keys = ['size_' + ans.size, 'power_' + ans.power];
      if (ans.protein === 'nobeef') keys.push('protein_nobeef');
      if (ans.protein === 'unsure') keys.push('protein_unsure');
      keys.push('goal_' + ans.goal);
      keys.forEach(function (k) {
        if (!copy[k]) return;
        var chip = document.createElement('span');
        chip.className = 'fmq-reason';
        chip.textContent = copy[k];
        wrap.appendChild(chip);
      });
    }
    // Every slot is fed price_min, so a multi-pack product needs the "from" prefix or it
    // reads as a flat price the pack selector immediately contradicts. But five of the
    // twelve products the quiz can recommend have exactly ONE price, and "from $32.99"
    // on those implies a cheaper option that does not exist. The blob carries `multi`
    // per slot; a slot that does not carry it keeps the prefix, which is the safe half.
    function fmqPrice(p) {
      var pfx = root.getAttribute('data-fmq-from') || 'from';
      return (p.multi === false ? '' : pfx + ' ') + p.price;
    }
    function renderResult() {
      var plan = planFor();
      if (!plan.hero) {
        var message = root.querySelector('.fmq-empty-copy');
        if (message) {
          var reason = ans.size === 'small' && ans.protein !== 'any' ? 'lamb' : ans.goal === 'training' ? 'training' : 'general';
          message.textContent = message.getAttribute('data-fmq-empty-' + reason);
        }
        show('empty');
        fpAnnounce('No suitable match. ' + (message ? message.textContent : 'Please review your answers.'));
        return;
      }
      var hero = root.querySelector('.fmq-hero');
      hero.querySelector('.fmq-hero-img').src = plan.hero.p.img || '';
      hero.querySelector('.fmq-hero-title').textContent = plan.hero.p.title;
      hero.querySelector('.fmq-hero-price').textContent = fmqPrice(plan.hero.p);
      hero.querySelector('.fmq-hero-cta').href = plan.hero.p.url;
      var cards = root.querySelectorAll('.fmq-alt');
      for (var i = 0; i < cards.length; i++) {
        var a = plan.alts[i];
        cards[i].hidden = !a;
        if (a) {
          cards[i].href = a.p.url;
          cards[i].querySelector('img').src = a.p.img || '';
          cards[i].querySelector('.fmq-alt-t').textContent = a.p.title;
          // Same rule as the hero card, from the same helper — see fmqPrice().
          cards[i].querySelector('.fmq-alt-p').textContent = fmqPrice(a.p);
        }
      }
      var altsLabel = root.querySelector('.fmq-alts-label');
      if (altsLabel) altsLabel.hidden = plan.alts.length === 0;
      var altsWrap = root.querySelector('.fmq-alts');
      if (altsWrap) altsWrap.hidden = plan.alts.length === 0;
      var note = root.querySelector('.fmq-result-note');
      if (note) note.hidden = !(ans.goal === 'sample' && plan.hero.p.handle !== 'variety' && plan.hero.p.handle !== 'lamb-collagen-tripe-bully-stick-variety-pack-15-count');
      reasonChips();
      show(5);
      // The result pane is long; the one thing the shopper waited for is the name
      // of the chew. Announce it on top of the focus move.
      fpAnnounce('Your match: ' + (plan.hero.p.title || '') + '.');
      playBark(400);
      try { celebrate(hero, 'joinpack'); } catch (e) { /* confetti is optional */ }
    }
    function startLoad() {
      show('load');
      window.setTimeout(renderResult, 1400);
    }
    root.addEventListener('click', function (ev) {
      var el = ev.target instanceof Element ? ev.target.closest('[data-fmq]') : null;
      if (!el) return;
      var kv = el.getAttribute('data-fmq').split(':');
      switch (kv[0]) {
        case 'name': {
          var input = document.getElementById('fmqName');
          name = kv[1] === 'skip' ? '' : ((input && input.value) || '').replace(/\s+/g, ' ').trim().slice(0, 24);
          applyName();
          show(1);
          return;
        }
        case 'size': ans.size = kv[1]; show(2); return;
        case 'power': ans.power = kv[1]; show(3); return;
        case 'protein': ans.protein = kv[1]; show(4); return;
        case 'goal': ans.goal = kv[1]; startLoad(); return;
        case 'back': show(cur === 1 ? 0 : cur - 1); return;
        case 'retake': ans = { size: '', power: '', protein: '', goal: '' }; show(1); return;
      }
    });
    var nameInput = document.getElementById('fmqName');
    if (nameInput) nameInput.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter') { ev.preventDefault(); name = (nameInput.value || '').replace(/\s+/g, ' ').trim().slice(0, 24); applyName(); show(1); }
    });
    applyName();
  }

  // ---------- first-party reviews (PDP) — REMOVED 2026-09-07 ----------
  // initFpReviews() lived here (9,117 B): the write-a-review form driver for the
  // retired `fp` engine, posting to a Supabase endpoint that was closed on
  // 2026-09-01. Reviews are collected by Klaviyo Reviews (engine `klaviyo`, a theme
  // app block per product template) and the `loox` branch is the only fallback the
  // schema still offers, so the form it drove no longer renders anywhere. The
  // ?rv=N#fp-reviews email deep link it honoured is gone with it — review requests
  // are Klaviyo's now, and their links land on Klaviyo's own list.

  // ---------- regional access gate — REMOVED 2026-09-07 ----------
  // snippets/fp-geo-gate.liquid used to publish window.__fpGeo and armMedia()
  // below waited on it. The snippet is deleted (its setting had already left the
  // schema, so it rendered nothing and this branch failed open on every load —
  // measured __fpGeo undefined 44/44). Media boots directly now.

  // ---------- deferred images (img[data-fpsrc]) ----------
  // Lifted out of the inline IIFE in sections/main-product.liquid, which is why
  // the deferral only ever worked on a product page: nothing on the homepage
  // promoted a deferred frame, so a section that adopted data-fpsrc there would
  // have shipped images that never load. Idempotent and page-agnostic.
  //
  // 2026-09-07: data-fpsrc means "promote ALL of these on the first interaction or
  // at idle" and is now carried by the hero's third slide only. The PDP gallery
  // frames moved to data-fpdef (fpPromoteFrame below), promoted ONE AT A TIME on
  // thumbnail intent, because this wholesale promoter cost every product view its
  // whole gallery: measured 2026-09-06 on /products/6-bully-sticks at 412px, all
  // eight remaining frames (994 KiB; PT3 225, PT7 261 KiB) were requested 13 ms
  // after `load` at the 960w rung for a 585-device-px slot, on a phone that had
  // tapped nothing. The variety pack was 1,190 KiB.
  function fpPromoteGallery() {
    Array.prototype.slice.call(document.querySelectorAll('img[data-fpsrc]')).forEach(function (im) {
      // FP40: hero slide 3 carries a phone crop on a <source> beside it. A <source>
      // with no srcset is skipped by the browser, so fill it in BEFORE the <img> gets
      // its src — the selection that src triggers then sees the crop.
      var pic = im.parentNode;
      if (pic && pic.nodeName === 'PICTURE') {
        Array.prototype.slice.call(pic.querySelectorAll('source[data-fpsrcset]')).forEach(function (so) {
          so.setAttribute('srcset', so.getAttribute('data-fpsrcset'));
          so.removeAttribute('data-fpsrcset');
        });
      }
      var ss = im.getAttribute('data-fpsrcset');
      if (ss) { im.setAttribute('srcset', ss); im.removeAttribute('data-fpsrcset'); }
      im.setAttribute('src', im.getAttribute('data-fpsrc'));
      im.removeAttribute('data-fpsrc');
    });
    document.removeEventListener('pointerdown', fpPromoteGallery, true);
    document.removeEventListener('keydown', fpPromoteGallery, true);
    var dots = document.getElementById('fpHeroDots');
    if (dots) {
      dots.removeEventListener('pointerenter', fpPromoteGallery);
      dots.removeEventListener('focusin', fpPromoteGallery);
    }
  }
  function fpInitDeferredImages() {
    // The capture-phase listeners are the mechanism: the first touch or key
    // anywhere on the page promotes, so an engaged shopper never waits.
    document.addEventListener('pointerdown', fpPromoteGallery, true);
    document.addEventListener('keydown', fpPromoteGallery, true);
    // FP40 review fix 2026-10-08 (L1): intent BEFORE the press. A dot's pointerdown is
    // ~100 ms ahead of its click, nowhere near a 245 KB (phone crop) to 1.1 MB (3509w)
    // download, so the crossfade used to land on an empty box. A mouse reaching the dots
    // or focus arriving on them buys the hover/Tab time; heroTouchStart covers a swipe.
    var dots = document.getElementById('fpHeroDots');
    if (dots) {
      dots.addEventListener('pointerenter', fpPromoteGallery);
      dots.addEventListener('focusin', fpPromoteGallery);
    }
    // The idle pre-warm is a convenience, and only where the bytes are cheap —
    // unconditionally it cost a phone 402 KB of frames nobody looked at.
    function preWarm() {
      if (fpSaveData()) return;
      // FP40 review fix 2026-10-08 (L1): a still hero's slide 3 is pre-warmed too. Without
      // it no visitor who taps a dot cold ever sees a ready slide (FP40 had skipped this
      // to save a picture most never open). Kept cheap: only after `load`, only at idle,
      // never on Data Saver / 2g (fpSaveData), and fetchpriority=low, so it queues behind
      // anything the page itself still wants.
      var deferred = Array.prototype.slice.call(document.querySelectorAll('img[data-fpsrc]'));
      var still = deferred.length && deferred.every(function (im) { return im.closest && im.closest('[data-fp-autorotate="false"]'); });
      var go = still ? function () {
        Array.prototype.slice.call(document.querySelectorAll('img[data-fpsrc]')).forEach(function (im) {
          im.setAttribute('fetchpriority', 'low');
        });
        fpPromoteGallery();
      } : fpPromoteGallery;
      if (window.requestIdleCallback) window.requestIdleCallback(go, { timeout: 3000 });
      else window.setTimeout(go, 300);
    }
    if (document.readyState === 'complete') preWarm();
    else window.addEventListener('load', preWarm);
  }
  // Published so sections/main-product.liquid can drop its private copy without a
  // window where neither exists.
  window.fpPromoteGallery = fpPromoteGallery;

  // One deferred PDP frame (img[data-fpdef]), by element or by gallery index.
  // Idempotent: a promoted frame has no data-fpdef left to read. The frame is
  // found through the same binding the runtime shows it with (data-fp-s
  // "opacity:g.m<N>"), never by position, so a re-ordered gallery cannot promote
  // the wrong picture.
  function fpPromoteFrame(target) {
    var im = target;
    if (typeof target === 'number') im = document.querySelector('img[data-fp-s="opacity:g.m' + target + '"]');
    if (!im || !im.getAttribute || !im.hasAttribute('data-fpdef')) return;
    var ss = im.getAttribute('data-fpdefset');
    if (ss) { im.setAttribute('srcset', ss); im.removeAttribute('data-fpdefset'); }
    im.setAttribute('src', im.getAttribute('data-fpdef'));
    im.removeAttribute('data-fpdef');
  }
  window.fpPromoteFrame = fpPromoteFrame;

  // Published for snippets/fp-first-order-modal.liquid, which runs in its own IIFE
  // and cannot see this module's scope. It is a BRIDGE, not a second implementation:
  // the modal must share this `barkAudio` object and this anti-spam clock, or the
  // two surfaces build two Audio objects for the same 33 KB clip and can bark over
  // each other. Callers pass their own minimum gap; see playBark.
  window.fpBark = playBark;

  // Published for sections/join-widget.liquid. The widget used to collect the email itself
  // and POST it to https://a.klaviyo.com/client/subscriptions/ from the browser. That request
  // carries NO form fingerprint, so Klaviyo's Bot Protection classified real shoppers as
  // scripted and globally suppressed 18 consenting customers between 24 and 28 Aug 2026 -
  // every one with a Shopify consent record written within 0-3 seconds of the suppression.
  // Nothing can be added to that request to fix it: /client/subscriptions/ has no form field.
  // So the bubble now opens the NATIVE Klaviyo form, which carries the fingerprint by
  // construction. openForm also overrides the form's own dismissal and 30-day frequency
  // (measured on production 2026-08-29) - which is the entire point of this control: it
  // exists for the shopper who closed the popup and wants the code back.
  var fpJwPending = null;
  // The Klaviyo form id comes from Theme settings → Welcome signup form id. It was a literal
  // ('WJtGmd') until 2026-10-19: when that form was retired for the 30% one, the widget kept
  // asking Klaviyo for a draft form and every click ended in "couldn't open".
  var fpJwFormId = (S && S.welcomeFormId) || 'SLzfgS';
  window.fpJwOpenForm = function (onUnavailable, onOpened) {
    if (fpJwPending) return;
    var tries = 0, timer = null, sent = false;
    function finish(opened, cancelled) {
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener('klaviyoForms', onForm);
      fpJwPending = null;
      if (cancelled) return;
      var cb = opened ? onOpened : onUnavailable;
      if (typeof cb === 'function') cb();
    }
    function onForm(event) {
      var detail = event.detail;
      if (detail && detail.formId === fpJwFormId && detail.type === 'open') finish(true, false);
    }
    fpJwPending = { cancel: function () { finish(false, true); } };
    window.addEventListener('klaviyoForms', onForm);
    (function attempt() {
      timer = null;
      var k = window.klaviyo;
      if (!sent && k && typeof k.openForm === 'function') {
        sent = true;
        tries = 0;
        try { k.openForm(fpJwFormId); } catch (error) { finish(false, false); return; }
        if (!fpJwPending) return;
      }
      if (++tries > (sent ? 80 : 40)) { finish(false, false); return; }
      timer = window.setTimeout(attempt, 100);
    })();
  };

  // ---------- join widget placement (FP40, 2026-10-08) ----------
  // Site review A1/A2/B1/B4/B6/J1. The widget is the one on-page 50% surface; it may
  // not sit on the hero CTA, the Mobile ATC bar or the last lines of the footer. One
  // IntersectionObserver per rule feeds a flag, and the jwBubble/jwMin keys derive
  // what shows, so st.jw (the shopper's own choice) is never overwritten:
  //  - jwHeroOn  phones (<=743px) with a hero: nothing shows while any of the hero is
  //              in view. The bubble used to open at 2.2 s over the hero copy, and from
  //              scrollY 160-220 0 of 15 taps on "SHOP ALL PRODUCTS" reached it (A2).
  //  - jwAtcOn   the PDP's Mobile ATC bar is on screen: only the 52px tab shows (J1).
  //  - jwFootOn  any footer is in view: only the tab shows, so the bubble stops hiding
  //              the address and copyright lines at the end of every page (B6).
  //  - jwBuyOn   FP40 live fix 2026-10-08: a PDP buy block (data-fp-clear, main-product
  //              .liquid) is inside the widget's own corner (jwZoneMargin): nothing shows.
  //              On the published page the 52px tab sat on the quantity "−" (35-55% of it)
  //              and on "5 Pack" (61%) while those scrolled past it, on every phone.
  // An observer, not scroll maths: it reports scrolling, display:none and an off-screen
  // transform alike, and costs nothing between changes. No IntersectionObserver (very
  // old engines) = no flags = the widget behaves as it did before.
  function jwTucked() { return st.jwAtcOn || st.jwFootOn; }
  // FP40 live fix 2026-10-08: the CONTROLS inside the PDP buy blocks, not the blocks. A block's
  // own box runs to its border, past its padding: at 1400px FP's resting cell came within 4px of
  // the purchase box's right border (1258 vs 1262) while every price and button inside ended at
  // 1242, and the block alone kept FP hidden, and his walk-in waiting, at the top of every PDP.
  var FP_BUY_CONTROLS = '[data-fp-clear] button, [data-fp-clear] label, [data-fp-clear] summary, [data-fp-clear] a, [data-fp-clear] [role="spinbutton"]';
  // A widget that holds focus stays: unmounting it would drop a keyboard shopper's focus
  // on <body>. It goes once focus leaves it (the focusout listener in fpJwPlacement).
  function jwClearOfBuy() {
    if (!st.jwBuyOn) return true;
    var a = document.activeElement;
    return !!(a && a.closest && a.closest('[data-fp-jw-tab], [data-screen-label="Join Widget"]'));
  }
  // One observer per selector; onChange(on) runs when "any match intersects" flips.
  // FP40 review fix 2026-10-08 (L5): returns a handle (null when nothing matches) so a
  // theme-editor section reload can drop it, and `skip` leaves out the matches inside a
  // section that is being unloaded. `dead` drops a callback that was already queued.
  // FP40 review fix 2026-10-08 (short pages): `margin`, when given, is the observer's
  // rootMargin (fpFootMargin below); without it the whole viewport counts, as before.
  // FP41 (2026-10-19): `sel` may also be a function returning the elements (fpSceneControls: the
  // footer scene's buttons live in its shadow root, out of a selector's reach). An element in a
  // shadow root belongs to its host's section when a section unloads.
  function fpWatchVisible(sel, onChange, skip, margin) {
    var found = typeof sel === 'function' ? sel() : document.querySelectorAll(sel);
    var els = Array.prototype.slice.call(found).filter(function (el) {
      var root = el.getRootNode ? el.getRootNode() : null;
      var node = root && root !== document && root.host ? root.host : el;
      return !(skip && skip.contains && skip.contains(node));
    });
    if (!els.length) return null;
    var vis = els.map(function () { return false; });
    var was = null, dead = false;
    var io = new IntersectionObserver(function (entries) {
      if (dead) return;
      entries.forEach(function (en) {
        var i = els.indexOf(en.target);
        if (i !== -1) vis[i] = en.isIntersecting;
      });
      var on = vis.indexOf(true) !== -1;
      if (on !== was) { was = on; onChange(on); }
    }, margin ? { rootMargin: margin } : undefined);
    els.forEach(function (el) { io.observe(el); });
    return { stop: function () { dead = true; io.disconnect(); } };
  }
  // FP40 review fix 2026-10-08 (L5): these watches used to observe the nodes present at
  // boot, forever. A footer save in the theme editor replaces the section, so the old
  // nodes left and the new ones were never observed: data-fp-foot and jwFootOn stuck at
  // their last value (FP hidden mid-page, the bubble tucked). Every watch is kept here.
  // shopify:section:unload of a section holding a match re-watches what lies outside it;
  // shopify:section:load of one re-watches the page as it now is. A new observer reports
  // its first state whatever it is (was = null); no match at all = off, said here,
  // because no observer is left to say it.
  var fpLiveWatches = [];
  function fpWatchLive(sel, onChange, margin, within) {
    // `within` (FP41): for a function `sel`, the selector a reloading section must hold to matter.
    var w = { sel: sel, onChange: onChange, margin: margin, within: within, m: margin ? margin() : undefined };
    // m null = the sprite is walking in (fpCornerMargin): no watch until fpRemargin, at the
    // end of the walk, measures him at home and builds it.
    w.h = w.m === null ? null : fpWatchVisible(sel, onChange, null, w.m);
    if (!w.h) onChange(false);
    fpLiveWatches.push(w);
  }
  function fpRewatch(section, leaving) {
    fpLiveWatches.forEach(function (w) {
      var probe = typeof w.sel === 'function' ? w.within : w.sel;
      var holds = !section || !section.querySelector || !!(probe && section.querySelector(probe))
        || !!(probe && section.matches && section.matches(probe));
      if (!holds) return;
      if (w.h) w.h.stop();
      if (w.margin) w.m = w.margin();
      w.h = w.m === null ? null : fpWatchVisible(w.sel, w.onChange, leaving ? section : null, w.m);
      if (!w.h) w.onChange(false);
    });
  }
  // FP40 review fix 2026-10-08 (short pages): the footer end zone counts only where FP can
  // cover it, not anywhere on screen. On short pages (empty /cart, 404) at 2560x1440 the
  // zone never left the screen, so FP never showed, yet it stood clear of every word
  // (zone x 660-1900, FP x 2314-2544). A top-only band would not have done it: the zone
  // sat at y 1176-1410, inside FP's own rows. Measured, not assumed: FP v2 is the
  // [data-fp20] box plus any control overflowing it (Reset, left of a 120px box), the
  // sprite its .fp-mascot__sway cell (the .fp-mascot lane itself is full width); 8px of
  // air all round. Docked: from that box's top-left to the viewport's bottom-right, so a
  // CSS-only move down or right (the phone join-bubble lift ending, a --pageBtm) is still
  // inside. Pinned or carried: the box itself; only a drag or a resize moves it, and both
  // are re-measured. No FP, or no box yet (FP v2 before its script shows it) = the whole
  // viewport, as before.
  // FP40 live fix 2026-10-08: not while the sprite walks in (.is-arriving, fp-mascot.js). His
  // lane slides in from up to --fp-enter left of home, and a corner measured 150 ms into that
  // walk reached across the buy column of a 1440-1600px product page: the flag rose, he
  // vanished mid-walk and came back at rest (UI review, every load). Mid-walk = null:
  // fpRemargin keeps the corner it had (measured at rest, before the walk), and a watch that
  // has none yet waits (fpWatchLive, fpRewatch). That happens when fp-mascot.js, which runs
  // first, starts the walk before this file boots (a cached sheet; Chromium, 3 of 5 repeat
  // loads in the logic review): measuring his first walk frame then froze that corner for
  // the whole walk. The class change that ends the walk measures him at home.
  // `own` (FP40 live fix 2026-10-08, the buy controls' watch): FP's own box and 8px, never the
  // reach to the viewport's bottom-right. The buy controls are page content, not a zone at the
  // end of the page, and with that reach the ADD TO CART below a 1400px FP hid him though
  // nothing of his sat on it (his cell ended at y 789, the button began at 860).
  // FP41 (2026-10-19): the footer farm scene's Sound and Pause (snippets/fp-farm-scene.liquid).
  // They sit top-right in the scene, in FP's column, and scrolling to the footer slid them under
  // FP's own Sound / Pause: a click meant for the scene toggled FP (UI review, Chromium 1440).
  // FP steps aside while one is inside his own box, as for the PDP buy controls. While the
  // scene's controls are hidden (art still loading, Reduce Motion) they have no box and never count.
  function fpSceneControls() {
    var out = [];
    Array.prototype.forEach.call(document.querySelectorAll('fp-farm-scene'), function (h) {
      if (h.shadowRoot) out = out.concat(Array.prototype.slice.call(h.shadowRoot.querySelectorAll('.ctl button')));
    });
    return out;
  }
  // fp-farm-scene.js is a deferred script of its own and may define the element after this file
  // boots: until then the host has no shadow root and the watch found nothing. Watch again.
  function fpSceneRewatch() {
    fpLiveWatches.forEach(function (w) {
      if (w.sel !== fpSceneControls || w.m === null) return;
      if (w.h) w.h.stop();
      w.h = fpWatchVisible(w.sel, w.onChange, null, w.m);
      if (!w.h) w.onChange(false);
    });
  }
  function fpFootMargin() { return fpCornerMargin(false); }
  function fpBuyMargin() { return fpCornerMargin(true); }
  // FP's box: FP v2's [data-fp20] plus any control overflowing it, or the sprite's cell. null
  // when there is no FP or he has no box.
  function fpFpRect() {
    var v2 = document.querySelector('[data-fp20]'), sp = document.querySelector('[data-fp-mascot]');
    var parts = v2 ? [v2].concat(Array.prototype.slice.call(v2.querySelectorAll('.fp20-companion-controls > button')))
      : sp ? [sp.querySelector('.fp-mascot__sway')] : [];
    var r = null;
    parts.forEach(function (el) {
      if (!el || !el.getClientRects().length) return;
      var b = el.getBoundingClientRect();
      if (!(b.width > 0 && b.height > 0)) return;
      r = r ? { top: Math.min(r.top, b.top), left: Math.min(r.left, b.left), bottom: Math.max(r.bottom, b.bottom), right: Math.max(r.right, b.right) }
        : { top: b.top, left: b.left, bottom: b.bottom, right: b.right };
    });
    return r;
  }
  function fpCornerMargin(own) {
    var v2 = document.querySelector('[data-fp20]'), sp = document.querySelector('[data-fp-mascot]');
    if (!v2 && sp && sp.classList.contains('is-arriving')) return null;
    var r = fpFpRect();
    var vw = document.documentElement.clientWidth || window.innerWidth, vh = window.innerHeight;
    if (!r || r.top >= vh || r.left >= vw || r.bottom <= 0 || r.right <= 0) return '0px';
    var cut = function (n) { var v = Math.max(0, Math.floor(n) - 8); return v ? '-' + v + 'px' : '0px'; };
    var pinned = v2 ? v2.getAttribute('data-manual') === 'true' : sp.hasAttribute('data-fp-moved');
    return cut(r.top) + ' ' + (pinned || own ? cut(vw - r.right) + ' ' + cut(vh - r.bottom) : '0px 0px') + ' ' + cut(r.left);
  }
  // FP40 live fix 2026-10-08: the join widget's corner, as fpFootMargin is FP's: from the
  // widget's top-right to the viewport's bottom-left, 8px of air. Measured on the bubble or
  // the tab when one is mounted (fpFootFollow re-measures on every mount); while neither is
  // (hidden by these very flags), the last box seen, so hiding cannot shrink the zone and
  // bring the widget straight back; kept as its distance from the bottom, which it is
  // anchored to, so a rotation or a toolbar resize while it is unmounted moves it along. Before any box: 380px wide and 192px tall from the
  // bottom-left, which holds the tab at 20 + --pageBtm (78px + a 34px home indicator at
  // most) + 52 and the bubble's width, so the first mount is never over a buy control.
  var jwLastBox = null;
  function jwZoneMargin() {
    var el = document.querySelector('[data-screen-label="Join Widget"], [data-fp-jw-tab]');
    var b = el && el.getClientRects().length ? el.getBoundingClientRect() : null;
    var vw = document.documentElement.clientWidth || window.innerWidth, vh = window.innerHeight;
    if (b && b.width > 0 && b.height > 0) {
      // Measured 150 ms after a mount, the bubble is still in its fpFadeUp (translateY 26px):
      // its box sat ~12px low. A fixed element's offsetTop is its layout top, transforms
      // aside; a child sticking out above (the bubble's close button) is added on top.
      var top = b.top;
      if (typeof el.offsetTop === 'number' && el.offsetParent === null && getComputedStyle(el).position === 'fixed') top = el.offsetTop;
      var over = 0;
      Array.prototype.forEach.call(el.querySelectorAll('*'), function (c) {
        var cb = c.getBoundingClientRect();
        if (cb.width > 0) over = Math.max(over, b.top - cb.top);
      });
      jwLastBox = { up: vh - (top - over), right: b.right };
    }
    var r = jwLastBox ? { top: vh - jwLastBox.up, right: jwLastBox.right } : { top: vh - 192, right: 380 };
    var cut = function (n) { var v = Math.max(0, Math.floor(n) - 8); return v ? '-' + v + 'px' : '0px'; };
    return cut(r.top) + ' ' + cut(vw - r.right) + ' 0px 0px';
  }
  // Re-measured on resize, on load and whenever FP is shown, pinned, dragged, carried, let
  // go or ends its walk-in (fpFootFollow, 150 ms after the last change, so a drag is
  // measured where it stops); a watch is rebuilt only when its margin moved, and the new
  // observer reports its first state (was = null), so the flag follows at once.
  function fpRemargin() {
    fpLiveWatches.forEach(function (w) {
      if (!w.margin) return;
      var m = w.margin();
      if (m === null || m === w.m) return;
      w.m = m;
      if (w.h) w.h.stop();
      w.h = fpWatchVisible(w.sel, w.onChange, null, m);
      if (!w.h) w.onChange(false);
    });
  }
  var fpRemarginTimer = 0, fpFootMo = null;
  function fpRemarginSoon() {
    window.clearTimeout(fpRemarginTimer);
    fpRemarginTimer = window.setTimeout(fpRemargin, 150);
  }
  // FP40 live fix 2026-10-08: a buy control mounted after boot (the Subscribe panel is a
  // data-fp-if template: its frequency summary, options and policy link arrive when the shopper
  // picks it) was never observed. An element added to or removed from a [data-fp-clear] block
  // rebuilds the FP_BUY_CONTROLS watches on the page as it now is; text-only renders (prices,
  // labels) add no element and rebuild nothing. A watch still waiting on a walk-in is left to
  // fpRemargin, which queries afresh.
  var fpBuyTimer = 0, fpBuyMo = null;
  function fpBuyRewatch() {
    fpLiveWatches.forEach(function (w) {
      if (w.sel !== FP_BUY_CONTROLS || w.m === null) return;
      if (w.h) w.h.stop();
      w.h = fpWatchVisible(w.sel, w.onChange, null, w.m);
      if (!w.h) w.onChange(false);
    });
  }
  function fpBuyChanged(records) {
    var el = records.some(function (r) {
      return [].some.call(r.addedNodes || [], function (n) { return n.nodeType === 1; })
        || [].some.call(r.removedNodes || [], function (n) { return n.nodeType === 1; });
    });
    if (!el) return;
    window.clearTimeout(fpBuyTimer);
    fpBuyTimer = window.setTimeout(fpBuyRewatch, 50);
  }
  function fpFootFollow() {
    if (fpFootMo) fpFootMo.disconnect();
    if (!window.MutationObserver) return;
    fpFootMo = new MutationObserver(fpRemarginSoon);
    var v2 = document.querySelector('[data-fp20]'), sp = document.querySelector('[data-fp-mascot]');
    if (v2) fpFootMo.observe(v2, { attributes: true, attributeFilter: ['hidden', 'data-manual', 'style'] });
    if (sp) fpFootMo.observe(sp, { attributes: true, attributeFilter: ['data-fp-moved', 'class', 'style'] });
    // The join widget mounts and unmounts beside its templates (data-fp-if): re-measure it.
    var jw = document.querySelector('template[data-fp-if="jwBubble"]');
    if (jw && jw.parentNode) fpFootMo.observe(jw.parentNode, { childList: true });
    if (fpBuyMo) fpBuyMo.disconnect();
    fpBuyMo = new MutationObserver(fpBuyChanged);
    Array.prototype.forEach.call(document.querySelectorAll('[data-fp-clear]'), function (b) {
      fpBuyMo.observe(b, { childList: true, subtree: true });
    });
  }
  function fpJwPlacement() {
    if (!('IntersectionObserver' in window)) return;
    document.addEventListener('shopify:section:unload', function (ev) { fpRewatch(ev && ev.target, true); });
    document.addEventListener('shopify:section:load', function (ev) {
      fpRewatch(ev && ev.target, false);
      fpFootFollow(); fpRemarginSoon(); // a reloaded FP section is a new FP element
    });
    // FP40 review fix 2026-10-08 (U1): html[data-fp-foot="1"] while the footer's legal /
    // identity end zone is on screen. sections/footer.liquid tags that zone
    // data-fp-foot-end (home: legal links + identity line + #fpFooterLegal; the other
    // footers: the identity block and the copyright/safety row), and the FP stylesheets
    // hide FP while the flag is set (at every width since the second FP40 review fix of
    // 2026-10-08; it was phones only), so FP never sits on those lines. Wired
    // before the join-widget check below: FP renders whether or not the widget does.
    // Removed when off, never set to "0", so [data-fp-foot] alone is a valid test.
    // FP40 review fix 2026-10-08 (short pages): only inside FP's corner (fpFootMargin).
    // FP40 live fix 2026-10-08: and while a PDP buy control (FP_BUY_CONTROLS: pack sizes,
    // purchase options, quantity + ADD TO CART) is inside FP's own box (fpBuyMargin). On the
    // published page the corner sprite covered up to 53% of ADD TO CART, 98% of "25 Pack" and
    // the one-time price on every phone (WebKit, 375-430px), and nothing else hid it there.
    // Two watches, one flag: set while either holds.
    var fpFootOn = false, fpBuyOn = false, fpFocusOn = false, fpSceneOn = false;
    var fpFootFlag = function () {
      var de = document.documentElement;
      if (fpFootOn || fpBuyOn || fpFocusOn || fpSceneOn) de.setAttribute('data-fp-foot', '1'); else de.removeAttribute('data-fp-foot');
    };
    fpWatchLive('[data-fp-foot-end]', function (on) { fpFootOn = on; fpFootFlag(); }, fpFootMargin);
    fpWatchLive(FP_BUY_CONTROLS, function (on) { fpBuyOn = on; fpFootFlag(); }, fpBuyMargin);
    // FP41: the footer scene's controls, inside FP's own box (fpSceneControls).
    fpWatchLive(fpSceneControls, function (on) { fpSceneOn = on; fpFootFlag(); }, fpBuyMargin, 'fp-farm-scene');
    if (window.customElements && window.customElements.whenDefined && document.querySelector('fp-farm-scene')) {
      window.customElements.whenDefined('fp-farm-scene').then(fpSceneRewatch);
    }
    // Follow-up 2026-10-08 (WCAG 2.2 SC 2.4.11, Focus Not Obscured): a control that takes
    // keyboard focus under FP or under the join widget hides that widget until focus moves on.
    // Measured with the live fix in Chromium at 375px: Tab left the footer's Instagram link 83%
    // under the join tab, "Commonly Asked Questions" 27% and "Contact Us" 31% under FP, and
    // 7-19 focused elements per 70 Tabs under one of them. Keyboard focus only (:focus-visible),
    // so a tap or a click never makes a widget vanish; a widget that holds focus itself is left
    // alone (FP v2's own controls have their data-key-focus reveal). Checked at every focus move
    // and once focus has left; the widget's box is FP's own or the join widget's (its last box
    // while it is unmounted), with 4px of air.
    var fpKeyFocus = function (el) {
      try { return el.matches(':focus-visible'); } catch (e) { return true; }
    };
    var fpUnder = function (r, b) {
      return !!b && r.right > b.left - 4 && r.left < b.right + 4 && r.bottom > b.top - 4 && r.top < b.bottom + 4;
    };
    var jwRect = function () {
      var el = document.querySelector('[data-screen-label="Join Widget"], [data-fp-jw-tab]');
      var b = el && el.getClientRects().length ? el.getBoundingClientRect() : null;
      var vh = window.innerHeight;
      // Kept, as jwZoneMargin keeps it: the widget this hides is unmounted at the next check.
      if (b && b.width > 0 && b.height > 0) { jwLastBox = { up: vh - b.top, right: b.right }; return b; }
      return jwLastBox ? { left: 0, top: vh - jwLastBox.up, right: jwLastBox.right, bottom: vh } : null;
    };
    var fpFocusCheck = function () {
      var a = document.activeElement, onFp = false, onJw = false;
      // FP41: focus inside a shadow root (the footer scene's Sound / Pause) reports its host here,
      // which is :focus but never :focus-visible. Follow it down to the real control.
      while (a && a.shadowRoot && a.shadowRoot.activeElement) a = a.shadowRoot.activeElement;
      if (a && a !== document.body && a.getBoundingClientRect && a.closest
          && !a.closest('[data-fp20], [data-fp-mascot], [data-fp-jw-tab], [data-screen-label="Join Widget"], [data-fp-welcome-status]')
          && fpKeyFocus(a)) {
        var r = a.getBoundingClientRect();
        if (r.width > 0 || r.height > 0) { onFp = fpUnder(r, fpFpRect()); onJw = fpUnder(r, jwRect()); }
      }
      if (onFp !== fpFocusOn) { fpFocusOn = onFp; fpFootFlag(); }
      if (onJw !== st.jwFocusOn) patch({ jwFocusOn: onJw });
    };
    // Again on the next frame and while the page scrolls with something focused: the engine
    // scrolls a focused control into view after focusin, which is where it met a widget (5 of
    // the 8 still covered with a focusin-only check, Chromium 375 and 1440).
    var fpFocusRaf = 0;
    var fpFocusSoon = function () {
      if (fpFocusRaf) return;
      fpFocusRaf = window.requestAnimationFrame(function () { fpFocusRaf = 0; fpFocusCheck(); });
    };
    document.addEventListener('focusin', function () { fpFocusCheck(); fpFocusSoon(); });
    document.addEventListener('focusout', function () { window.setTimeout(fpFocusCheck, 0); });
    window.addEventListener('scroll', function () {
      if (document.activeElement && document.activeElement !== document.body) fpFocusSoon();
    }, { passive: true });
    fpFootFollow();
    window.addEventListener('resize', fpRemarginSoon);
    window.addEventListener('load', fpRemarginSoon);
    if (!document.querySelector('template[data-fp-if="jwBubble"]')) return;
    var phone = window.matchMedia ? window.matchMedia('(max-width: 743px)') : null;
    var hero = document.querySelector('[data-screen-label="Hero"]');
    if (hero) {
      // Counted as in view until the first callback, which lands within a frame of
      // observe() — far ahead of the bubble's 2.2 s timer, so nothing can flash.
      var heroVisible = true;
      var syncHero = function () {
        var on = heroVisible && !!(phone && phone.matches);
        if (on !== st.jwHeroOn) patch({ jwHeroOn: on });
      };
      syncHero();
      new IntersectionObserver(function (entries) {
        heroVisible = entries[entries.length - 1].isIntersecting;
        syncHero();
      }).observe(hero);
      if (phone && phone.addEventListener) phone.addEventListener('change', syncHero);
      else if (phone && phone.addListener) phone.addListener(syncHero);
    }
    function flagWhileVisible(sel, key, margin) {
      fpWatchLive(sel, function (on) {
        if (on !== st[key]) { var p = {}; p[key] = on; patch(p); }
      }, margin);
    }
    // The bar is display:none above 743px (main-product.liquid), which the observer
    // reports as not intersecting — no width check needed here.
    flagWhileVisible('[data-screen-label="Mobile ATC"]', 'jwAtcOn');
    flagWhileVisible('[data-screen-label="Footer"]', 'jwFootOn');
    flagWhileVisible(FP_BUY_CONTROLS, 'jwBuyOn', jwZoneMargin);
    document.addEventListener('focusout', function (e) {
      var t = e.target;
      if (!st.jwBuyOn || !t || !t.closest || !t.closest('[data-fp-jw-tab], [data-screen-label="Join Widget"]')) return;
      // activeElement is the new target only after focusout; a move inside the widget keeps it.
      window.setTimeout(function () { if (st.jwBuyOn) patch({}); }, 0);
    });
  }

  // ---------- boot ----------
  function boot() {
    // FP40: no fp:entrance-release listener any more — the hero never waits on FP (A5).
    scan(document.body);
    // Not on a page that cannot be in demo mode (DEMO_ABLE, top of file): there the drawer
    // lists the Shopify cart or nothing. No path on those pages writes st.cart (the
    // collection's demo add merges into localStorage itself), so skipping the read cannot
    // overwrite a saved demo cart.
    if (!REAL && DEMO_ABLE) loadDemoCart();

    ['click', 'input', 'change', 'submit', 'keydown', 'touchstart', 'touchend'].forEach(function (evName) {
      var opts = (evName === 'touchstart' || evName === 'touchend') ? { passive: true } : false;
      document.addEventListener(evName, delegated(evName), opts);
    });

    // Create the live region once, at boot, and empty. A node inserted in the
    // same tick as its text is routinely swallowed.
    fpLiveRegion();
    // Same rule, same reason: the failure toast used to create its own node at the
    // moment of the first failure, so that first failure was the one nobody heard.
    fpToastRegion();
    // The Klaviyo cart nudge button (fpCartHeard): adopts the layout's form, before the first cart change.
    fpCartNudgeMount();
    // The drawer ships closed but only translated off-screen, so its seven
    // controls sat in the tab order on every page that renders it. Take them out
    // until it actually opens.
    fpDrawerReachable(!!st.open);

    // One Escape must dismiss ONE thing. The first-order promo is a real modal
    // <dialog>, so an Escape aimed at it also reached this listener and closed the
    // cart drawer underneath. `dialog:modal` and NOT `dialog[open]`: an embedded app
    // can leave a non-modal <dialog open> in the document, which takes no top layer
    // and owns no Escape — an [open] guard would permanently disable this key.
    window.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !st.open) return;
      try { if (document.querySelector('dialog:modal')) return; } catch (err) { /* older engine: no :modal */ }
      closeDrawer();
    });

    // The two header overlays. Escape used to work only while focus was inside the
    // search INPUT (its own data-fp-on), and the mobile menu had no Escape and no
    // trap at all — Tab walked straight out of an aria-modal dialog into the page
    // behind it. Both panels are data-fp-if templates, so the panel is resolved at
    // event time rather than bound once.
    window.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (st.open) return; // the drawer's own listener above owns this Escape
        if (st.searchOpen) closeSearchOverlay();
        else if (st.mnav) setMnav(false);
        return;
      }
      if (e.key !== 'Tab') return;
      // role="dialog" goes on the inner panel for search; the overlay around it is
      // the click-to-dismiss backdrop and must stay outside the trap.
      var root = st.searchOpen ? document.getElementById('fpSearchPanel')
        : (st.mnav ? document.getElementById('fpMnav') : null);
      if (root) fpTrapFor(root)(e);
    });

    // /cart checks out through data-fp-on="click:goCheckout" (main-cart.liquid:449) and
    // begin_checkout for that path is emitted by the cart page module, via fpGoCheckout.
    // A capture-phase submit listener used to sit here claiming to cover a native
    // <form> POST — but the theme's only button[name="checkout"] is inside the
    // <noscript> fallback (main-cart.liquid:525), whose children are raw text with
    // scripting on, so it could never match and never fired. Removed: an inert listener
    // whose comment denies the working hook is how the working hook gets deleted.
    // The <noscript> POST is unreachable with scripting on and is deliberately untracked.

    initMegaMenu();
    initFindMyChew();
    fpInitDeferredImages();
    fpJwPlacement();

    // media boot — the join-widget timer, the bark object, the pack observer and
    // the autoplay kick. Armed unconditionally since the geo gate was removed
    // (2026-09-07); see the note above fpPromoteGallery.
    function armMedia() {
      // join widget timer (design: 2.2s, editable per section)
      var joined = false;
      try { joined = localStorage.getItem('fp-joined-v1') === '1'; } catch (e) { joined = false; }
      window.setTimeout(function () {
        if (!joined && st.jw === 'off') patch({ jw: 'bubble' });
      }, (JW.delaySeconds != null ? JW.delaySeconds : 2.2) * 1000);

      // bark: constructed here so the first tap has an object ready, but NOT
      // fetched — playBark() builds it lazily anyway, and preload='auto' plus an
      // explicit .load() made a 33 KB sound effect the largest resource on pages
      // that have no cart button at all.
      try {
        barkAudio = new Audio(S.barkUrl || '');
        barkAudio.preload = 'none';
        barkAudio.volume = 0.85;
      } catch (e) { barkAudio = null; }

      // pack on-screen observer
      var sect = document.getElementById('dogs');
      if (sect && 'IntersectionObserver' in window) {
        // Every other moving element in this theme honours reduced motion — the
        // marquee, the footer shine, the corner widget, the hero rotator. This
        // observer was the one that was missed: it started looping full-bleed
        // video the moment #dogs was 20% visible, whatever the shopper asked for.
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            packOnScreen = en.isIntersecting;
            // STAGE clips only, and only the one actually on stage. This walked every
            // <video> in the section — four elements, two stage clips plus two
            // thumbnails on the same two URLs — so it streamed 2.2 MB and ran four
            // decode pipelines (two at 1080p) for the single frame anyone can see.
            // The off-stage stage clip sits at opacity:0; the thumbnails are stills.
            Array.prototype.slice.call(sect.querySelectorAll('video[data-fp-stage]')).forEach(function (v) {
              var onStage = v.getAttribute('data-fp-stage') === String(st.packStage);
              // Five independent reasons not to play, and all five must hold to
              // start: on screen, on stage, not explicitly paused (WCAG 2.2.2), no
              // reduced-motion preference, and not on a metered connection — the same
              // signal the deferred-image pre-warm refuses 402 KB of stills on.
              if (en.isIntersecting && onStage && !st.pkPaused && !fpReduceMotion() && !fpSaveData()) {
                if (v.paused) v.play().catch(function () { /* noop */ });
              } else if (!v.paused) v.pause();
            });
            if (!en.isIntersecting && st.pkSnd) muteAllPack();
            if (!en.isIntersecting) packHqDrop();
          });
        }, { threshold: 0.2 }).observe(sect);
      }

      // autoplay kick — pack clips are excluded: they start/stop via the #dogs
      // on-screen observer so offscreen tiles never stream video. The hero video
      // is excluded too: syncHeroVideo() starts it when the rotator reaches its
      // slide, so an invisible 800 KB clip no longer competes with the LCP image.
      window.setTimeout(function () {
        Array.prototype.slice.call(document.querySelectorAll('video')).forEach(function (v) {
          if (v.closest && v.closest('#dogs')) return;
          if (v.id === 'fpHeroVideo' || v.hasAttribute('data-fp20-video')) return;
          if (v.paused) v.play().catch(function () { /* autoplay policy */ });
        });
        syncHeroVideo();
      }, 50);
    }
    armMedia();

    // sound policy guard: hero + thumbnails can never be audible;
    // pack clips only while ON + active clip tile + section on screen.
    // Only where there is something to police: 22 of the 29 rendered pages carry
    // no <video> at all, and this woke the main thread 86 times a minute on each
    // of them, running a whole-document querySelectorAll every tick.
    if (document.querySelector('video')) window.setInterval(function () {
      // Nothing can be audible in a hidden tab, so there is nothing to police.
      if (document.hidden) return;
      var hv = document.getElementById('fpHeroVideo');
      if (hv && !hv.muted) hv.muted = true;
      var act = activeClipIndex();
      [0, 1, 2, 3].forEach(function (ci) {
        var el2 = packVid(ci);
        if (!el2) return;
        var audible = st.pkSnd && ci === act && packOnScreen !== false;
        if (el2.muted === audible) el2.muted = !audible;
      });
      Array.prototype.slice.call(document.querySelectorAll('video[data-vsrc]:not([id])')).forEach(function (tv) {
        if (!tv.muted) tv.muted = true;
      });
    }, 700);

    // #search deep link
    if ((location.hash || '').indexOf('search') !== -1) {
      try { history.replaceState(null, '', location.pathname); } catch (e) { /* noop */ }
      // Through the same door as the magnifier, so the deep link gets the trap and
      // the focus restore too.
      openSearchOverlay();
    }

    startHeroTicker();

    if (REAL) fetchRealCart(null, true);
    // Search, the Find My Chew quiz, 404, contact and all seven policy pages
    // carry no Best Sellers blob and no page module, so real-vs-demo mode was
    // decided by data that page never emits: the cart fell back to the empty
    // localStorage demo cart and the header badge disappeared mid-session. Ask
    // the cart itself instead of inferring it.
    else if (BEST.length === 0) {
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
        // One request, both readers: the collection module keeps its own count and used
        // to GET the identical URL a second time on every load. It publishes PAGE.cart
        // to be fed from here instead.
        if (PAGE.cart) PAGE.cart(c);
        // Recorded even when it is not adopted below: a collection-card add on a cart that was
        // empty at load is a change Klaviyo has to hear about (fpCartHeard).
        fpCartHeard(c, true);
        // Only adopt the live cart when it actually holds something: an empty
        // response must not switch a demo-mode page (products not linked) away
        // from its localStorage cart.
        if (c && Array.isArray(c.items) && c.items.length > 0) { shopifyCart = c; cartLoaded = true; REAL = true; render(); fpExpressCheckCart(); }
      }).catch(function () { /* badge stays hidden rather than wrong */ });
    }
    render();
  }

  // ---------- collection page module ----------
  ;(function () {
    var raw = readData('collection', null);
    if (!raw) return;
    var cards = raw.cards || raw;
    var ctaLabel = raw.cta || 'ADD TO CART';
    var ctaAdded = raw.ctaAdded || 'ADDED \u2713';
    var pst = { filter: 'all', added: {} };
    var liveCount = 0;
    // The FIRST count comes from boot(), not from here. A collection page carries no
    // Best Sellers blob, so boot() already GETs /cart.js in its `BEST.length === 0`
    // branch; this module used to fire an identical, uncacheable GET at IIFE time and
    // every collection load paid for the same number twice. The post-add refresh is the
    // shared fetchRealCart() now (FP47), which also feeds the drawer.
    PAGE.cart = function (c) { liveCount = (c && c.item_count) || 0; };
    var CHIP_ON = { bg: '#3D1409', color: '#FFE9C9', border: '#3D1409' };
    var CHIP_OFF = { bg: 'transparent', color: '#8A5A2E', border: '#E5C9A1' };
    var FKEYS = { fAll: 'all', fSix: 'six', fTwelve: 'twelve', fJumbo: 'jumbo', fValue: 'value' };
    function visible(i) {
      var card = cards[i] || {};
      return pst.filter === 'all' || String(card.tags || '').split(/\s+/).indexOf(pst.filter) !== -1;
    }
    PAGE.resolve = function (path) {
      var m;
      if ((m = path.match(/^(fAll|fSix|fTwelve|fJumbo|fValue)\.(bg|color|border)$/))) {
        var on = pst.filter === FKEYS[m[1]];
        return (on ? CHIP_ON : CHIP_OFF)[m[2]];
      }
      if ((m = path.match(/^s(\d+)$/))) return visible(+m[1] - 1) ? 'flex' : 'none';
      if ((m = path.match(/^b(\d+)$/))) {
        var c = cards[+m[1] - 1] || {};
        return pst.added[c.key] ? ctaAdded : ctaLabel;
      }
      if (path === 'sAll') return pst.filter === 'all' ? '' : 'none';
      if (path === 'shownStr') {
        if (pst.filter === 'all') return cards.length + ' products';
        var shown = 0;
        cards.forEach(function (_c, i) { if (visible(i)) shown++; });
        return shown + ' of ' + cards.length + ' shown';
      }
      // The header's floor price, over the cards the filter is actually SHOWING. It
      // was baked into the data-fp-t template as a Liquid literal computed once over
      // the whole collection, so picking "12-inch" left "from $15.23" standing above
      // a row whose cheapest card is $25.02. Empty when nothing matches — the header
      // then reads "0 of 13 shown • from ", which the section's own template handles
      // by never being reachable: every chip has at least one card.
      if (path === 'shownMin') {
        var min = null;
        cards.forEach(function (c, i) {
          if (visible(i) && typeof c.price === 'number' && (min === null || c.price < min)) min = c.price;
        });
        return min === null ? '' : '$' + min.toFixed(2);
      }
      // FP47: once the page holds the real Shopify cart (boot() adopted a non-empty one, or
      // an add below re-read it), the drawer's +/−/remove change it without going through
      // this module, so the header badge and the drawer footer must read the live cart, not
      // liveCount. Before that, liveCount is still the page's own count.
      if (path === 'cartCount') return (REAL && cartLoaded) ? undefined : liveCount;
      if (path === 'showBadge') return (REAL && cartLoaded) ? undefined : liveCount > 0;
      return undefined;
    };
    var addBusy = {};
    PAGE.handler = function (name, ev, item, el) {
      var m;
      if ((m = name.match(/^(fAll|fSix|fTwelve|fJumbo|fValue)\.pick$/))) {
        pst.filter = FKEYS[m[1]];
        render();
        return true;
      }
      if ((m = name.match(/^a(\d+)$/))) {
        var card = cards[+m[1] - 1];
        if (!card) return true;
        if (addBusy[card.key] || fpBusy(el)) return true;
        var done = function () {
          pst.added[card.key] = true;
          render();
          fpAnnounce((card.name || 'Item') + ' added to your cart.');
          window.setTimeout(function () { delete pst.added[card.key]; render(); }, 1300);
        };
        if (card.variantId && card.available !== false) {
          addBusy[card.key] = true;
          var release = fpLock(el);
          var settle = function () { addBusy[card.key] = false; release(); };
          fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: card.variantId, quantity: 1 }) })
            // fetch() does not reject on 4xx, so the old chain printed "ADDED ✓"
            // for a 422 and printed nothing at all for a dropped connection —
            // either way the click was a silent no-op that looked like a success.
            // 'ADDED ✓' may only appear after a 2xx.
            .then(function (r) {
              if (r.ok) return r.json();
              return r.json().then(
                function (e2) { throw new Error((e2 && (e2.description || e2.message)) || 'Could not add this to your cart.'); },
                function () { throw new Error('Could not add this to your cart.'); }
              );
            })
            // 2xx branch only, like every sibling add path. Without this the collection
            // grid — the store's primary category-browse surface — was the one add path
            // missing from GA4, while its view_item_list before it and its
            // begin_checkout after it were both recorded: the funnel read as if
            // collection browsers never add, and the drop-off was blamed on the wrong
            // step. item_name arrives once the card blob publishes a title.
            .then(function () {
              fpTrack('add_to_cart', {
                currency: FP_CCY, value: card.price || 0,
                items: [fpItem({ id: card.variantId, name: card.name, price: card.price })]
              });
              // FP47 (CART-04): the drawer renders on collection pages now, so a collection
              // add ends exactly like a PDP or home-card add: re-read the cart, then open the
              // drawer when the store setting asks for it (realAdd / pdpAdd do the same).
              // A 2xx from /cart/add.js is proof the cart is Shopify's, so the page switches
              // to the real cart here even if it loaded empty (boot() only adopts a non-empty
              // one) — otherwise the drawer's CHECKOUT would refuse with "temporarily
              // unavailable". The 1.3 s "ADDED ✓" stays: it is the button's own receipt.
              REAL = true;
              done();
              fetchRealCart(function () {
                liveCount = (shopifyCart && shopifyCart.item_count) || liveCount;
                if (S.openCartOnAdd !== false && fpDrawerEl()) openDrawer(); else render();
              });
            })
            .catch(function (err) { fpToast((err && err.message) || 'Could not add this to your cart. Please try again.'); })
            .then(settle, settle);
        } else {
          var saved = {};
          try { saved = JSON.parse(localStorage.getItem('foodiepaws-cart-v1') || '{}') || {}; } catch (e) { saved = {}; }
          saved[card.key] = (saved[card.key] || 0) + 1;
          try { localStorage.setItem('foodiepaws-cart-v1', JSON.stringify(saved)); } catch (e) { /* private mode */ }
          liveCount += 1;
          done();
        }
        return true;
      }
      return false;
    };
  })();

  // ---------- auth pages module ----------
  ;(function () {
    if (!document.querySelector('[data-fp-on*="togglePw"]')) return;
    var pst = { pw: false, recover: (location.hash || '').indexOf('recover') !== -1 };
    PAGE.resolve = function (path) {
      if (path === 'pwType') return pst.pw ? 'text' : 'password';
      if (path === 'eyeColor') return pst.pw ? '#D2232A' : '#C9A97E';
      if (path === 'recoverOpen') return pst.recover;
      return undefined;
    };
    PAGE.handler = function (name, ev) {
      if (name === 'togglePw') { pst.pw = !pst.pw; render(); return true; }
      if (name === 'toggleRecover') { if (ev && ev.preventDefault) ev.preventDefault(); pst.recover = !pst.recover; render(); return true; }
      return false;
    };
  })();

  // ---------- reviews page module — REMOVED 2026-09-07 ----------
  // Drove the filter/sort chips and the ratings card of the retired first-party
  // wall on /pages/reviews (data-fp-data="reviewspage"). sections/main-reviews.liquid
  // no longer emits that payload — the list is Klaviyo's app block — so readData()
  // returned null and this module returned on its first line on every load.

  // ---------- cart page module ----------
  ;(function () {
    var cp = readData('cartpage', null);
    if (!cp) return;
    var mains = cp.mains || [];
    // $7.00 is the flat rate the published Shipping policy charges under the
    // free-shipping threshold. The old 5.95 fallback quoted a fee the store does
    // not charge whenever the section setting is blank.
    var shipFee = parseFloat(cp.shipFee) || 7.00;
    if (mains.length && mains.every(function (m) { return m.variantId; })) {
      mains.forEach(function (m) { BEST.push({ key: m.key, name: m.name, meta: m.meta, variantId: m.variantId, available: m.available, realPrice: m.price, price: m.price }); });
      REAL = true;
    }
    // Published plans for one product, by handle. /cart.js does not carry the list,
    // so it is read once per product from the product JSON and cached. Week count is
    // parsed off the plan name with the same rule the product page uses.
    var planCache = {};
    // One schedule change in flight per line. Every other mutation in this theme is
    // guarded (realAddBusy, chgBusy/chgWant, qaBusy, pdpAddBusy) and this one — the
    // mutation that INVALIDATES its own line key — had nothing.
    var freqBusy = {};
    function productPlans(handle, cb) {
      if (planCache[handle]) return cb(planCache[handle]);
      fetch('/products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          var ps = [];
          if (p && p.selling_plan_groups && p.selling_plan_groups[0]) {
            ps = (p.selling_plan_groups[0].selling_plans || []).map(function (sp) {
              var m = /(\d+)/.exec(sp.name || '');
              return { id: sp.id, name: sp.name || '', weeks: m ? parseInt(m[1], 10) : 0 };
            }).filter(function (x) { return x.weeks > 0; });
          }
          planCache[handle] = ps; cb(ps);
        })
        .catch(function () { planCache[handle] = []; cb([]); });
    }
    function freqChange(entry, d) {
      var cf = parseInt(entry.freq, 10) || 4;
      if (REAL) {
        // No pre-clamp on this path any more. It capped at 12 while only six plans
        // exist, and the two disagreeing clamps are what routed a boundary press to a
        // bare `return`: "−" at 1 week exited HERE, before the plan list had even been
        // fetched, and "+" at 6 weeks exited below after a wasted round trip. Either
        // way the button did nothing and said nothing. The plan-aware branch owns both
        // bounds now, and states them from the published list.
        //
        // The delivery schedule lives on the SELLING PLAN. This used to POST only
        // properties._freq, which changes nothing Appstle bills on - the label moved,
        // the contract did not, and the page confirmed a change that never happened.
        // Swap the plan, or do nothing and say so.
        if (!entry._handle) return;
        // Two presses inside one round trip both read the same item snapshot, so the
        // second POSTed the key the first had just retired — Shopify answers 400 and the
        // shopper was told "Could not change the delivery schedule." immediately after it
        // changed, with the new cadence showing in the row underneath. Claimed BEFORE
        // productPlans(), because on a cold cache that fetch is itself the window.
        if (freqBusy[entry._key]) return;
        freqBusy[entry._key] = true;
        var settle = function () { delete freqBusy[entry._key]; };
        productPlans(entry._handle, function (ps) {
          if (!ps.length) { settle(); fpToast('This delivery schedule can only be changed from your subscription account.'); return; }
          var weeks = ps.map(function (p) { return p.weeks; }).sort(function (a, b) { return a - b; });
          var lo = weeks[0], hi = weeks[weeks.length - 1];
          var raw = cf + d;
          var want = Math.min(hi, Math.max(lo, raw));
          // Say which end you are at, in the merchant's own numbers rather than a
          // hardcoded 1 and 6 — the copy then survives a plan being added. A "+" that
          // stays enabled and simply does nothing reads as a broken page; the buttons
          // deliberately are NOT disabled, because the bounds are only known after
          // this fetch and a disabled binding would paint them dead on first render.
          if (raw < lo) { settle(); fpToast('Every ' + lo + (lo === 1 ? ' week' : ' weeks') + ' is the most often we deliver.'); return; }
          if (raw > hi) { settle(); fpToast('Every ' + hi + (hi === 1 ? ' week' : ' weeks') + ' is the least often we deliver.'); return; }
          var target = null;
          for (var pi = 0; pi < ps.length; pi++) if (ps[pi].weeks === want) target = ps[pi];
          // No plan at that exact interval (a gap in the published list), or the cart
          // is already on it. Neither is the shopper's mistake, so neither is silent.
          if (!target) { settle(); fpToast('Every ' + want + (want === 1 ? ' week' : ' weeks') + ' is not one of the schedules we offer.'); return; }
          if (target.id === entry._planId) { settle(); return; }
          fetch('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: entry._key, quantity: entry.qty, selling_plan: target.id, properties: { _freq: String(target.weeks) } })
          })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (c) {
              if (c && Array.isArray(c.items)) { shopifyCart = c; render(); fpCartHeard(c); fpExpressRefresh(); }
              else { fpToast('Could not change the delivery schedule.'); fetchRealCart(); }
            })
            .catch(function () { fpToast('Could not change the delivery schedule.'); fetchRealCart(); })
            // finally-equivalent: a failed change must never leave the line locked.
            .then(settle, settle);
        });
        return;
      }
      // Demo/localStorage path: no published plans to bound it, so the old 1..12
      // clamp and its early return stay here, where they are the only rule there is.
      var nf = Math.min(12, Math.max(1, cf + d));
      if (nf === cf) return;
      var parts = entry._key.split(':');
      if (parts[1] !== 'sub') return;
      var nk = parts[0] + ':sub:' + nf;
      var q = st.cart[entry._key];
      delete st.cart[entry._key];
      st.cart[nk] = (st.cart[nk] || 0) + q;
      try { localStorage.setItem('foodiepaws-cart-v1', JSON.stringify(st.cart)); } catch (e) { /* private mode */ }
      render();
    }
    // fpFreqLabel, fpPlanParts and fpMetaNoFreq are MODULE scope (defined next to
    // cartTotals). This page held the only copy once, which is exactly why the drawer
    // could not publish the same chip off the same rule.
    //
    // FP47 sticky checkout bar (CART-05 / MOB-04). Shown below the one-column breakpoint
    // (1099px, the query foodiepaws.css collapses #fpCartGrid on) while the cart has lines
    // and the summary's own CHECKOUT ([data-fp-cko-main]) is off screen.
    // cartBarDisplay measures the button itself on every render: the observer cannot be the
    // source of truth, because it fires on CHANGES only, and the grid going from display:none
    // to a summary that starts below the fold is "not intersecting" both before and after
    // (measured on FP47: the bar never showed at the top of /cart).
    // FP50 (F2): "on screen" means the summary CHECKOUT can be pressed whole, from the sticky
    // header's bottom edge down to the viewport's bottom edge, so exactly one CHECKOUT is
    // reachable at every scroll offset: that one, or the bar's. Measured against the viewport
    // top and counting any visible pixel, the bar stayed hidden while CHECKOUT slid fully under
    // the 61px header (0 of 9 points pressable) and while only its top edge peeked in above the
    // viewport bottom (live FP49, 390x664). The header's edge is measured at every check, never
    // assumed: its height follows the width and the logo, and while the announcement bar still
    // shows above it, it sits lower. Checked once per frame on scroll and resize, when the button
    // crosses a viewport edge or goes fully in or out (IntersectionObserver), and when the grid
    // or the header changes size (ResizeObserver); render() runs only when the answer changes.
    var barMq = window.matchMedia ? window.matchMedia('(max-width: 1099px)') : null;
    var mainCko = document.querySelector('#fpCartGrid [data-fp-cko-main]');
    var cartHdr = document.querySelector('header[data-screen-label="Header"]');
    var ckoSeen = null, ckoRaf = 0;
    function ckoTopEdge() {
      if (!cartHdr) return 0;
      var h = cartHdr.getBoundingClientRect();
      return h.height ? Math.max(0, h.bottom) : 0;
    }
    function ckoOnScreen() {
      if (!mainCko) return true;
      var b = mainCko.getBoundingClientRect();
      if (!b.width && !b.height) return true; // grid hidden: nothing to stand in for
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return b.top >= ckoTopEdge() - 0.5 && b.bottom <= vh + 0.5;
    }
    function ckoCheck() {
      ckoRaf = 0;
      if (ckoOnScreen() !== ckoSeen) render();
    }
    function ckoSoon() {
      if (ckoRaf) return;
      ckoRaf = window.requestAnimationFrame ? window.requestAnimationFrame(ckoCheck) : window.setTimeout(ckoCheck, 16);
    }
    if (mainCko && document.getElementById('fpCartBar')) {
      window.addEventListener('scroll', ckoSoon, { passive: true });
      window.addEventListener('resize', ckoSoon);
      if ('IntersectionObserver' in window) new IntersectionObserver(ckoSoon, { threshold: [0, 1] }).observe(mainCko);
      if (typeof ResizeObserver === 'function') {
        var ckoRo = new ResizeObserver(ckoSoon);
        var ckoGrid = document.getElementById('fpCartGrid');
        if (ckoGrid) ckoRo.observe(ckoGrid);
        if (cartHdr) ckoRo.observe(cartHdr);
      }
      if (barMq) {
        if (barMq.addEventListener) barMq.addEventListener('change', function () { render(); });
        else if (barMq.addListener) barMq.addListener(function () { render(); });
      }
    }
    PAGE.resolve = function (path) {
      var totals;
      // The grid is real markup now (main-cart.liquid), shown exactly when the old
      // <template data-fp-if="hasItems"> would have been mounted.
      if (path === 'cartGridDisplay') return PAGE.resolve('hasItems') ? 'grid' : 'none';
      if (path === 'cartBarDisplay') {
        ckoSeen = ckoOnScreen();
        var barOn = !!(barMq && barMq.matches) && !!PAGE.resolve('hasItems') && !ckoSeen;
        // The one side effect in a resolver, and an idempotent one: the class lifts the
        // toast, FP and the page end above the bar (main-cart.liquid's style block) and has
        // to change in the same render as the bar itself.
        document.documentElement.classList.toggle('fp-cart-bar-on', barOn);
        return barOn ? 'flex' : 'none';
      }
      if (path === 'cartItems') {
        return cartTotals().entries.map(function (e) {
          var unit = e.qty > 0 ? e.line / e.qty : 0;
          var plan = fpPlanParts(e);
          return {
            name: e.name, qty: e.qty, meta: fpMetaNoFreq(e.meta),
            // Derived from the selling plan's OWN name where there is one (see
            // cartEntries), so this row and the drawer's chip cannot disagree — they
            // used to, and the weekly plan printed "EVERY 1 WKS". FP47 (CART-10): the
            // cadence stepper prints freqEvery ("Every 2 weeks") and the SUBSCRIPTION chip
            // gains offTail (" · 20% OFF"): the whole label does not fit one line at 390.
            isSub: !!e.sub, freqLabel: fpFreqLabel(e), freqEvery: plan.every,
            offTail: plan.off ? ' · ' + plan.off.toUpperCase() : '',
            unitStr: money(unit), lineStr: money(e.line),
            img: e.img || '', url: e.url || '', hasImg: !!e.img,
            isBully: !e.img && e.id.indexOf('bully') === 0, isCollagen: !e.img && e.id === 'collagen', isTripe: !e.img && e.id === 'tripe', isVariety: !e.img && e.id === 'variety',
            _key: e._key, _entry: e
          };
        });
      }
      // Same rule as noItems: nothing is asserted about the cart until /cart.js
      // has answered.
      if (path === 'hasItems') return (REAL && !cartLoaded) ? false : cartTotals().entries.length > 0;
      // Shipping is charged on the post-discount total (see cartTotals).
      if (path === 'shipLineStr') {
        totals = cartTotals();
        if (totals.payable === 0) return '\u2014';
        return totals.payable >= S.freeShipThreshold ? 'FREE' : money(shipFee);
      }
      if (path === 'totalStr') {
        totals = cartTotals();
        var ship = totals.payable > 0 && totals.payable < S.freeShipThreshold ? shipFee : 0;
        return money(totals.payable + ship);
      }
      return undefined;
    };
    // One quick-add path for both button families. Every one of them used to
    // fire-and-forget: a 422 (sold out, line limit) and a dropped connection both
    // produced no message and no visible change, so the click was a silent no-op
    // on the page where the shopper is closest to buying.
    var qaBusy = {};
    function qaAdd(entry, el) {
      if (!entry) return;
      var lock = entry.key || String(entry.variantId || '');
      if (qaBusy[lock] || fpBusy(el)) return;
      if (entry.variantId && entry.available !== false) {
        qaBusy[lock] = true;
        var release = fpLock(el);
        var settle = function () { qaBusy[lock] = false; release(); };
        fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: entry.variantId, quantity: 1 }) })
          .then(function (r) {
            if (r.ok) return r.json();
            return r.json().then(
              function (e2) { throw new Error((e2 && (e2.description || e2.message)) || 'Could not add this to your cart.'); },
              function () { throw new Error('Could not add this to your cart.'); }
            );
          })
          .then(function () {
            fpTrack('add_to_cart', {
              currency: FP_CCY, value: entry.price || 0,
              items: [fpItem({ id: entry.variantId, name: entry.name, price: entry.price })]
            });
            fpAnnounce((entry.name || 'Item') + ' added to your cart.');
            fetchRealCart();
          })
          // Re-read on failure too: a stale or sold-out variant should be
          // reflected on the page, not just complained about.
          .catch(function (err) { fpToast((err && err.message) || 'Could not add this to your cart. Please try again.'); fetchRealCart(); })
          .then(settle, settle);
        return;
      }
      // Below here is the DEMO cart, and on a real /cart it can never be true. REAL is
      // set at :cartpage boot from `mains.every(m => m.variantId)` — availability is
      // not part of that test — and cartEntries() then reads shopifyCart only, so this
      // localStorage write can never surface on any element of this page. The
      // announcement that followed it was not merely optimistic, it was structurally
      // unable to be true: the shopper was told a sold-out pack was in their cart and
      // nothing changed on screen. Fail loudly instead.
      if (REAL) { fpToast('That pack is sold out right now.'); return; }
      st.cart[entry.key] = (st.cart[entry.key] || 0) + 1;
      try { localStorage.setItem('foodiepaws-cart-v1', JSON.stringify(st.cart)); } catch (e) { /* private mode */ }
      fpAnnounce((entry.name || 'Item') + ' added to your cart.');
      render();
    }
    PAGE.handler = function (name, ev, item, el) {
      var m;
      // The /cart CHECKOUT fired no begin_checkout at all, so the whole cart page
      // was missing from the funnel — the drawer's checkout was the only one
      // counted, and the two disagreed about what a checkout even is.
      // Same guard as the drawer's `checkout`: an empty cart must not navigate.
      if (name === 'goCheckout') {
        if (cartTotals().count === 0) return true;
        playBark(400); fpGoCheckout(el, cartTotals()); return true;
      }
      if ((m = name.match(/^addQa([1-4])$/))) {
        var slot = +m[1];
        qaAdd(mains.filter(function (x) { return x.slot === slot; })[0], el);
        return true;
      }
      if ((m = name.match(/^add(Bully|Collagen|Tripe|Variety)$/))) {
        var key = m[1].toLowerCase();
        qaAdd(mains.filter(function (x) { return x.key === key; })[0] || { key: key }, el);
        return true;
      }
      if ((name === 'item.fDec' || name === 'item.fInc') && item && item._entry) {
        freqChange(item._entry, name === 'item.fInc' ? 1 : -1);
        return true;
      }
      return false;
    };
    // Only when boot() will NOT do it. In the normal case this module has already set
    // REAL = true above and boot() — which runs later in the same deferred-script tick —
    // hits `if (REAL) fetchRealCart();`, so a bare call here made every /cart load issue
    // two identical GET /cart.js and doubled the exposure to fetchRealCart()'s
    // retry/toast path. The !REAL case is NOT covered by boot: its `BEST.length === 0`
    // branch adopts the cart only when it holds something, so an empty cart would leave
    // cartLoaded false and the page would render its heading with no empty-cart panel.
    if (!REAL) fetchRealCart(null, true);
  })();

  // ---------- product page module ----------
  ;(function () {
    var pd = readData('pdp', null);
    if (!pd) return;
    var vs = (pd.variants && pd.variants.length) ? pd.variants : [{ id: pd.variantId, title: '', price: pd.price, available: pd.available }];
    vs.forEach(function (v) { v.price = parseFloat(v.price) || 0; });
    var v0 = Math.min(Math.max(parseInt(pd.vIdx, 10) || 0, 0), vs.length - 1);
    function curOf(i) { return vs[i] || vs[0] || {}; }
    // Keep Shopify's media indices stable: frames, thumbnails and deferred loading
    // all use the same index. Pack images beyond the first twelve must stay reachable.
    var mediaIds = Array.isArray(pd.mediaIds) ? pd.mediaIds.slice() : [];
    var variantMediaIds = Object.create(null);
    vs.forEach(function (v) {
      if (v.mediaId != null) variantMediaIds[String(v.mediaId)] = true;
    });
    var extraMediaOwners = pd.extraMediaOwners && typeof pd.extraMediaOwners === 'object'
      ? pd.extraMediaOwners : {};
    function galleryVisible(gi, vi) {
      if (gi < 0 || gi >= mediaIds.length || gi !== Math.floor(gi)) return false;
      var id = String(mediaIds[gi]), variant = curOf(vi);
      // Shopify's native variant image remains the source of truth for the front.
      if (variant.mediaId != null && id === String(variant.mediaId)) return true;
      if (variantMediaIds[id]) return false;
      // Count/weight-specific back labels are explicitly owned too. An absent or
      // empty list leaves genuinely shared product and lifestyle media available.
      var owners = extraMediaOwners[id];
      if (Array.isArray(owners) && owners.length) {
        return owners.some(function (owner) { return String(owner) === String(variant.id); });
      }
      return true;
    }
    function galleryIndex(vi) {
      var id = curOf(vi).mediaId;
      if (id != null) {
        for (var gi = 0; gi < mediaIds.length; gi++) {
          if (String(mediaIds[gi]) === String(id)) return gi;
        }
      }
      // Never fall back to another pack's photograph when an assignment is missing.
      for (var fallback = 0; fallback < mediaIds.length; fallback++) {
        if (galleryVisible(fallback, vi)) return fallback;
      }
      return -1;
    }
    var pReal = !!(curOf(v0).id && curOf(v0).available !== false);
    if (pReal) {
      BEST.push({ key: pd.key, name: pd.name, meta: pd.meta, variantId: curOf(v0).id, available: curOf(v0).available, realPrice: curOf(v0).price, price: curOf(v0).price });
      REAL = true;
    }
    // Every plan the merchant published, in Shopify's order. Falls back to the
    // legacy single-id shape so a theme deployed ahead of the Liquid change
    // still works. The frequency picker walks THIS list, so it can only ever
    // offer a frequency that actually exists — the old picker moved a free
    // 1..12 counter that reached Shopify as a cosmetic property.
    var fpPlans = (pd.sellingPlans && pd.sellingPlans.length)
      ? pd.sellingPlans.slice()
      : (pd.sellingPlanId ? [{ id: pd.sellingPlanId, label: '' }] : []);
    // Liquid cannot tell us a plan's interval, so read it out of the plan's own
    // name ("Every 4 weeks" → 4). weeks stays a NUMBER because the cart drawer
    // renders it as 'every ' + freq + ' weeks'; a label there would print
    // "every Every 4 weeks weeks". No number in the name → 4, and the PDP shows
    // the raw label instead of inventing a week count.
    fpPlans = fpPlans.map(function (p) {
      var m = /(\d+)/.exec(p.label || '');
      return { id: p.id, label: p.label || '', weeks: m ? parseInt(m[1], 10) : 0 };
    });
    var hasPlan = fpPlans.length > 0;
    // Which plan the page opens on. This is a MERCHANT setting
    // (Theme settings > Store rules > Default subscription frequency), not a
    // constant, because it is a commercial decision and it has been changed.
    //
    // Keep the history in view before lowering it. The chip row replaced a
    // stepper whose default was literally freq=4, and Shopify returns the plans
    // shortest-first, so plain index 0 silently moved every customer's default
    // from monthly-ish to WEEKLY -- four times the shipments, four times the
    // charge. That was live on the store for the hour after the plans were
    // first published. A short default is legitimate, but it must be chosen,
    // which is what the setting makes it.
    //
    // Deliberately no plan COUNT here: the merchant adds and removes
    // frequencies from the app. Falls back to the first plan when the store
    // publishes no plan matching the setting.
    var fpDefaultWeeks = (S.subscribeDefaultWeeks > 0) ? S.subscribeDefaultWeeks : 4;

    // How long the chosen pack lasts, at the one-a-day the PDP tip already assumes,
    // rounded UP to whole weeks. Rounding up is the point: a delivery that lands
    // before the last chew is gone is how a subscriber ends up with a cupboard full
    // of them, and that is the usual reason they cancel. A 10-pack lasts ~10 days
    // -> 2 weeks, which is exactly what the tip on the page says.
    //
    // Only an EXPLICIT quantity counts. "5 Pack" and "25 Count" are quantities;
    // "12-Inch", "5-6-Inch Thin" and "1 LB BAG" are not. A bare first-number parse
    // would read the 5 out of "5-6-Inch" and put that shopper on weekly delivery,
    // so anything without a pack/count word falls back to the merchant setting.
    // Hyphens and parens are separators here, not part of the number. Without the
    // normalisation "10-Count" scored 0 while "10 Count" scored 10, so the SAME
    // pack was read two different ways depending on how the merchant typed the
    // variant — and the one that scored 0 fell through to the merchant default.
    // (Parens already worked, because \s* matches the space inside them; only the
    // hyphen forms diverged.) The pack/count/ct word is still required: "12-Inch",
    // "5-6-Inch Thin" and "1 LB BAG" are sizes, not quantities.
    function fpCountIn(text) {
      var m = /(\d+)\s*(?:pack|count|ct)\b/i.exec(String(text || '').replace(/[-()]/g, ' '));
      return m ? parseInt(m[1], 10) : 0;
    }
    // The VARIANT wins, because that is what the shopper just picked. The product
    // title is the fallback, and it is not optional: four products carry their only
    // quantity there and sell it as a single variant called "Default Title" -
    // "12-Inch Lamb Tripe Sticks - 4 Count", "5-6-Inch Thin Bully Sticks - 25 Count"
    // and both variety packs. Reading the variant alone scored all four as 0 and
    // dropped them on the merchant default, so a 4-count shipped every 4 weeks.
    function fpPackCount(variantTitle) {
      return fpCountIn(variantTitle) || fpCountIn(pd.name);
    }
    function fpPackWeeks(variantTitle) {
      var n = fpPackCount(variantTitle);
      return n > 0 ? Math.max(1, Math.ceil(n / 7)) : 0;
    }
    // The spec line under the title, recomputed per variant. Mirrors the Liquid in
    // sections/main-product.liquid exactly - same inputs, same order, same fallback -
    // so the server paint and the first client render cannot disagree. Prints only
    // what it derived; the typed line is reached only when it derived nothing.
    function fpMetaLine(vi) {
      var parts = [];
      if (pd.pieceSize) parts.push(pd.pieceSize);
      var n = fpPackCount(curOf(vi).title);
      if (n > 0) parts.push(n + ' count');
      if (pd.material) parts.push(pd.material);
      return parts.length ? parts.join(' \u2022 ') : (pd.metaFallback || pd.meta || '');
    }
    function fpHintLine(vi) {
      var n = fpPackCount(curOf(vi).title);
      return n > 0 ? ('One a day: a ' + n + '-pack lasts about ' + n + ' days.') : (pd.hintFallback || '');
    }
    // The shortest published plan that is not SHORTER than the pack lasts; if every
    // plan is shorter, the longest one. Never deliver before the pack runs out.
    function fpIdxForWeeks(want) {
      var best = -1, bestScore = Infinity;
      for (var fi2 = 0; fi2 < fpPlans.length; fi2++) {
        var w = fpPlans[fi2].weeks;
        if (!w) continue;
        var score = (w >= want) ? (w - want) : (1000 + (want - w));
        if (score < bestScore) { bestScore = score; best = fi2; }
      }
      return best;
    }
    function fpAutoIdx(vi) {
      var i = fpIdxForWeeks(fpPackWeeks(curOf(vi).title) || fpDefaultWeeks);
      return i < 0 ? 0 : i;
    }
    var fpDefaultIdx = fpAutoIdx(v0);
    // Default is ONE-TIME. Subscribe & Save used to be pre-selected, which collided
    // with the first-order promotional code: that code applies to one-time purchases
    // only, so a shopper who arrived for "50% off your first order" and simply pressed
    // add was silently charged full price on a recurring plan. Subscribe & Save stays
    // one tap away, with its saving shown next to it.
    var pst = { gIdx: galleryIndex(v0), v: v0, plan: 'one', planIdx: fpDefaultIdx, freq: fpDefaultWeeks, qty: 1, detIdx: 0, faqIdx: -1, freqTouched: false };
    // Top of the funnel. Without this there is no denominator to compare add_to_cart
    // against, so a product page that gets traffic and no adds is indistinguishable
    // from one that gets no traffic.
    fpTrack('view_item', {
      currency: FP_CCY, value: curOf(v0).price,
      items: [fpItem({ id: curOf(v0).id, name: pd.name, variant: curOf(v0).title, price: curOf(v0).price })]
    });
    function curPlan() { return fpPlans[pst.planIdx] || null; }
    // Weeks actually charged: the plan's parsed value, or the legacy default.
    function planWeeks() { var p = curPlan(); return (p && p.weeks) ? p.weeks : 4; }
    function cur() { return curOf(pst.v); }
    function curReal() { return !!(cur().id && cur().available !== false); }
    var LETTER = { bully: 'b', collagen: 'c', tripe: 't', variety: 'v' }[pd.key] || 'b';
    var fmt2 = function (n) { return Number(n).toFixed(2); };
    // The subscription figure the PLATFORM will actually charge, not one this theme
    // derives. main-product.liquid emits variant.selling_plan_allocations as
    // planPrices:[{planId,price}] — Shopify's own allocation for THIS variant on THIS
    // plan. subOf() reads settings.subscribe_save_percent, a second source of truth for
    // a number the subscription app owns: change the discount in Appstle without also
    // editing Theme settings and every PDP advertises a price checkout will not honour.
    // Allocation first; the computed value only when the variant publishes none (a
    // product with no plans, the legacy single-id fallback shape, or a theme served
    // ahead of the Liquid change) — so nothing changes where nothing is emitted.
    function subUnit() {
      var pp = cur().planPrices, p = curPlan(), i, n;
      if (p && pp && pp.length) {
        for (i = 0; i < pp.length; i++) {
          // String() both sides: Liquid emits the id as a number and fpPlans is built
          // from the same payload, but a cached or app-written payload can send either
          // and 12345 !== '12345'.
          if (String(pp[i].planId) === String(p.id)) {
            n = parseFloat(pp[i].price);
            // A matched-but-unusable price falls through rather than printing $0.00.
            return (isFinite(n) && n > 0) ? n : subOf(cur().price);
          }
        }
      }
      return subOf(cur().price);
    }
    function unit() { return pst.plan === 'sub' ? subUnit() : cur().price; }
    PAGE.resolve = function (path) {
      var m;
      if ((m = path.match(/^g\.thumb(\d+)$/))) return galleryVisible(+m[1], pst.v) ? 'inline-flex' : 'none';
      if ((m = path.match(/^g\.order(\d+)$/))) return cur().mediaId != null && String(mediaIds[+m[1]]) === String(cur().mediaId) ? -1 : 0;
      if ((m = path.match(/^g\.m(\d+)$/))) return pst.gIdx === +m[1] && galleryVisible(+m[1], pst.v) ? 1 : 0;
      if ((m = path.match(/^g(\d+)on$/))) return pst.gIdx === +m[1] && galleryVisible(+m[1], pst.v) ? 'true' : 'false';
      if ((m = path.match(/^g(\d+)hidden$/))) return pst.gIdx === +m[1] && galleryVisible(+m[1], pst.v) ? 'false' : 'true';
      if ((m = path.match(/^g\.([bctv])([0-2])$/))) return (m[1] === LETTER && pst.gIdx === +m[2]) ? 1 : 0;
      // Which photo is selected is carried by this border and nothing else, so it is a
      // non-text contrast (SC 1.4.11, 3:1 floor). --fp-accent #F58220 measured 2.53:1 on
      // the #FFFBFB page and 1.92:1 against the unselected #F0DBBB — under the floor on
      // BOTH counts, i.e. the selection was invisible. --fp-cta #B4590A measures 4.67:1
      // on the page and 3.56:1 against the unselected border. Must stay in step with
      // main-product.liquid's first-paint literal on the same button
      // (`border:2px solid {% if forloop.first %}...`), or first paint and this
      // hydrated value disagree on the very thumbnail that opens selected.
      if ((m = path.match(/^t(\d+)b$/))) return pst.gIdx === +m[1] ? '#B4590A' : '#F0DBBB';
      // 2026-08-30: was '#F58220', which is 2.53:1 against the page and 2.36:1 against its own
      // fill - under the 3:1 WCAG 1.4.11 requires for the state of a component. '#B4590A' is
      // --fp-cta, the same token the thumbnail line above already resolves to. MUST move together
      // with main-product.liquid's inline literal or first paint and hydration disagree.
      if ((m = path.match(/^v(\d+)bd$/))) return pst.v === +m[1] ? '#B4590A' : '#E5C9A1';
      if ((m = path.match(/^v(\d+)bg$/))) return pst.v === +m[1] ? '#FFF3DC' : '#FFFFFF';
      if ((m = path.match(/^v(\d+)fg$/))) { var vv = vs[+m[1]] || {}; return pst.v === +m[1] ? '#3D1409' : (vv.available === false ? '#C9A97E' : '#8A5A2E'); }
      // Which pack pill is pressed. The pills were styled-only: three colour bindings
      // and nothing an assistive tech could read, so the selected pack was invisible
      // to it while the price beside it changed. STRINGS, exactly like freq…On below —
      // fill() maps null/undefined to '', and aria-pressed="" is not a valid value.
      if ((m = path.match(/^v(\d+)on$/))) return pst.v === +m[1] ? 'true' : 'false';
      if (path === 'metaLine') return fpMetaLine(pst.v);
      if (path === 'hintLine') return fpHintLine(pst.v);
      if (path === 'netWeight') {
        var netWeightLabel = cur().netWeightLabel;
        if (typeof netWeightLabel === 'string' && netWeightLabel.trim()) return netWeightLabel.trim();
        var g = parseFloat(cur().weight);
        if (!isFinite(g) || g <= 0) return '';
        return (Math.round(g * 0.035274 * 10) / 10).toFixed(1) + ' oz';
      }
      if (path === 'onePriceStr') return fmt2(cur().price);
      if (path === 'subPriceStr') return fmt2(subUnit());
      if (path === 'planSub') return pst.plan === 'sub';
      if (path === 'subRowBg') return pst.plan === 'sub' ? '#FFF3DC' : '#FFFFFF';
      if (path === 'oneRowBg') return pst.plan === 'one' ? '#FFF3DC' : '#FFFFFF';
      if (path === 'subDot') return pst.plan === 'sub' ? '#F58220' : 'transparent';
      if (path === 'oneDot') return pst.plan === 'one' ? '#F58220' : 'transparent';
      // Show the plan's own words when its name carries no parseable number —
      // a "Monthly" plan must not be relabelled "4 WEEKS" by this theme.
      // Chip bindings, one set per PUBLISHED plan (freqShort0, freqBg0, …).
      // The short form is parsed from the plan's own name so the chip and the
      // _freq cart property can never disagree: both read planWeeks().
      if ((m = path.match(/^freq(Short|Bg|Fg|Bd|On|Tag)(\d+)$/))) {
        var fi = +m[2], fpp = fpPlans[fi];
        if (!fpp) return '';
        var on = fi === pst.planIdx;
        switch (m[1]) {
          case 'Short': return fpp.weeks ? (fpp.weeks === 1 ? '1 week' : fpp.weeks + ' weeks') : (fpp.label || '');
          // The badge marks the row that FITS the pack on screen, so it has to move
          // with the pack size. A badge pinned to one frequency would sit on 4 weeks
          // while the box opened on 2, and contradict itself.
          // …and it appears ONLY when a fit was actually computed. The two
          // weight-sold products ("4-Inch Bully Sticks - 1 LB Bag", "Bully Stick
          // Bites - 1 LB BAG") yield no piece count, so the badge used to land
          // on the merchant fallback and tell the shopper a frequency "fits" a
          // bag whose contents the store never counted.
          case 'Tag':   return (fpPackCount(curOf(pst.v).title) > 0 && fi === fpAutoIdx(pst.v)) ? 'inline-block' : 'none';
          // --fp-cta (#B4590A, 4.80:1 on white), never --fp-accent (#F58220, 2.59:1) —
          // the same rule main-product.liquid:434-438 already applies to this control's
          // focus ring. White on the accent measures 2.59:1 against the 4.5:1 floor for
          // 13.5px text, and this row is the one line that states how often the shopper
          // will be CHARGED. The border moves with the fill: darkening one alone left an
          // orange ring around a dark chip.
          case 'Bg':    return on ? '#B4590A' : '#FFFFFF';
          case 'Fg':    return on ? '#FFFFFF' : 'var(--fp-ink)';
          case 'Bd':    return on ? '#B4590A' : '#E5C9A1';
          case 'On':    return on ? 'true' : 'false';
        }
      }
      if (path === 'fLabel') {
        var fp = curPlan();
        if (fp && !fp.weeks && fp.label) return fp.label.toUpperCase();
        var w = fp ? planWeeks() : pst.freq;
        return w + (w === 1 ? ' WEEK' : ' WEEKS');
      }
      if (path === 'fPop') return (curPlan() ? planWeeks() : pst.freq) === 4;
      if (path === 'qty') return pst.qty;
      if (path === 'ctaStr') return money(unit() * pst.qty);
      // The whole button label, so the verb tracks the selected purchase type.
      // Subscribe & Save is one tap away and is NOT pre-selected — see pst.plan at the
      // top of this module, which was moved to 'one' so the first-order code could not
      // be lost to a recurring plan. When the shopper does select it the price on the
      // button is the subscription price, so the label has to name the subscription: a
      // button that said only "ADD TO CART" was enrolling shoppers in recurring billing
      // without naming it.
      if (path === 'ctaLabel') {
        var ctaPfx = (pst.plan === 'sub' && hasPlan)
          ? (pd.ctaPrefixSub || 'SUBSCRIBE & ADD — ')
          : (pd.ctaPrefix || 'ADD TO CART — ');
        // Exactly one space before the price whatever the setting's trailing
        // whitespace: four templates shipped "ADD TO CART —$22.49" (measured
        // 2026-09-06) because their prefix ended on the dash. Same rule as the
        // Liquid first paint in main-product.liquid (`| strip` then a literal space).
        return ctaPfx.replace(/\s+$/, '') + ' ' + money(unit() * pst.qty);
      }
      // Verb only, no money - for the fixed mobile bar, which already prints the
      // price in its own span. Without this the mobile button read "ADD TO CART"
      // while Subscribe & Save was selected, so a tap started recurring billing
      // under a one-time label.
      if (path === 'ctaLabelShort') {
        var shortPfx = (pst.plan === 'sub' && hasPlan)
          ? (pd.ctaPrefixSub || 'SUBSCRIBE & ADD')
          : (pd.ctaPrefix || 'ADD TO CART');
        return shortPfx.replace(/\s*[—–-]\s*$/, '').trim();
      }
      // The line under the mobile price: states the recurring terms while a plan is
      // selected, and falls back to the shipping line for a one-time purchase.
      if (path === 'ctaTerms') {
        if (pst.plan === 'sub' && hasPlan) {
          var tw = planWeeks();
          return 'Renews every ' + tw + (tw === 1 ? ' week' : ' weeks') + ' · cancel anytime';
        }
        return pd.shipLine || '';
      }
      if (path === 'ctaDisabled') return !curReal() && vs.length > 1;
      // `open` mounts the panel (<template data-fp-if>), `disp` shows a panel that
      // is ALREADY in the DOM. The accordion bodies carry ~440 words of unique
      // product copy and the whole FAQ; behind a <template> none of it is
      // crawlable, so the panels now ship in the markup and this key drives their
      // visibility instead. Keep both: `open` still serves any panel left mounted
      // conditionally, and `sym` drives the +/\u2212 glyph either way.
      if ((m = path.match(/^d([1-4])\.(open|sym|disp)$/))) {
        var open = pst.detIdx === (+m[1] - 1);
        if (m[2] === 'disp') return open ? 'flex' : 'none';
        return m[2] === 'open' ? open : (open ? '\u2212' : '+');
      }
      if ((m = path.match(/^pfaq(\d+)\.(open|sym|disp)$/))) {
        var fopen = pst.faqIdx === +m[1];
        if (m[2] === 'disp') return fopen ? 'block' : 'none';
        return m[2] === 'open' ? fopen : (fopen ? '\u2212' : '+');
      }
      return undefined;
    };
    // In flight for THIS page, independent of which button was pressed.
    var pdpAddBusy = false;
    PAGE.handler = function (name, ev, item, el) {
      var m;
      if ((m = name.match(/^pickG(\d+)$/))) {
        if (!galleryVisible(+m[1], pst.v)) return true;
        // The frame is fetched HERE, when the shopper asks for it — never the whole
        // gallery after load. See fpPromoteFrame. Before render(), so the opacity
        // transition reveals a picture that is already requested.
        fpPromoteFrame(+m[1]);
        pst.gIdx = +m[1]; render(); return true;
      }
      if ((m = name.match(/^pickV(\d+)$/))) {
        var vi = +m[1];
        if (vs[vi]) {
          pst.v = vi;
          pst.gIdx = galleryIndex(vi);
          if (pst.gIdx >= 0) fpPromoteFrame(pst.gIdx);
          // A different pack lasts a different number of days, so re-fit the
          // frequency -- unless the shopper has already picked one. Their choice
          // always outranks the computed fit.
          if (!pst.freqTouched) pst.planIdx = fpAutoIdx(vi);
          // The page READS ?variant= on load but never wrote it back, so a
          // refresh, a back-nav or a shared link silently reverted to the default
          // pack — and the packs here run from $17.50 to $47.50, so that is not a
          // cosmetic difference. replaceState, not pushState: choosing a pack is
          // not a history step the Back button should have to walk through.
          try {
            var vid = String(curOf(vi).id || '').replace(/\D/g, '');
            if (vid && window.history && history.replaceState) {
              var u = new URL(window.location.href);
              u.searchParams.set('variant', vid);
              history.replaceState(history.state, '', u.pathname + u.search + u.hash);
            }
          } catch (e) { /* URL/history unavailable — the picker still works */ }
          render();
          // The packs here run from $17.50 to $47.50 and the pill row carries no
          // other confirmation, so a screen-reader shopper changed the price of the
          // page and heard nothing. Polite region, same one the add-to-cart uses.
          fpAnnounce(curOf(vi).title + ' selected. ' + money(unit() * pst.qty) + '.');
        }
        return true;
      }
      if (name === 'pickPlanSub') {
        if (curReal() && !hasPlan) return true; // no selling plan → never promise the discount
        pst.plan = 'sub'; render(); return true;
      }
      if (name === 'pickPlanOne') { pst.plan = 'one'; render(); return true; }
      // Walk the published plans, not a free counter. With one plan the arrows
      // are inert, which is correct: there is nothing else to choose.
      // Direct chip selection. The stepper below stays for the no-plan
      // fallback only — with plans published the chips are the whole control.
      if ((m = name.match(/^pickFreq(\d+)$/))) {
        var pi = +m[1];
        if (fpPlans[pi]) { pst.planIdx = pi; pst.freqTouched = true; render(); }
        return true;
      }
      if (name === 'fDec' || name === 'fInc') {
        var d = name === 'fInc' ? 1 : -1;
        if (hasPlan) pst.planIdx = Math.min(fpPlans.length - 1, Math.max(0, pst.planIdx + d));
        else pst.freq = Math.min(12, Math.max(1, pst.freq + d));
        render(); return true;
      }
      // Stepping down clears the cap notice — it is no longer true.
      if (name === 'qtyDec') { if (pst.qty === 9) atcSay(''); pst.qty = Math.max(1, pst.qty - 1); render(); return true; }
      // The cap stays at 9, but say so. A "+" that stays enabled and simply does
      // nothing reads as a broken page; disabling it instead would hide the
      // explanation, because browsers suppress title tooltips on disabled
      // controls. atcSay routes the reason to the live region, so it is announced too.
      // Says what this control actually does, not a rule the store does not keep.
      // The cap lives here and nowhere else: the drawer's "+", the cart page stepper
      // and a typed quantity all pass straight through, so a shopper CAN leave with
      // more than nine and the old wording — "9 per order is the maximum" — promised
      // an order-wide limit nothing enforces. Whether nine per order is a real
      // commercial rule is the owner's to say; until then this states the truth of
      // the stepper rather than inventing a policy or blocking a sale that succeeds today.
      if (name === 'qtyInc') {
        if (pst.qty >= 9) { atcSay('9 at a time — add again, or email us for bulk.', el); return true; }
        pst.qty += 1; render(); return true;
      }
      if ((m = name.match(/^d([1-4])\.pick$/))) {
        var i = +m[1] - 1;
        pst.detIdx = pst.detIdx === i ? -1 : i;
        render(); return true;
      }
      if ((m = name.match(/^pfaq(\d+)\.pick$/))) {
        if (ev && ev.stopPropagation) ev.stopPropagation();
        pst.faqIdx = pst.faqIdx === +m[1] ? -1 : +m[1];
        render(); return true;
      }
      // One message slot beside the add-to-cart row the shopper actually pressed.
      //
      // Two things were wrong with resolving it as `.fp-atc[data-fp-t]`. That is the
      // FIRST such button in document order, which is the sticky Mobile ATC bar's
      // (main-product.liquid:145, emitted before <main>), so `host` was the
      // position:fixed container and insertBefore(host.nextSibling) dropped the
      // message at the very top of the section, above the gallery — never next to
      // either button, on either breakpoint. And resolving from the click source
      // alone repairs desktop only: on a phone the pressed button IS the mobile-bar
      // button, and its host is that fixed div again. So resolve from the source AND
      // special-case the fixed bar, appending inside it where it is visible.
      //
      // Announcement is NOT this node's job. It used to carry role="alert" and was
      // created in the same tick as its first text, which is the one case screen
      // readers routinely swallow — so the FIRST failure, the one that matters, was
      // silent. It speaks through fpAnnounce's region instead, which is created empty
      // at boot; role="alert" is deliberately absent so the two cannot both announce.
      function atcSay(text, srcEl) {
        var btn = (srcEl && srcEl.closest && srcEl.closest('.fp-atc'))
          || document.querySelector('main .fp-atc')
          || document.querySelector('.fp-atc[data-fp-t]')
          || document.querySelector('.fp-atc');
        if (!btn) return;
        var host = btn.parentElement || btn;
        var fixed = btn.closest ? btn.closest('[data-screen-label="Mobile ATC"]') : null;
        var el = document.getElementById('fpAtcMsg');
        if (!el) {
          if (!text) return;
          el = document.createElement('p');
          el.id = 'fpAtcMsg';
          el.style.cssText = 'margin:8px 0 0;font-size:13px;font-weight:700;color:#B3261E;line-height:1.35';
        }
        var want = fixed || host.parentElement || host;
        if (el.parentElement !== want) {
          if (fixed) fixed.appendChild(el);
          else if (host.parentElement) host.parentElement.insertBefore(el, host.nextSibling);
          else host.appendChild(el);
        }
        el.textContent = text || '';
        el.style.display = text ? '' : 'none';
        if (text) fpAnnounce(text);
      }
      if (name === 'pdpAdd') {
        // The buy-box button is type="submit" inside a real /cart/add form since
        // 2026-09-07, so the page works with scripting off. With scripting on this
        // handler owns the add (drawer, announce, selling plan, quantity) and the
        // native POST must not also fire.
        if (ev && ev.preventDefault) ev.preventDefault();
        var sub = pst.plan === 'sub';
        // Two guards. fpBusy/fpLock covers the clicked button; pdpAddBusy covers
        // the OTHER one — this page renders the same handler on the buy box and
        // on the fixed mobile bar, and locking one left the other live. Unguarded,
        // a double tap added two units, or two subscription lines.
        if (pdpAddBusy || fpBusy(el)) return true;
        if (curReal()) {
          var body = { id: cur().id, quantity: pst.qty };
          // The SELECTED plan, not plan #1. _freq stays numeric for the cart
          // drawer's 'every N weeks' string.
          if (sub && hasPlan) {
            var chosen = curPlan();
            body.selling_plan = chosen ? chosen.id : pd.sellingPlanId;
            body.properties = { _freq: planWeeks() };
          }
          // fetch() does not reject on 4xx, so an unchecked chain treated a 422
          // (expired selling plan, variant gone, line limit) or a 429 as success:
          // it re-read the cart and opened the drawer as if the add had worked.
          // Never open the drawer on a failed add; say what Shopify said.
          pdpAddBusy = true;
          // BOTH add buttons, not just the one that was pressed: this page renders
          // `.fp-atc` twice — the buy box and the sticky mobile bar — so locking
          // only `el` left the other one live and tappable mid-request.
          var locks = Array.prototype.map.call(document.querySelectorAll('.fp-atc'), fpLock);
          if (el && el.classList && !el.classList.contains('fp-atc')) locks.push(fpLock(el));
          var settle = function () { pdpAddBusy = false; locks.forEach(function (r) { r(); }); };
          fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
            .then(function (r) {
              if (r.ok) return r.json();
              return r.json().then(
                function (e) { throw new Error((e && (e.description || e.message)) || 'Could not add this to your cart.'); },
                function () { throw new Error('Could not add this to your cart.'); }
              );
            })
            .then(function () { return fetch((S.cartUrl || '/cart') + '.js'); })
            .then(function (r) { return r.json(); })
            .then(function (c) {
              atcSay('');
              fpTrack('add_to_cart', {
                currency: FP_CCY, value: Math.round(unit() * pst.qty * 100) / 100,
                items: [fpItem({ id: cur().id, name: pd.name, variant: cur().title, price: unit(), qty: pst.qty })]
              });
              shopifyCart = c; fpCartHeard(c); fpExpressRefresh(); if (sub) showSubStrip();
              // The drawer used to slide in silently with focus left on the button
              // behind it: a blind shopper pressed ADD TO CART and heard nothing at
              // the single most important step. Announce into the persistent polite
              // region, THEN move focus — openDrawer un-hides the drawer before it
              // focuses, so this cannot land on an inert element.
              fpAnnounce(pd.name + ' added to your cart. Cart subtotal ' + money(cartTotals().subtotal) + '.');
              if (S.openCartOnAdd !== false) openDrawer(); else render();
            })
            .catch(function (err) { atcSay((err && err.message) || 'Could not add this to your cart. Please try again.', el); })
            .then(settle, settle);
        } else if (cur().id) {
          // A REAL variant that is unavailable. Without this arm it fell through to
          // the demo branch below and wrote a localStorage line the page never reads,
          // then announced a successful add — the discriminator for that branch is
          // "no variant id", never "this variant is sold out".
          atcSay('That pack size is sold out — pick another size.', el);
        } else {
          // Demo/local-cart branch (no real variant). Key off the same weeks the
          // real branch sends, so the two paths cannot disagree.
          var key = sub ? pd.key + ':sub:' + (hasPlan ? planWeeks() : pst.freq) : pd.key;
          st.cart[key] = (st.cart[key] || 0) + pst.qty;
          try { localStorage.setItem('foodiepaws-cart-v1', JSON.stringify(st.cart)); } catch (e) { /* private mode */ }
          if (sub) showSubStrip();
          fpAnnounce(pd.name + ' added to your cart. Cart subtotal ' + money(cartTotals().subtotal) + '.');
          if (S.openCartOnAdd !== false) openDrawer(); else render();
        }
        return true;
      }
      return false;
    };
  })();

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // theme editor: re-render after section reloads in Customize.
  //
  // Dropping the registries is not the same as unmounting what they created: the
  // nodes a data-fp-if had already inserted stayed in the DOM with nothing
  // tracking them, so the next scan mounted a SECOND copy on every section save.
  // Unmount first, then rebuild.
  //
  // Re-initialising is scoped to the section that actually reloaded, because
  // neither initialiser is idempotent — initMegaMenu attaches document-level
  // keydown/click and a window scroll handler on every call, and initFindMyChew
  // binds its inputs afresh.
  document.addEventListener('shopify:section:load', function (ev) {
    ifTpls.slice().forEach(function (rec) { if (rec.mounted) unmountIf(rec); });
    eachTpls.forEach(function (rec) {
      rec.rows.forEach(function (n) { if (n.parentNode) n.parentNode.removeChild(n); });
      rec.rows = [];
    });
    boundNodes.length = 0;
    ifTpls.length = 0;
    eachTpls.length = 0;
    scan(document.body);
    var r = ev && ev.target;
    if (r && r.querySelector) {
      if (r.querySelector('#fpMegaTrigger')) initMegaMenu();
      if (r.querySelector('#fpQuiz')) initFindMyChew();
    }
    render();
  });
})();


/* The footer scene loader that lived here (fetch footer-scene.svg into #fpScene, hand-driven
   SMIL morphs) went with FP41 (2026-10-19): the home footer renders <fp-farm-scene>
   (snippets/fp-farm-scene.liquid, assets/fp-farm-scene.js) and #fpScene no longer exists. */
